/**
 * @fileoverview Main Diagram Canvas Component
 * 
 * This is the primary canvas component for the diagramming application. It integrates
 * all the major systems including rendering, tools, commands, frames, and user
 * interaction to provide a complete diagramming experience.
 * 
 * Key Features:
 * - Interactive canvas with zoom, pan, and selection
 * - Complete tool system integration
 * - Undo/redo command management
 * - Frame-based organization
 * - Multi-selection and transformation
 * - Real-time collaboration support
 * - Touch and mouse input handling
 * 
 * Architecture:
 * - Uses React-Konva for 2D canvas rendering
 * - Zustand store for state management
 * - Tool system for extensible operations
 * - Command pattern for undo/redo
 * - Event bus for component communication
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import React, { useContext, useEffect, useRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { Stage, Layer, Rect } from 'react-konva';
import Konva from 'konva';
import { toast } from 'sonner';

import { getNodeMutationManager } from '../../core/commands/mutations/NodeMutationManager';
import { CanvasContextMenu } from './CanvasContextMenu';
import { useCanvasContextMenu } from '../../stores/canvasContextMenu';
import { usePopupManager } from '../../contexts/PopupManagerContext';
import { computeConnectionSnap, getClosestNodeAnchor, resolveConnectionGeometryNode } from './lib/connectionSnap';
import { executeCanvasConnectionPlan, planCanvasConnectionCommit } from './lib/commitCanvasConnection';
import { getGenogramPersonSymbolRect } from './lib/genogramPersonSymbolBounds';

import {
  SHORTCUT_IDS_TO_UNREGISTER,
  TOOL_IDS_TO_PRESERVE_ON_UNMOUNT,
  TOOL_IDS_TO_UNREGISTER,
} from './lib/diagramCanvasCleanupIds';
// geometry helpers used by overlays
import type * as Y from 'yjs';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { useDiagramStore } from '../../stores/diagramStore';
import { useHistoryPreviewStore } from '../../stores/historyPreviewStore';
import { useProjectedLayoutMode } from '../../hooks/useProjectedLayoutMode';
import { useOrgHistoryPreview } from '../../domains/orgchart/hooks/useOrgHistoryPreview';
import { resolveConnectorShapeState } from './lib/resolveConnectorShapeState';
import { shouldRestoreOrgTreeModeDrop } from './lib/orgTreeModeDrag';
import { resolveScenarioPositionSyncDecision } from './lib/scenarioPositionSync';
import { useNavigationStore } from '../../stores/navigationStore';
import { useLinkPreviewStore } from '../../stores/linkPreviewStore';
import { CanvasPluginHosts } from './components/CanvasPluginHosts';
import { CanvasPopups } from './components/CanvasPopups';
import { usePluginHostState } from './hooks/usePluginHostState';
import { useOrgChartHostBridge } from './hooks/useOrgChartHostBridge';
import { useCanvasTextEditing } from './hooks/useCanvasTextEditing';
import { useFlowchartTextLayoutSync } from './hooks/useFlowchartTextLayoutSync';
import { useCanvasPopupsState } from './hooks/useCanvasPopupsState';
import { useCanvasDropHandlers } from './hooks/useCanvasDropHandlers';
import { useCanvasImageInsertion } from './hooks/useCanvasImageInsertion';
import { useCanvasPdfInsertion } from './hooks/useCanvasPdfInsertion';
import { useCanvasPanZoomHandlers } from './hooks/useCanvasPanZoomHandlers';
import { useCanvasStagePointerHandlers } from './hooks/useCanvasStagePointerHandlers';
import { useCompositedStagePan } from './hooks/useCompositedStagePan';
import { projectPositionChartViewport } from '../../domains/orgchart/services/positionChartViewportProjection';
import { useViewportStageController } from './hooks/useViewportStageController';
import { useCanvasTransformHandlers } from './hooks/useCanvasTransformHandlers';
import { useCanvasTestHarness } from './hooks/useCanvasTestHarness';
import { useCanvasKeyboardShortcuts } from './hooks/useCanvasKeyboardShortcuts';
import { useCanvasUrlShapeFocus } from './hooks/useCanvasUrlShapeFocus';
import { useCanvasStageResize } from './hooks/useCanvasStageResize';
import { useCanvasFrameToolActivation } from './hooks/useCanvasFrameToolActivation';
import { useCanvasLinkIndicatorListener } from './hooks/useCanvasLinkIndicatorListener';
import { useCanvasMovementToolToggle } from './hooks/useCanvasMovementToolToggle';
import { useCanvasAutoConnectorPathingEngine } from './hooks/useCanvasAutoConnectorPathingEngine';
import { useCanvasPluginsLoader } from './hooks/useCanvasPluginsLoader';
import { useCanvasCommentPlacementBridge } from './hooks/useCanvasCommentPlacementBridge';
import { useDiagramMediaReferenceSyncListener } from '../../domains/media/hooks/useDiagramMediaReleaseListener';
import { useMediaUpload } from '../../domains/media/hooks/useMediaUpload';
import { DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT } from '../../domains/media/constants/mediaReleaseEvents';
import { isLikelyStoredMediaObjectKey } from '../../domains/media/utils/mediaRefObjectKeyMatch';
import { computeCommentRelativeLocOnShape } from '../../lib/commentShapePlacement';
import { DiagramDocumentContext } from '../../contexts/DiagramDocumentContext';
import { useSuccessionPlanningProjection } from '../../domains/orgchart/hooks/useSuccessionPlanningProjection';
import { computeFullTreeLayout } from '../../domains/orgchart/services/graphAnalysis';

// New renderer components
import { EdgesRenderer } from './renderers/EdgesRenderer';
import { NodesRenderer } from './renderers/NodesRenderer';
import { OverlaysLayer } from './layers/OverlaysLayer';
import { TransformersLayer } from './layers/TransformersLayer';
import { EditableTextOverlay } from './EditableTextOverlay';

import type { CanvasTestingBridge } from '../../testing/CanvasTestingBridge';
import { DockNodeToTableCellCommand } from '../../core/commands/DockNodeToTableCellCommand';
import { ReflowDockedNodesInTableCellCommand } from '../../core/commands/ReflowDockedNodesInTableCellCommand';
import { AutoResizeTableCellToFitDockedContentCommand } from '../../core/commands/AutoResizeTableCellToFitDockedContentCommand';
import { getTableDropHitAtPoint } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { resolveCellLayoutConfig } from '../../domains/diagram/shapes/table/tableCellLayout.registry';


// Tool system imports
import { EventBus } from '../../core/events/IEventBus';
import { CommandManager } from '../../core/commands/CommandManager';
import { getCommandManager } from '../../hooks/useCommandManager';
import { ToolRegistry } from '../../core/tools/ToolRegistry';
import { ShortcutPriority } from '../../core/keyboard/KeyboardShortcutManager';
import { useTableCellStore } from '../../stores/tableCellStore';
import { IToolContext, ContextType, type DiagramType, type PresentationOwnership } from '../../core/tools/interfaces/ITool';
import type { Entitlements } from '../../domains/access/models/Entitlements';
import { MoveNodeCommand, MoveNodesCommand } from '../../core/commands/TransformCommands';
import { isProjectedNodePlacementLocked, isProjectionOwnedNode } from '../../core/canvas/composeProjectedScene';
import { UpdateEdgeCommand } from '../../core/commands/edges/UpdateEdgeCommand';
import { AutoPathEdgesCommand } from '../../core/commands/edges/AutoPathEdgesCommand';
import { MoveFreeLineCommand } from '../../core/commands/edges/MoveFreeLineCommand';
import { isFreeEndpointConnector } from '../../core/connectors/isFreeEndpointConnector';
import { DeleteNodeCommand } from '../../core/commands/DeleteNodeCommand';
import type { CommentRestorationCallbacks } from '../../domains/comments/models/Comment';
import { getShapeManifest } from '../../lib/diagram/registries';
import { collectGroupMembersByKey, expandNodeIdsWithGroupMembers, getNodeGroupKey } from '../../lib/diagram/grouping';
import { useScenariosStore } from '../../core/assistant/stores/scenariosStore';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { UpdateImageNodeAttrsCommand } from '../../core/commands/UpdateImageNodeAttrsCommand';
import { DefaultCanvasToolsPlugin } from './plugins/DefaultCanvasToolsPlugin';
import type { CanvasPlugin } from '../../core/canvas/CanvasPlugin';

// Frame system imports
import { FrameManager } from '../../core/frames/FrameManager';
import { observeYjsFrames, snapshotFrames } from './lib/observeYjsFrames';
import { IFrame, IFrameBounds } from '../../core/frames/IFrame';
import { FrameRenderer, FramePreview } from './FrameRenderer';
import { resolveEdgeRenderDiagramType } from './lib/diagramTypeResolution';
import { loadOrgChartDeps } from './lib/orgChartDeps';
import { releaseOrgProjectionHold } from './lib/orgProjectionLoadBoundary';
import { ContainerShapeLabelOverlay } from './ContainerShapeLabelOverlay';
import { OrgDisplayRuleLabelsOverlay } from '../../domains/orgchart/components/OrgDisplayRuleLabelsOverlay';
import { useCompassContainerDragSync } from './hooks/useCompassContainerDragSync';
import VersionedComponentRegistry from '../../lib/diagram/VersionedComponentRegistry';
import { useUIStore } from '../../stores/uiStore';
import { Clipboard } from '../../core/clipboard/Clipboard';
import { buildClipboardSelection, hasClipboardContent } from '../../core/clipboard/clipboardSelection';
import { PasteNodesCommand } from '../../core/commands/PasteNodesCommand';
import { CutNodesCommand } from '../../core/commands/CutNodesCommand';
import { AlignmentGuidesService } from '../../domains/diagram/services/alignment/AlignmentGuidesService';
import { DistributionGuidesService } from '../../domains/diagram/services/alignment/DistributionGuidesService';
import { TransformSnapService } from '../../domains/diagram/services/alignment/TransformSnapService';
import type { AlignmentGuideCandidate, AlignmentLine } from '../../domains/diagram/models/AlignmentGuides';
import type { DistributionGapSegment } from '../../domains/diagram/models/DistributionGuides';
import type { TransformHandle } from '../../domains/diagram/models/TransformSnap';
import type { Bounds } from '../../core/shapes/models/ShapeViewModel';

// Comments layer imports
import type { CommentNode, UpdateCommentPayload } from '../../domains/comments/models/Comment';
import { CommentPinsLayer } from '../comments/CommentPinsLayer';
import { useCommentOverlayStore } from '../../stores/commentOverlayStore';
import { useDiagramComments } from '../../hooks/comments/useDiagramComments';
import type { Edge, Node } from '../../core/models/scene';
import { isPresentationAnnotation } from '../../core/canvas/composeProjectedScene';
import { getProfileForDiagramType } from '../../core/data-profiles';
import { computeProjectedScenarioView as computeProjectedScenarioViewGeneric } from '../../core/data-profiles/scenarios/useProjectedView';
import type { BaseShape } from '../../core/shapes/BaseShape';
import { positionCommentNode, computeDiagramPointFromShape } from '../../lib/commentPositioning';
import { getGravityUserId } from '../../hooks/useAuth';
import { Logger } from '../../utils/logger';
import { snapPointToGrid } from '../../lib/diagram/gridSnap';
import {
  extractFirstSupportedImageFileFromDataTransfer,
  isEditableDomTarget,
} from './utils/canvasImageFiles';
import { pickBestFileFromNavigatorClipboardItems } from './utils/clipboardImagePreference';
import { isFirefox } from '../../utils/isFirefox';
import { useTranslation } from '../../hooks/useTranslation';
import { ImageUploadModal } from './components/ImageUploadModal';
import { isOrgPersonNodeType } from '../../domains/orgchart/models/defaults';
import { globalEventBus } from '../../core/events/IEventBus';

const canvasLogger = Logger.getLoggerWithPrefix('DiagramCanvasWithTransform');
const EMPTY_NODES: Node[] = [];
const EMPTY_EDGES: Edge[] = [];
const MIN_PDF_NODE_WIDTH = 120;
const MIN_PDF_NODE_HEIGHT = 84;
const MANIFEST_CARD_TEXT_X = 52;
const MANIFEST_CARD_TEXT_RIGHT_PADDING = 10;
const MANIFEST_CARD_MIN_EDITOR_WIDTH = 80;

function getNodeDimension(node: Node, keys: Array<'width' | 'height' | 'w' | 'h'>, fallback: number): number {
  const record = node as Node & Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return fallback;
}

function isManifestResourceCardNameEditNode(
  node: Node | undefined,
  field: 'name' | 'location' | 'text' | undefined,
): boolean {
  if (!node || field !== 'name') return false;

  const nodeRecord = node as Node & Record<string, unknown>;
  const attrs = nodeRecord.attrs && typeof nodeRecord.attrs === 'object' && !Array.isArray(nodeRecord.attrs)
    ? nodeRecord.attrs as Record<string, unknown>
    : {};
  const width = getNodeDimension(node, ['width', 'w'], 180);
  const height = getNodeDimension(node, ['height', 'h'], 60);
  if (width >= 300 && height >= 150) return false;

  const type = typeof nodeRecord.type === 'string' ? nodeRecord.type : '';
  const manifestId = typeof attrs._manifestId === 'string' ? attrs._manifestId : type;
  return (
    typeof attrs.resourceType === 'string' ||
    typeof attrs.iconUrl === 'string' ||
    /^(aws|azure|gcp|k8s|generic|basic)_/.test(manifestId)
  );
}

function getManifestResourceCardNameEditorWidth(node: Node): number {
  const width = getNodeDimension(node, ['width', 'w'], 180);
  return Math.max(
    MANIFEST_CARD_MIN_EDITOR_WIDTH,
    width - MANIFEST_CARD_TEXT_X - MANIFEST_CARD_TEXT_RIGHT_PADDING,
  );
}

interface DiagramCanvasWithTransformProps {
  stageRef?: React.RefObject<any>;
  layerRef?: React.RefObject<any>;
  eventBus?: EventBus;
  commandManager?: CommandManager;
  toolRegistry?: ToolRegistry;
  diagramType?: DiagramType;
  // Optimistic preview support
  awareness?: any;
  updatePreview?: (shapeId: string, changes: Record<string, any>) => void;
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T;
  setUserSelection?: (shapeIds: string[]) => void;
  clearPreviewForShape?: (shapeId: string) => void;
  userInfo?: { id: string; name: string; color: string } | null;
  doc?: Y.Doc | null;
  // Permission controls
  canWrite?: boolean;
  userRole?: string;
  entitlements?: Entitlements;
  isPositionChart?: boolean;
  isPlanningChart?: boolean;
  /** Live projection with no active draft — structural verbs gate on this. */
  projectionLocked?: boolean;
  /** An Atlas saved custom view — scenario/succession launcher tools gate on this. */
  isSavedAtlasView?: boolean;
  /** A saved view rendered from the server scene (no source-folder access, or not yet confirmed). */
  isSceneScopedAtlasView?: boolean;
  /** Live People Charts keep projected person-card geometry under layout ownership. */
  lockProjectedNodePlacement?: boolean;
  /**
   * World-space bounds measured from the Konva nodes that are actually
   * rendered. Projection-based domains use this for DOM overlays that must
   * follow view-time layout rather than persisted node coordinates.
   */
  onRenderedSelectionBoundsChange?: (selection: {
    selectionKey: string;
    bounds: Bounds;
  } | null) => void;
}

type ModalReplaceTargetType = 'image-node' | 'org-person';

type ImageUploadModalReplaceTarget = {
  nodeId: string;
  targetType: ModalReplaceTargetType;
  hasImage: boolean;
};

type ImageUploadModalOpenOptions = {
  mode?: 'insert' | 'replace';
  maxFiles?: number;
  title?: string;
  description?: string;
  replaceTarget?: ImageUploadModalReplaceTarget;
};

/**
 * Main genogram canvas component with full tool integration.
 * 
 * This component provides the complete genogram editing experience including:
 * - Interactive canvas with person nodes and relationships
 * - Full tool system (selection, creation, editing, etc.)
 * - Undo/redo command management
 * - Frame-based organization
 * - Real-time collaboration
 * - Touch and mouse support
 * 
 * The component manages several subsystems:
 * - Rendering: React-Konva for 2D graphics
 * - State: Zustand store for application state
 * - Tools: Pluggable tool system for operations
 * - Commands: Command pattern for undo/redo
 * - Events: Event bus for loose coupling
 * - Frames: Organizational containers
 * 
 * @example
 * ```tsx
 * <GenogramCanvasWithTransform />
 * ```
 */
export const DiagramCanvasWithTransform: React.FC<DiagramCanvasWithTransformProps> = ({
  stageRef: externalStageRef,
  layerRef: externalLayerRef,
  eventBus: externalEventBus,
  commandManager: externalCommandManager,
  toolRegistry: externalToolRegistry,
  diagramType,
  awareness,
  updatePreview,
  getShapeState,
  setUserSelection,
  clearPreviewForShape,
  doc,
  userInfo,
  canWrite: documentCanWrite = false,
  entitlements,
  isPositionChart = false,
  isPlanningChart = false,
  projectionLocked = false,
  isSavedAtlasView = false,
  isSceneScopedAtlasView = false,
  lockProjectedNodePlacement: automaticPlacementLocked = false,
  onRenderedSelectionBoundsChange,
}) => {
  const projectedLayoutMode = useProjectedLayoutMode(doc);
  const { t } = useTranslation('diagram');
  const diagramDocument = useContext(DiagramDocumentContext);
  const historyScope = useHistoryPreviewStore((state) => state.scope);
  const historyActive = Boolean(historyScope && historyScope.diagramId === diagramDocument?.diagramId);
  const canWrite = documentCanWrite && !historyActive;
  const internalStageRef = useRef<Konva.Stage>(null);
  const { mode } = useThemeMode();
  const internalLayerRef = useRef<Konva.Layer>(null);
  const { closePopupsOnDeselect } = usePopupManager();
  const spacePressedRef = useRef<boolean>(false); // deprecated for panning; kept for compatibility
  const prevSelectedIdsRef = useRef<Set<string>>(new Set());

  // Use external refs if provided, otherwise use internal refs
  const stageRef = externalStageRef || internalStageRef;
  const layerRef = externalLayerRef || internalLayerRef;
  const transformerRef = useRef<Konva.Transformer>(null);
  const frameTransformerRef = useRef<Konva.Transformer>(null);
  const canvasPasteHostRef = useRef<HTMLDivElement | null>(null);
  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);
  const [imageUploadModalConfig, setImageUploadModalConfig] = useState<ImageUploadModalOpenOptions>({});

  // Expose stage to window for minimap snapshot
  useEffect(() => {
    if (stageRef.current) {
      (window as any).__konvaStage = stageRef.current;
    }
    return () => {
      delete (window as any).__konvaStage;
    };
  }, []);

  const frameRectRefs = useRef<Map<string, Konva.Rect>>(new Map());
  const nodeRefs = useRef<Map<string, Konva.Node>>(new Map());
  const frameGroupRefs = useRef<Map<string, Konva.Node>>(new Map());
  const lastFrameDragPosRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const edgeRefs = useRef<Map<string, Konva.Node>>(new Map());
  const testCanWriteOverrideRef = useRef<boolean | null>(null);
  // Fine-grained connector recompute: per-edge versions
  const edgeVersionsRef = useRef<Map<string, number>>(new Map());
  const [edgeVersions, setEdgeVersions] = useState<Map<string, number>>(new Map());
  const dirtyEdgeIdsRef = useRef<Set<string>>(new Set());
  const dirtyEdgesRafRef = useRef<number | null>(null);
  const [canvasPlugins, setCanvasPlugins] = useState<CanvasPlugin[]>([]);
  const canvasPluginsRef = useRef<CanvasPlugin[]>([]);
  canvasPluginsRef.current = canvasPlugins;
  const { pluginHostStateById, setPluginHostState } = usePluginHostState();
  const flushDirtyEdges = useCallback(() => {
    const pending = dirtyEdgeIdsRef.current;
    if (pending.size === 0) return;
    const next = new Map(edgeVersionsRef.current);
    pending.forEach((id) => {
      next.set(id, (next.get(id) || 0) + 1);
    });
    edgeVersionsRef.current = next;
    setEdgeVersions(next);
    pending.clear();
  }, []);
  const scheduleDirtyEdges = useCallback((edgeIds: Iterable<string>) => {
    for (const id of edgeIds) dirtyEdgeIdsRef.current.add(id);
    if (dirtyEdgesRafRef.current) return;
    dirtyEdgesRafRef.current = requestAnimationFrame(() => {
      dirtyEdgesRafRef.current = null;
      flushDirtyEdges();
    });
  }, [flushDirtyEdges]);
  const computeAffectedEdgesForNode = (nodeId: string): Iterable<string> => {
    for (const plugin of canvasPluginsRef.current) {
      if (plugin.computeAffectedEdgesForNode) return plugin.computeAffectedEdgesForNode(edges, nodeId);
    }
    const affected = new Set<string>();
    for (const edge of edges) {
      if (edge.from === nodeId || edge.to === nodeId) affected.add(edge.id);
    }
    return affected;
  };
  const scheduleDirtyForNode = (nodeId: string) => {
    scheduleDirtyEdges(computeAffectedEdgesForNode(nodeId));
  };
  const scheduleDirtyForNodes = (nodeIds: Iterable<string>) => {
    for (const plugin of canvasPluginsRef.current) {
      if (plugin.computeAffectedEdgesForNodes) {
        scheduleDirtyEdges(plugin.computeAffectedEdgesForNodes(edges, nodeIds));
        return;
      }
    }
    const moved = new Set<string>();
    for (const id of nodeIds) moved.add(id);
    if (moved.size === 0) return;
    const affected = new Set<string>();
    for (const edge of edges) {
      if (moved.has(edge.from) || moved.has(edge.to)) affected.add(edge.id);
    }
    if (affected.size > 0) scheduleDirtyEdges(affected);
  };
  const requestConnectorRefresh = (input: { nodeIds?: string[]; edgeIds?: string[] }) => {
    const directEdgeIds = input.edgeIds?.filter(Boolean) ?? [];
    const nodeIds = input.nodeIds?.filter(Boolean) ?? [];
    const run = () => {
      if (directEdgeIds.length > 0) {
        scheduleDirtyEdges(directEdgeIds);
      }
      if (nodeIds.length > 0) {
        scheduleDirtyForNodes(nodeIds);
      }
    };
    run();
    requestAnimationFrame(run);
    setTimeout(run, 50);
    setTimeout(run, 150);
  };
  const testBridgeRef = useRef<CanvasTestingBridge | null>(null);
  const framesMapRef = useRef<Y.Map<any> | null>(null);
  const isSyncingFramesToYjs = useRef(false);
  const isSyncingFramesFromYjs = useRef(false);
  const lastShortcutAtRef = useRef<{ paste: number; cut: number; copy: number; delete: number }>({ paste: 0, cut: 0, copy: 0, delete: 0 });


  // Imperative preview overlay
  const previewTargetsRef = useRef<Map<string, { x: number; y: number; timestamp: number }>>(new Map());
  const smoothedRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const rafRef = useRef<number | null>(null);
  const [overlayVersion, setOverlayVersion] = useState(0);
  const lastSentPreviewRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const [isDraggingShape, setIsDraggingShape] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  const alignmentStaticCandidatesRef = useRef<AlignmentGuideCandidate[]>([]);
  const alignmentMovingIdsRef = useRef<Set<string>>(new Set());
  const alignmentLastSnappedPosRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const rotationStaticCandidatesRef = useRef<AlignmentGuideCandidate[]>([]);
  const transformStaticCandidatesRef = useRef<AlignmentGuideCandidate[]>([]);
  const transformMovingIdsRef = useRef<Set<string>>(new Set());

  const alignmentGuidesRafRef = useRef<number | null>(null);
  const distributionGuidesRafRef = useRef<number | null>(null);
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentLine[]>([]);
  const [distributionSegments, setDistributionSegments] = useState<DistributionGapSegment[]>([]);
  const setAlignmentGuidesThrottled = useCallback((next: AlignmentLine[]) => {
    if (alignmentGuidesRafRef.current) {
      cancelAnimationFrame(alignmentGuidesRafRef.current);
      alignmentGuidesRafRef.current = null;
    }
    alignmentGuidesRafRef.current = requestAnimationFrame(() => {
      alignmentGuidesRafRef.current = null;
      setAlignmentGuides(next);
    });
  }, []);
  const setDistributionSegmentsThrottled = useCallback((next: DistributionGapSegment[]) => {
    if (distributionGuidesRafRef.current) {
      cancelAnimationFrame(distributionGuidesRafRef.current);
      distributionGuidesRafRef.current = null;
    }
    distributionGuidesRafRef.current = requestAnimationFrame(() => {
      distributionGuidesRafRef.current = null;
      setDistributionSegments(next);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (alignmentGuidesRafRef.current) {
        cancelAnimationFrame(alignmentGuidesRafRef.current);
        alignmentGuidesRafRef.current = null;
      }
      if (distributionGuidesRafRef.current) {
        cancelAnimationFrame(distributionGuidesRafRef.current);
        distributionGuidesRafRef.current = null;
      }
    };
  }, []);

  const nodes = useDiagramStore((state) => state.nodes);
  const edges = useDiagramStore((state) => state.edges);
  const selectedIds = useDiagramStore((state) => state.selectedIds);
  const setSelected = useDiagramStore((state) => state.setSelected);
  const updateNode = useDiagramStore((state) => state.updateNode);
  const updateNodes = useDiagramStore((state) => state.updateNodes);
  const addNode = useDiagramStore((state) => state.addNode);
  const deleteNode = useDiagramStore((state) => state.deleteNode);
  const addEdge = useDiagramStore((state) => state.addEdge);
  const deleteEdge = useDiagramStore((state) => state.deleteEdge);
  const storeStage = useDiagramStore((state) => state.stage);
  const setStage = useDiagramStore((state) => state.setStage);
  const {
    previewCompositedPan,
    settleCompositedPan,
  } = useCompositedStagePan(stageRef as React.RefObject<Konva.Stage | null>);
  const {
    viewportStage,
    previewViewportStage,
    getLatestViewportStage,
    commitViewportStageDebounced,
    commitViewportStageImmediate,
  } = useViewportStageController(storeStage, setStage, 120, settleCompositedPan);
  const previewPanViewportStage = useCallback((next: {
    scale: number;
    offsetX: number;
    offsetY: number;
  }) => {
    previewCompositedPan(next);
    previewViewportStage(next);
  }, [previewCompositedPan, previewViewportStage]);

	  const {
	    orgHostState,
	    collapsedNodeIds,
	    getOrgDeps,
	    ensureOrgDeps,
	    markOrgNodeDirty,
	    orgReportCounts,
	    orgReportingTargets,
	    orgChartTenure,
      getCsvRecord: getCsvRecordFromHost,
      getCsvChildren: getCsvChildrenFromHost,
      getCsvSecondaryManager: getCsvSecondaryManagerFromHost,
	  } = useOrgChartHostBridge(pluginHostStateById);
  const lockProjectedNodePlacement = automaticPlacementLocked
    && (projectedLayoutMode !== 'custom' || Boolean(orgHostState?.asOfDate));
	  const previousNodesRef = useRef<Map<string, Node>>(new Map());
	  const [orgExitNodes, setOrgExitNodes] = useState<Map<string, Node>>(new Map());
	  const orgExitTimersRef = useRef<Map<string, number>>(new Map());
	  const previousRenderedNodesRef = useRef<Map<string, Node>>(new Map());

	  const activeScenarioId = useScenariosStore((state) => state.activeScenarioId);
	  const scenarioState = useScenariosStore((state) => state.scenarioState);
	  const connectorAuthoringMode = useScenariosStore((state) => state.connectorAuthoringMode);
	  const activeScenario = useScenariosStore((state) => state.getActiveScenario());
	  const isExplicitScenarioAuthoring = scenarioState === 'EDITING_SCENARIO'
	    && connectorAuthoringMode === 'primary_reporting';

  type ScenarioProjection = {
    nodes: Node[];
    edges: Edge[];
    statusIndex: unknown;
    isScenarioActive: boolean;
    scenarioName: unknown;
    isEditingScenario: boolean;
  };

  // Detect stale orgHostState: scenario store says inactive but orgHostState still has active projection
  // (happens because orgHostState updates via useEffect one render later)
  const isOrgHostScenarioStale = diagramType === 'org'
    && !activeScenarioId
    && !!orgHostState?.scenarioProjection?.isScenarioActive;

  // An org chart is only meaningful once its projection modules have loaded —
  // before that the host publishes the raw store scene, which for a large chart
  // is every person uncollapsed. Rendering it paints hundreds of cards that are
  // torn straight back down once the real projection lands. Hold the scene back
  // instead. If the projection modules reject we fall through to the raw scene
  // rather than leaving the canvas blank. Successful loading is signalled by
  // the org plugin host itself; it is no longer approximated by a timer.
  const isOrgProjectionPending = diagramType === 'org' && !orgHostState?.orgDeps;
  const [orgProjectionUnavailable, setOrgProjectionUnavailable] = useState(false);
  useEffect(() => {
    if (diagramType !== 'org') {
      setOrgProjectionUnavailable(false);
      return;
    }
    // A slow-but-live plugin may still publish later. This boundary controls
    // only how long the raw scene is hidden, so a stalled dynamic import can
    // never leave the canvas blank forever.
    return releaseOrgProjectionHold({
      load: loadOrgChartDeps,
      release: () => setOrgProjectionUnavailable(true),
    });
  }, [diagramType]);
  const holdOrgSceneForProjection =
    isOrgProjectionPending && !orgProjectionUnavailable && !isOrgHostScenarioStale;

  // Scenario projection - applies delta when viewing/editing a scenario
  const scenarioProjection = useMemo<ScenarioProjection>(() => {
    if (diagramType === 'org' && orgHostState?.scenarioProjection && !isOrgHostScenarioStale) {
      return orgHostState.scenarioProjection;
    }

    if (diagramType === 'org' && isOrgHostScenarioStale) {
      return {
        nodes,
        edges,
        statusIndex: null,
        isScenarioActive: false,
        scenarioName: null,
        isEditingScenario: false,
      };
    }

    const profile = diagramType ? getProfileForDiagramType(diagramType) : null;
    const projector = profile?.scenarioConfig?.projector;
    if (!projector) {
      return {
        nodes,
        edges,
        statusIndex: null,
        isScenarioActive: false,
        scenarioName: null,
        isEditingScenario: false,
      };
    }

    return computeProjectedScenarioViewGeneric(
      nodes,
      edges,
      activeScenarioId,
      scenarioState,
      activeScenario as any,
      projector as any
    );
  }, [diagramType, orgHostState?.scenarioProjection, isOrgHostScenarioStale, nodes, edges, activeScenarioId, scenarioState, activeScenario]);
  const scenarioNodes = scenarioProjection.isScenarioActive ? scenarioProjection.nodes : nodes;
  const scenarioEdges = scenarioProjection.isScenarioActive ? scenarioProjection.edges : edges;

  const projectedNodes: Node[] =
    diagramType === 'org' ? (isOrgHostScenarioStale ? nodes : ((orgHostState?.projectedNodes as Node[] | undefined) ?? nodes)) : scenarioNodes;
  const projectedEdges: Edge[] =
    diagramType === 'org' ? (isOrgHostScenarioStale ? edges : ((orgHostState?.projectedEdges as Edge[] | undefined) ?? edges)) : scenarioEdges;
  const focusedEdges: Edge[] =
    diagramType === 'org' ? (isOrgHostScenarioStale ? edges : ((orgHostState?.focusedEdges as Edge[] | undefined) ?? projectedEdges)) : projectedEdges;
  const baseRenderedNodes: Node[] =
    diagramType === 'org'
      ? (holdOrgSceneForProjection
          ? EMPTY_NODES
          : isOrgHostScenarioStale
            ? nodes
            : ((orgHostState?.renderedNodes as Node[] | undefined) ?? projectedNodes))
      : scenarioNodes;
  const baseRenderedEdges: Edge[] =
    diagramType === 'org'
      ? (holdOrgSceneForProjection
          ? EMPTY_EDGES
          : isOrgHostScenarioStale
            ? edges
            : ((orgHostState?.renderedEdges as Edge[] | undefined) ?? projectedEdges))
      : scenarioEdges;

  const successionCanvasProjection = useSuccessionPlanningProjection({
    folderId: diagramDocument?.folderId,
    diagramId: diagramDocument?.diagramId,
    nodes: baseRenderedNodes,
    edges: baseRenderedEdges,
    // Succession discovery reads the source folder, so a saved view that is
    // rendering the server scene must never start it.
    enabled: diagramType === 'org' && !isSceneScopedAtlasView,
  });

  const storedPreviewNodes = useHistoryPreviewStore((s) => s.previewNodes);
  const storedPreviewEdges = useHistoryPreviewStore((s) => s.previewEdges);
  const historicalProjection = useOrgHistoryPreview(diagramDocument?.diagramId, diagramType === 'org');
  const collapsedHistoricalPreview = useMemo(() => {
    const scene = historicalProjection.scene;
    if (!historyActive || !scene || diagramType !== 'org' || collapsedNodeIds.length === 0) return scene;
    const applyCollapse = (orgHostState?.orgDeps as any)?.applyOrgChartCollapse;
    if (typeof applyCollapse !== 'function') return scene;
    const collapsed = applyCollapse(scene.nodes, scene.edges, collapsedNodeIds);
    const positions = computeFullTreeLayout(collapsed.nodes, collapsed.edges);
    if (positions.size === 0) return collapsed;
    return {
      ...collapsed,
      nodes: collapsed.nodes.map((node: Node) => {
        const position = positions.get(node.id);
        return position ? { ...node, x: position.x, y: position.y } : node;
      }),
    };
  }, [collapsedNodeIds, diagramType, historyActive, historicalProjection.scene, orgHostState?.orgDeps]);
  // A presentation-only snapshot must never replace a source-backed chart.
  // Keep the current authorized scene during loading/failure, with explicit
  // status in the history panel; never substitute current facts as history.
  const previewNodes = historyActive
    ? historicalProjection.managed ? collapsedHistoricalPreview?.nodes : storedPreviewNodes
    : null;
  const previewEdges = historyActive
    ? historicalProjection.managed ? collapsedHistoricalPreview?.edges : storedPreviewEdges
    : null;

  let renderedNodes: Node[] = successionCanvasProjection.nodes;
  let renderedEdges: Edge[] = successionCanvasProjection.edges;

  if (previewNodes) renderedNodes = previewNodes;
  if (previewEdges) renderedEdges = previewEdges;

  useEffect(() => {
    if (diagramType !== 'org') return;
    if (renderedEdges.length === 0) return;

    const orgEdgeIds = renderedEdges
      .filter((edge) => {
        const edgeType = String(edge.type ?? '');
        return edgeType === 'reporting' || edgeType === 'reports_to' || edgeType === 'scenario_reporting';
      })
      .map((edge) => edge.id);

    if (orgEdgeIds.length === 0) return;

    let raf1: number | null = null;
    let raf2: number | null = null;
    let timeoutId: number | null = null;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        scheduleDirtyEdges(orgEdgeIds);
        timeoutId = window.setTimeout(() => scheduleDirtyEdges(orgEdgeIds), 50);
      });
    });

    return () => {
      if (raf1 !== null) cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [diagramType, historyActive, renderedEdges, scheduleDirtyEdges]);

  // A history preview swaps both topology and geometry without touching the
  // live Yjs scene. Invalidate every visible historical edge after the swap so
  // cached routes cannot survive with endpoints from the current chart.
  useEffect(() => {
    if (!historyActive || !previewEdges || previewEdges.length === 0) return;
    scheduleDirtyEdges(previewEdges.map((edge: Edge) => edge.id));
  }, [historyActive, previewEdges, scheduleDirtyEdges]);

  // Expose currently visible nodes for minimap bounds/snapshot (org uses collapsed projection).
  useEffect(() => {
    (window as any).__minimapNodes = diagramType === 'org' ? renderedNodes : projectedNodes;
    return () => {
      delete (window as any).__minimapNodes;
    };
  }, [diagramType, renderedNodes, projectedNodes]);

  let interactiveNodes: Node[] = diagramType === 'org' ? renderedNodes : projectedNodes;
  let interactiveEdges: Edge[] = diagramType === 'org' ? renderedEdges : projectedEdges;

  // Org viewport-fit (+ expand-fit animation) is hosted by the orgchart CanvasPlugin.


  if (previewNodes) interactiveNodes = previewNodes;
  if (previewEdges) interactiveEdges = previewEdges;
  // Refs for keyboard handlers to access the visible (interactive) set
  const interactiveNodesRef = useRef(interactiveNodes);
  const interactiveEdgesRef = useRef(interactiveEdges);
  interactiveNodesRef.current = interactiveNodes;
  interactiveEdgesRef.current = interactiveEdges;
  const getDockedTableIdForNode = useCallback((nodeId: string | null | undefined): string | null => {
    if (!nodeId) return null;
    const n = nodes.find((node) => node.id === nodeId);
    return ((n?.attrs as any)?.tableDock?.tableId as string | undefined) || null;
  }, [nodes]);

  // When connecting from a shape docked inside a table, avoid snapping to that table's border.
  // Otherwise tables should remain valid snap targets.
  const getSnapCandidatesForSourceNode = useCallback((sourceNodeId: string | null | undefined) => {
    const dockedTableId = getDockedTableIdForNode(sourceNodeId);
    if (!dockedTableId) return interactiveNodes;
    return interactiveNodes.filter((n) => n.type !== 'table' || n.id !== dockedTableId);
  }, [getDockedTableIdForNode, interactiveNodes]);
  const enableOrgCollapseAnimation = diagramType === 'org' && !historyActive && renderedNodes.length <= 200;
  const orgExitNodeIds = useMemo(() => new Set(orgExitNodes.keys()), [orgExitNodes]);
  const renderedNodesWithExit = useMemo(() => {
    if (diagramType !== 'org' || historyActive || orgExitNodes.size === 0) return renderedNodes;
    const merged = new Map<string, Node>();
    renderedNodes.forEach((node) => merged.set(node.id, node));
    orgExitNodes.forEach((node, nodeId) => {
      if (!merged.has(nodeId)) merged.set(nodeId, node);
    });
    return Array.from(merged.values());
  }, [diagramType, historyActive, orgExitNodes, renderedNodes]);

  useLayoutEffect(() => {
    if (diagramType !== 'org' || !enableOrgCollapseAnimation) {
      if (orgExitNodes.size > 0) {
        setOrgExitNodes(new Map());
      }
      orgExitTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      orgExitTimersRef.current.clear();
      previousRenderedNodesRef.current = new Map(renderedNodes.map((node) => [node.id, node]));
      return;
    }

    const currentIds = new Set(renderedNodes.map((node) => node.id));
    const previousMap = previousRenderedNodesRef.current;
    const removedNodes: Node[] = [];
    previousMap.forEach((node, nodeId) => {
      if (!currentIds.has(nodeId)) removedNodes.push(node);
    });

    setOrgExitNodes((prev) => {
      const next = new Map(prev);
      currentIds.forEach((nodeId) => {
        if (!next.has(nodeId)) return;
        const timerId = orgExitTimersRef.current.get(nodeId);
        if (timerId !== undefined) window.clearTimeout(timerId);
        orgExitTimersRef.current.delete(nodeId);
        next.delete(nodeId);
      });

      removedNodes.forEach((node) => {
        if (next.has(node.id)) return;
        next.set(node.id, node);
        const timerId = window.setTimeout(() => {
          setOrgExitNodes((latest) => {
            if (!latest.has(node.id)) return latest;
            const updated = new Map(latest);
            updated.delete(node.id);
            return updated;
          });
          orgExitTimersRef.current.delete(node.id);
        }, 200);
        orgExitTimersRef.current.set(node.id, timerId);
      });
      return next;
    });

    previousRenderedNodesRef.current = new Map(renderedNodes.map((node) => [node.id, node]));
  }, [diagramType, enableOrgCollapseAnimation, renderedNodes, orgExitNodes.size]);

  // Track recently restored comments to prevent race condition with useEffect unpinning
  const recentlyRestoredCommentsRef = useRef<Set<string>>(new Set());

  // Track node geometry changes from collaborators and command commits, then
  // invalidate affected connectors. Resize commits update width/height without
  // necessarily changing position, so size changes must use the same path as moves.
  useEffect(() => {
    const prev = previousNodesRef.current;
    const changedGeometryNodeIds = new Set<string>();

    const getNodeDimension = (node: Node, primary: 'w' | 'h', fallback: 'width' | 'height') => {
      const value = (node as any)[primary] ?? (node as any)[fallback];
      return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    };

    nodes.forEach((node) => {
      const prevNode = prev.get(node.id);
      if (prevNode) {
        const positionChanged = prevNode.x !== node.x || prevNode.y !== node.y;
        const sizeChanged =
          getNodeDimension(prevNode, 'w', 'width') !== getNodeDimension(node, 'w', 'width') ||
          getNodeDimension(prevNode, 'h', 'height') !== getNodeDimension(node, 'h', 'height');

        if (positionChanged || sizeChanged) {
          changedGeometryNodeIds.add(node.id);
        }
      }
      prev.set(node.id, { ...node });
    });

    // Schedule connector updates for moved or resized nodes
    if (changedGeometryNodeIds.size > 0) {
      changedGeometryNodeIds.forEach((nodeId) => {
        scheduleDirtyForNode(nodeId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]); // scheduleDirtyForNode is stable (defined in component scope)

  // Node lookup map so overlays/tools can resolve geometry without scanning the array.
  const nodesById = useMemo(() => {
    const map = new Map<string, Node>();
    interactiveNodes.forEach((node) => {
      map.set(node.id, node);
    });
    return map;
  }, [interactiveNodes]);

  // Ensure connectors layout correct when edges appear after nodes mount.
  // 1) One-time pass for the first batch of edges
  const didInitialConnectorsLayoutRef = useRef(false);
  useEffect(() => {
    if (didInitialConnectorsLayoutRef.current) return;
    if (!edges || edges.length === 0) return;
    didInitialConnectorsLayoutRef.current = true;
    const ids = edges.map((e) => e.id);
    const r1 = requestAnimationFrame(() => scheduleDirtyEdges(ids));
    const r2 = requestAnimationFrame(() => scheduleDirtyEdges(ids));
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [edges]);

  // 2.5) After a wholesale scene load (e.g., template import), wait until
  // all current nodes have mounted refs, then trigger a full connectors
  // recompute so adapters can read correct client rects on first paint.
  useEffect(() => {
    if (!edges || edges.length === 0) return;
    const nodeCount = nodes.length;
    if (nodeCount === 0) return;
    // If not all refs are mounted yet, poll briefly over a couple of frames.
    let attempts = 0;
    let raf: number | null = null;
    const tick = () => {
      attempts++;
      const mounted = nodeRefs.current.size;
      if (mounted >= nodeCount) {
        const ids = edges.map((e) => e.id);
        // also add a tiny timeout to catch any late clientRect readiness
        setTimeout(() => scheduleDirtyEdges(ids), 50);
        if (raf) cancelAnimationFrame(raf);
        return;
      }
      if (attempts < 8) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [nodes, edges]);

  // 2) Subsequent passes for newly added edges (diff-based)
  const prevEdgeIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const current = new Set<string>();
    edges.forEach((e) => current.add(e.id));
    const prev = prevEdgeIdsRef.current;
    const added: string[] = [];
    current.forEach((id) => { if (!prev.has(id)) added.push(id); });
    prevEdgeIdsRef.current = current;
    if (added.length === 0) return;
    const r1 = requestAnimationFrame(() => scheduleDirtyEdges(added));
    const r2 = requestAnimationFrame(() => scheduleDirtyEdges(added));
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [edges]);

  const [dimensions, setDimensions] = useState({
    width: 800,
    height: 600
  });

  const [isSelecting, setIsSelecting] = useState(false);
  const [frames, setFrames] = useState<IFrame[]>([]);
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const setFramesInStore = useDiagramStore(s => s.setFrames);
  const [framePreviewBounds, setFramePreviewBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Sync frames to global store
  useEffect(() => {
    setFramesInStore(frames);
  }, [frames, setFramesInStore]);
  const stageScaleRef = useRef(1);
  // Compass container → children drag cascade (swimlane-style). Mount
  // once at top level; the hook is a no-op for diagrams that have no
  // `attrs.isContainer` nodes, so it's safe to run everywhere.
  useCompassContainerDragSync();
  useEffect(() => {
    stageScaleRef.current = viewportStage.scale || 1;
  }, [viewportStage.scale]);

  const toTransformHandle = useCallback((activeAnchor: string | null | undefined): TransformHandle => {
    const a = activeAnchor || null;
    if (!a) return null;
    if (
      a === 'top-left' ||
      a === 'top' ||
      a === 'top-right' ||
      a === 'left' ||
      a === 'right' ||
      a === 'bottom-left' ||
      a === 'bottom' ||
      a === 'bottom-right' ||
      a === 'rotater'
    ) {
      return a;
    }
    return null;
  }, []);

  const translateBounds = useCallback((b: Bounds, dx: number, dy: number): Bounds => {
    return { x: b.x + dx, y: b.y + dy, width: b.width, height: b.height };
  }, []);

  const getCanvasBoundsForNodeId = useCallback(
    (id: string): Bounds | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      const stageScale = stage.scaleX?.() || stageScaleRef.current || 1;
      const stagePos = stage.position?.() || { x: 0, y: 0 };
      const container = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
      if (!container) return null;

      try {
        const storeNode = nodes.find((n) => n.id === id) as any;
        const variant = String(storeNode?.attrs?.variant ?? storeNode?.attrs?.shapeType ?? '').toLowerCase();
        const useFullContainer = variant === 'bpmn-annotation';
        const inner =
          !useFullContainer && (container as any)?.findOne
            ? ((container as any).findOne('.rotation-group') as Konva.Node | null)
            : null;
        const measureNode: Konva.Node = (inner || (container as unknown as Konva.Node)) as Konva.Node;
        const raw = measureNode.getClientRect({ skipShadow: true });
        return {
          x: (raw.x - stagePos.x) / stageScale,
          y: (raw.y - stagePos.y) / stageScale,
          width: raw.width / stageScale,
          height: raw.height / stageScale,
        };
      } catch {
        return null;
      }
    },
    [nodes, stageRef, nodeRefs, stageScaleRef]
  );

  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const isPanMode = useUIStore(s => s.isPanMode);
  const setPanMode = useUIStore(s => s.setPanMode);
  const alignmentGuidesEnabled = useUIStore(s => s.alignmentGuidesEnabled);
  const alignmentGuidesSnapEnabled = useUIStore(s => s.alignmentGuidesSnapEnabled);
  const distributionGuidesEnabled = useUIStore(s => s.distributionGuidesEnabled);
  const distributionGuidesSnapEnabled = useUIStore(s => s.distributionGuidesSnapEnabled);
  const setAlignmentGuidesEnabled = useUIStore(s => s.setAlignmentGuidesEnabled);
  const setAlignmentGuidesSnapEnabled = useUIStore(s => s.setAlignmentGuidesSnapEnabled);
  const setDistributionGuidesEnabled = useUIStore(s => s.setDistributionGuidesEnabled);
  const setDistributionGuidesSnapEnabled = useUIStore(s => s.setDistributionGuidesSnapEnabled);
  const ORG_GUIDES_NODE_LIMIT = 250;
  const shouldDisableGuidesForOrgSize = diagramType === 'org' && renderedNodes.length > ORG_GUIDES_NODE_LIMIT;
  const effectiveAlignmentGuidesEnabled = alignmentGuidesEnabled && !shouldDisableGuidesForOrgSize;
  const effectiveAlignmentGuidesSnapEnabled = alignmentGuidesSnapEnabled && effectiveAlignmentGuidesEnabled;
  const effectiveDistributionGuidesEnabled = distributionGuidesEnabled && !shouldDisableGuidesForOrgSize;
  const effectiveDistributionGuidesSnapEnabled =
    distributionGuidesSnapEnabled && effectiveDistributionGuidesEnabled;
  const panOverlayActiveRef = useRef(false);

  useEffect(() => {
    if (diagramType !== 'org') return;
    // Keep org charts in the low-latency path by default; users can re-enable guides explicitly.
    setAlignmentGuidesEnabled(false);
    setAlignmentGuidesSnapEnabled(false);
    setDistributionGuidesEnabled(false);
    setDistributionGuidesSnapEnabled(false);
  }, [
    diagramType,
    setAlignmentGuidesEnabled,
    setAlignmentGuidesSnapEnabled,
    setDistributionGuidesEnabled,
    setDistributionGuidesSnapEnabled,
  ]);

  const handleNodesTransformerBoundBox = useCallback(
    (oldBox: { x: number; y: number; width: number; height: number; rotation: number }, newBox: { x: number; y: number; width: number; height: number; rotation: number }) => {
      // Prevent PDF cards from being resized below their visual-content minimum.
      // Returning oldBox keeps Konva anchor behavior stable at the clamp boundary.
      try {
        const movingIds = Array.from(transformMovingIdsRef.current);
        if (movingIds.length === 1) {
          const movingNode = nodes.find((node) => node.id === movingIds[0]);
          if (movingNode?.type === 'pdf') {
            const stage = stageRef.current;
            const stageScale = stage?.scaleX?.() || stageScaleRef.current || 1;
            const minWidth = MIN_PDF_NODE_WIDTH * stageScale;
            const minHeight = MIN_PDF_NODE_HEIGHT * stageScale;
            if (Math.abs(newBox.width) < minWidth || Math.abs(newBox.height) < minHeight) {
              return oldBox;
            }
          }
        }
      } catch {}

      if (!effectiveAlignmentGuidesEnabled) {
        setDistributionSegmentsThrottled([]);
        setAlignmentGuidesThrottled([]);
        return newBox;
      }

      const tr = transformerRef.current as any;
      const activeAnchor = tr?.getActiveAnchor?.() as string | null | undefined;
      const activeHandle = toTransformHandle(activeAnchor);

      const stage = stageRef.current;
      const stageScale = stage?.scaleX?.() || stageScaleRef.current || 1;
      const stagePos = stage?.position?.() || { x: 0, y: 0 };
      const threshold = 6 / stageScale;

      const toCanvasBox = (box: { x: number; y: number; width: number; height: number; rotation: number }): Bounds => {
        return {
          x: (box.x - stagePos.x) / stageScale,
          y: (box.y - stagePos.y) / stageScale,
          width: box.width / stageScale,
          height: box.height / stageScale,
        };
      };

      const toTransformerBox = (canvasBox: Bounds, base: { x: number; y: number; width: number; height: number; rotation: number }) => {
        return {
          x: canvasBox.x * stageScale + stagePos.x,
          y: canvasBox.y * stageScale + stagePos.y,
          width: canvasBox.width * stageScale,
          height: canvasBox.height * stageScale,
          rotation: base.rotation,
        };
      };

      const out = TransformSnapService.compute({
        movingIds: Array.from(transformMovingIdsRef.current),
        oldBox: toCanvasBox(oldBox),
        newBox: toCanvasBox(newBox),
        activeHandle,
        staticCandidates: transformStaticCandidatesRef.current,
        threshold,
      });

      setDistributionSegmentsThrottled([]);
      setAlignmentGuidesThrottled(out.guides);

      if (!effectiveAlignmentGuidesSnapEnabled) return newBox;
      return toTransformerBox(out.snappedBox, newBox);
    },
    [
      effectiveAlignmentGuidesEnabled,
      effectiveAlignmentGuidesSnapEnabled,
      setAlignmentGuidesThrottled,
      setDistributionSegmentsThrottled,
      stageRef,
      stageScaleRef,
      toTransformHandle,
      nodes,
      transformMovingIdsRef,
      transformStaticCandidatesRef,
      transformerRef,
    ]
  );

  const alignmentViewportRect = useMemo(() => {
    const scale = viewportStage.scale || 1;
    const pos = { x: viewportStage.offsetX || 0, y: viewportStage.offsetY || 0 };
    return {
      left: (0 - pos.x) / scale,
      top: (0 - pos.y) / scale,
      right: (dimensions.width - pos.x) / scale,
      bottom: (dimensions.height - pos.y) / scale,
    };
  }, [dimensions.height, dimensions.width, viewportStage.offsetX, viewportStage.offsetY, viewportStage.scale]);

  // Handle cursor changes when pan mode toggles
  useEffect(() => {
    if (isPanMode) {
      document.body.style.cursor = 'grab';
    } else {
      document.body.style.cursor = 'default';
    }

    return () => {
      document.body.style.cursor = 'default';
    };
  }, [isPanMode]);

  // When pan mode turns on, detach node transformers so resize anchors aren't interactive
  useEffect(() => {
    if (isPanMode && transformerRef.current) {
      try { transformerRef.current.nodes([]); transformerRef.current.getLayer()?.batchDraw(); } catch { }
    }
  }, [isPanMode]);

  const [selectionRect, setSelectionRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    visible: false
  });

  const [, forceUpdate] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [isEndpointDragging, setIsEndpointDragging] = useState(false);
  const [firstConnectNode, setFirstConnectNode] = useState<string | null>(null);
  const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);
  const [hoveredTargetAnchor, setHoveredTargetAnchor] = useState<'top' | 'right' | 'bottom' | 'left' | null>(null);
  const [hoveredTargetPoint, setHoveredTargetPoint] = useState<{ x: number; y: number } | null>(null);
  const [hoveredTargetAnchors, setHoveredTargetAnchors] = useState<Array<{ id: string; side: 'top' | 'right' | 'bottom' | 'left'; pt: { x: number; y: number } }>>([]);
  const [hoveredDropFrameId, setHoveredDropFrameId] = useState<string | null>(null);
  // Marriage bar hover preview for initial connector drag
  const [hoveredMarriageBar, setHoveredMarriageBar] = useState<
    | { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }
    | null
  >(null);
  const [connectionPreview, setConnectionPreview] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    visible: boolean;
  }>({ startX: 0, startY: 0, endX: 0, endY: 0, visible: false });
  const previewStartAnchorRef = useRef<'top' | 'right' | 'bottom' | 'left' | null>(null);
  const previewEndAnchorRef = useRef<'top' | 'right' | 'bottom' | 'left' | null>(null);
  const autoStartAnchorRef = useRef(false);
  const connectorDragSourceRef = useRef<string | null>(null);
  const freeConnectionStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const cancelConnectingMode = useCallback(() => {
    setIsConnecting(false);
    setFirstConnectNode(null);
    setConnectionPreview((prev) => ({ ...prev, visible: false }));
    setHoveredMarriageBar(null);
    setHoveredTargetId(null);
    setHoveredTargetAnchor(null);
    setHoveredTargetPoint(null);
    setHoveredTargetAnchors([]);
    autoStartAnchorRef.current = false;
    previewStartAnchorRef.current = null;
    previewEndAnchorRef.current = null;
    connectorDragSourceRef.current = null;
    freeConnectionStartPointRef.current = null;
    document.body.style.cursor = 'default';
  }, []);
  // Rotation interaction state
  const [isRotating, setIsRotating] = useState(false);

  const rotateTargetIdRef = useRef<string | null>(null);
  const rotateStartAngleRef = useRef<number>(0);
  const rotateInitialRotationRef = useRef<number>(0);
  const rotatePreviewRef = useRef<number>(0);
  const rotateInnerRef = useRef<Konva.Node | null>(null);
  const rotateCenterRef = useRef<{ cx: number; cy: number }>({ cx: 0, cy: 0 });
  const rotateSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const rotateRafRef = useRef<number | null>(null);
  const hasFocusedFromUrlRef = useRef<boolean>(false);
  // removed hover state; cursor is handled inside overlays
  const updateRotateHandlePosition = (_angle: number) => { };
  // Live pen preview
  const [penPreview, setPenPreview] = useState<{
    points: number[];
    color: string;
    width: number;
    opacity: number;
    visible: boolean;
  }>({ points: [], color: '#000', width: 2, opacity: 1, visible: false });
  const [placePreview, setPlacePreview] = useState<{
    x: number; y: number; width?: number; height?: number; visible: boolean; shapeId?: string; category?: string; fillColor?: string; strokeColor?: string;
    fromPoint?: { x: number; y: number };
    toPoint?: { x: number; y: number };
  }>({ x: 0, y: 0, visible: false });

  const diagramId = useMemo(() => {
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string };
    return globalObj.__DIAGRAM_ID__ ?? 'demo-diagram';
  }, []);

  useDiagramMediaReferenceSyncListener(diagramId);
  const { uploadImage } = useMediaUpload({ source: 'canvas.image', diagramId });

  const currentUserId = useMemo(() => {
    const fromAwareness = userInfo?.id;
    const fromToken = getGravityUserId();
    const resolved = fromAwareness?.trim() || fromToken?.trim() || 'anonymous';
    return resolved.toLowerCase();
  }, [userInfo]);

  // Permission check helper
  const checkWritePermission = useCallback(() => {
    if (!canWrite) {
      return false;
    }
    return true;
  }, [canWrite]);

  // Shared comment data/mutations used by both pins and overlays for consistency.
  const {
    comments: diagramComments,
    createRootComment,
    createReply,
    isCreating,
    isReplying,
    resolveComment,
    reopenComment,
    isResolving,
    isReopening,
    updateComment,
    isUpdating,
    softDeleteComment,
    isDeleting,
  } = useDiagramComments(diagramId, currentUserId);

  const visibleNodeIds = useMemo(() => {
    return new Set(renderedNodes.map((node) => node.id));
  }, [renderedNodes]);

  // Comment pin helpers need node geometry; precompute absolute positions once per render cycle.
  const positionedComments: CommentNode[] = useMemo(
    () =>
      diagramComments
        .filter((comment) => {
          if (!comment.shapeId) return true;
          return visibleNodeIds.has(comment.shapeId);
        })
        .map((comment) => positionCommentNode(comment, nodesById)),
    [diagramComments, nodesById, visibleNodeIds],
  );

  /**
   * Helper function to create DeleteNodeCommand with comment restoration callbacks.
   *
   * This function should be used when creating DeleteNodeCommand instances within
   * this component or components that have access to it, to enable comment pinning
   * restoration when deletion is undone.
   *
   * Note: Tools create commands without callbacks (backward compatible), so comment
   * restoration only works when commands are created using this helper.
   *
   * @param nodeIds - Array of node IDs to delete
   * @returns DeleteNodeCommand with comment restoration enabled
   */
  const createDeleteNodeCommandWithComments = useCallback((nodeIds: string[]): DeleteNodeCommand => {
    const callbacks: CommentRestorationCallbacks = {
      getCommentsForShapes: async (shapeIds: string[]) => {
        // Filter comments from current diagramComments that are pinned to the given shapes
        return diagramComments.filter(
          (comment) =>
            comment.parentId === null &&
            comment.shapeId !== null &&
            shapeIds.includes(comment.shapeId)
        );
      },
      updateComment: async (commentId: string, updates: UpdateCommentPayload) => {
        await updateComment(commentId, updates);
      },
      markCommentsAsRestored: (commentIds: string[]) => {
        // Mark comments as recently restored to prevent race condition with useEffect
        commentIds.forEach((id) => {
          recentlyRestoredCommentsRef.current.add(id);
        });
        // Clear after delay (handled by existing useEffect)
      },
    };

    return new DeleteNodeCommand(nodeIds, callbacks);
  }, [diagramComments, updateComment]);

  useEffect(() => {
    const previousMap = previousNodesRef.current;
    const currentMap = new Map<string, Node>();
    nodes.forEach((node) => currentMap.set(node.id, node));

    const removedNodes: Node[] = [];
    previousMap.forEach((node, nodeId) => {
      if (!currentMap.has(nodeId)) {
        removedNodes.push(node);
      }
    });

    previousNodesRef.current = currentMap;

    if (removedNodes.length === 0 || diagramComments.length === 0) {
      return;
    }

    const tasks: Array<{ promise: Promise<void>; commentId: string; shapeId: string }> = [];

    removedNodes.forEach((removedNode) => {
      const rootComments = diagramComments.filter(
        (comment) =>
          comment.parentId === null &&
          comment.shapeId === removedNode.id &&
          comment.locOnShape &&
          comment.shapeId !== null &&
          !recentlyRestoredCommentsRef.current.has(comment.id), // Skip recently restored comments
      );

      if (rootComments.length === 0) {
        return;
      }

      rootComments.forEach((comment) => {
        if (!comment.locOnShape) {
          return;
        }

        const derivedLocation = computeDiagramPointFromShape(removedNode, comment.locOnShape);
        const fallbackLocation = comment.locOnDiagram ? { ...comment.locOnDiagram } : null;
        let targetLocation = derivedLocation ?? fallbackLocation;

        if (!targetLocation) {
          const widthCandidate =
            typeof removedNode.width === 'number' && Number.isFinite(removedNode.width) && removedNode.width > 0
              ? removedNode.width
              : typeof removedNode.w === 'number' && Number.isFinite(removedNode.w) && removedNode.w > 0
                ? removedNode.w
                : 0;
          const heightCandidate =
            typeof removedNode.height === 'number' && Number.isFinite(removedNode.height) && removedNode.height > 0
              ? removedNode.height
              : typeof removedNode.h === 'number' && Number.isFinite(removedNode.h) && removedNode.h > 0
                ? removedNode.h
                : 0;

          targetLocation = {
            x: removedNode.x + widthCandidate / 2,
            y: removedNode.y + heightCandidate / 2,
          };
        }

        const promise = updateComment(comment.id, {
          shapeId: null,
          locOnShape: null,
          locOnDiagram: targetLocation,
        });

        tasks.push({
          promise,
          commentId: comment.id,
          shapeId: removedNode.id,
        });
      });
    });

    if (tasks.length === 0) {
      return;
    }

    void Promise.allSettled(tasks.map((task) => task.promise)).then((results) => {
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedTask = tasks[index];
          Logger.warn('DiagramCanvasWithTransform', 'Failed to unpin comment after shape deletion', {
            commentId: failedTask.commentId,
            shapeId: failedTask.shapeId,
            error: result.reason,
          });
        }
      });
    });
  }, [nodes, diagramComments, updateComment]);

  // Clear recently restored comments after a delay to allow undo operations to complete
  useEffect(() => {
    const timeout = setTimeout(() => {
      recentlyRestoredCommentsRef.current.clear();
    }, 1000); // 1 second should be enough for undo to complete
    return () => clearTimeout(timeout);
  }, [nodes]); // Clear when nodes change

  // Minimal shape stubs exposed to the tool registry so tool logic can query bounds.
  // Uses interactiveNodes (visible after collapse) instead of all nodes for performance.
  const toolShapesMap = useMemo(() => {
    const map = new Map<string, BaseShape<any>>();
    interactiveNodes.forEach((node) => {
      const widthCandidate = typeof node.width === 'number' ? node.width : typeof node.w === 'number' ? node.w : undefined;
      const heightCandidate = typeof node.height === 'number' ? node.height : typeof node.h === 'number' ? node.h : undefined;
      const width = widthCandidate && Number.isFinite(widthCandidate) && widthCandidate > 0 ? widthCandidate : 60;
      const height = heightCandidate && Number.isFinite(heightCandidate) && heightCandidate > 0 ? heightCandidate : 60;
      const bounds = {
        x: node.x,
        y: node.y,
        width,
        height,
      };
      const shapeStub = {
        getBounds: () => bounds,
        // Include node data for tools that need to access attrs (e.g., LinkDiagramTool)
        id: node.id,
        type: node.type,
        data: node,
        attrs: (node as any).attrs || {},
      } as unknown as BaseShape<any>;
      map.set(node.id, shapeStub);
    });
    return map;
  }, [interactiveNodes]);
  const toolShapesMapRef = useRef(toolShapesMap);
  toolShapesMapRef.current = toolShapesMap;
  // Overlay store wiring for highlighting pins and clearing selection from stage interactions.
  const activeCommentId = useCommentOverlayStore((state) => state.activeCommentId);
  const clearActiveComment = useCommentOverlayStore((state) => state.clearActiveComment);

  const {
    handleWheel,
    handlePanOverlayWheel,
    handlePanOverlayMouseDown,
    handlePanOverlayMouseMove,
    handlePanOverlayMouseUp,
  } = useCanvasPanZoomHandlers({
    stageRef: stageRef as any,
    clearActiveComment,
    isPanMode,
    isPanning,
    setIsPanning,
    panStart,
    setPanStart,
    panOverlayActiveRef,
    previewViewportStage: previewPanViewportStage,
    getLatestViewportStage,
    commitViewportStageDebounced,
    commitViewportStageImmediate
  });

  const [shapePickerState, setShapePickerState] = useState<{
    visible: boolean;
    position: { x: number; y: number };
    sourceNodeId: string | null;
    sourceSide: 'top' | 'right' | 'bottom' | 'left' | null;
    sourceRect: { x: number; y: number; width: number; height: number } | null;
  }>({
    visible: false,
    position: { x: 0, y: 0 },
    sourceNodeId: null,
    sourceSide: null,
    sourceRect: null
  });

  // Track drag start positions for creating move commands
  const dragStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Track docked-node start positions so we can move them visually with their parent table during drag.
  const dockedDragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const dockedDragDeltaRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const dockedPreviewRafRef = useRef<number | null>(null);
  const [dockedPreviewTick, setDockedPreviewTick] = useState(0);
  const draggedTableIdsRef = useRef<Set<string>>(new Set());
  const dragSessionIdRef = useRef(0);
  const activeDragSessionIdRef = useRef(0);
  const draggingSessionByIdRef = useRef<Map<string, number>>(new Map());
  // Track last seen position of the actively dragged node (reserved for future debug)
  const dragLastPos = useRef<Map<string, { x: number; y: number }>>(new Map());
  const transformStartState = useRef<Map<string, { x: number; y: number; w: number; h: number }>>(new Map());
  const frameTransformStartState = useRef<Map<string, IFrameBounds>>(new Map());
  // Track free-line start points for multi-drag (so they move with selected shapes)
  const selectedFreeLineIdsRef = useRef<Set<string>>(new Set());
  const freeLineDragStartPointsRef = useRef<
    Map<string, { fromPoint: { x: number; y: number }; toPoint: { x: number; y: number } }>
  >(new Map());
  // Track selection-drag (initiated from an edge) so dragging a selected free line moves the whole selection.
  const selectionDragActiveRef = useRef<boolean>(false);
  const selectionDragDeltaRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const selectionDragStartNodePosRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const selectionDragNodeIdsRef = useRef<Set<string>>(new Set());
  const selectionDragFreeLineIdsRef = useRef<Set<string>>(new Set());
  const selectionDragFreeLineStartPointsRef = useRef<
    Map<string, { fromPoint: { x: number; y: number }; toPoint: { x: number; y: number } }>
  >(new Map());

  // Clear drag positions when selection changes to prevent stale data.
  // Node updates can arrive while a shape is actively being dragged; keep the
  // start snapshot intact so preview-only drops can return to the visual origin.
  useEffect(() => {
    if (dragStartPositions.current.size > 0) return;
    dragStartPositions.current.clear();
    dockedDragStartPositionsRef.current.clear();
    dockedDragDeltaRef.current = { dx: 0, dy: 0 };
    draggedTableIdsRef.current.clear();
    if (dockedPreviewRafRef.current != null) {
      cancelAnimationFrame(dockedPreviewRafRef.current);
      dockedPreviewRafRef.current = null;
    }
  }, [selectedIds, nodes]);

  const setDraggingGuarded = useCallback((id: string, dragging: boolean) => {
    const store = useDiagramStore.getState();
    const session = activeDragSessionIdRef.current;
    if (dragging) {
      store.setDragging(id, true);
      draggingSessionByIdRef.current.set(id, session);
      return;
    }
    const recorded = draggingSessionByIdRef.current.get(id);
    if (recorded !== session) return;
    store.setDragging(id, false);
    draggingSessionByIdRef.current.delete(id);
  }, []);

  const nodesForRender = useMemo(() => {
    if (dockedDragStartPositionsRef.current.size === 0) return renderedNodesWithExit;
    const { dx, dy } = dockedDragDeltaRef.current;
    if (dx === 0 && dy === 0) return renderedNodesWithExit;
    return renderedNodesWithExit.map((n) => {
      const start = dockedDragStartPositionsRef.current.get(n.id);
      if (!start) return n;
      return { ...n, x: start.x + dx, y: start.y + dy };
    });
  }, [renderedNodesWithExit, dockedPreviewTick]);
  const positionChartViewportProjection = useMemo(() => {
    if (!isPositionChart) {
      return { nodes: nodesForRender, edges: renderedEdges };
    }
    return projectPositionChartViewport(nodesForRender, renderedEdges, {
      scale: viewportStage.scale,
      offsetX: viewportStage.offsetX,
      offsetY: viewportStage.offsetY,
      width: dimensions.width,
      height: dimensions.height,
    });
  }, [
    dimensions.height,
    dimensions.width,
    isPositionChart,
    nodesForRender,
    renderedEdges,
    viewportStage.offsetX,
    viewportStage.offsetY,
    viewportStage.scale,
  ]);
  const canvasNodesForRender = positionChartViewportProjection.nodes;
  const canvasEdgesForRender = positionChartViewportProjection.edges;

  // The org-chart projection can reflow visible cards without changing the
  // persisted scene. DOM chrome must therefore use the measured Konva bounds,
  // not the store positions that were used to build the projection.
  useLayoutEffect(() => {
    if (!onRenderedSelectionBoundsChange) return;
    if (diagramType !== 'org') {
      onRenderedSelectionBoundsChange(null);
      return;
    }

    const selectedRenderedNodes = canvasNodesForRender.filter((node) => selectedIds.has(node.id));
    const selectionKey = selectedRenderedNodes.map((node) => node.id).sort().join('|');
    if (selectedRenderedNodes.length === 0) {
      onRenderedSelectionBoundsChange(null);
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const measured = selectedRenderedNodes
        .map((node) => getCanvasBoundsForNodeId(node.id))
        .filter((bounds): bounds is Bounds => bounds !== null);
      if (measured.length === 0) {
        onRenderedSelectionBoundsChange(null);
        return;
      }

      const minX = Math.min(...measured.map((bounds) => bounds.x));
      const minY = Math.min(...measured.map((bounds) => bounds.y));
      const maxX = Math.max(...measured.map((bounds) => bounds.x + bounds.width));
      const maxY = Math.max(...measured.map((bounds) => bounds.y + bounds.height));
      onRenderedSelectionBoundsChange({
        selectionKey,
        bounds: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    canvasNodesForRender,
    diagramType,
    getCanvasBoundsForNodeId,
    onRenderedSelectionBoundsChange,
    selectedIds,
  ]);

  // Force-sync Konva node positions with projected data during scenario preview.
  // react-konva may not update positions through memo barriers; this ensures Konva
  // nodes match the projected positions after every render.
  // Also syncs once on deactivation to revert positions.
  const prevScenarioActiveRef = useRef(false);
  useLayoutEffect(() => {
    const syncDecision = resolveScenarioPositionSyncDecision({
      isStaleOrgHostScenario: isOrgHostScenarioStale,
      isScenarioActive: scenarioProjection.isScenarioActive,
      wasScenarioActive: prevScenarioActiveRef.current,
    });
    prevScenarioActiveRef.current = syncDecision.nextWasScenarioActive;
    if (!syncDecision.shouldSyncPositions) return;

    const refs = nodeRefs.current;
    if (refs.size === 0) return;

    let updated = false;
    for (const node of nodesForRender) {
      const konvaNode = refs.get(node.id);
      if (!konvaNode) continue;
      if (Math.abs(konvaNode.x() - node.x) > 1 || Math.abs(konvaNode.y() - node.y) > 1) {
        konvaNode.x(node.x);
        konvaNode.y(node.y);
        updated = true;
      }
    }

    if (updated) {
      stageRef.current?.batchDraw();
    }
  }, [isOrgHostScenarioStale, scenarioProjection.isScenarioActive, nodesForRender]);

  // Initialize tool system - use external instances if provided, or global command manager
  const eventBus = useMemo(() => externalEventBus || new EventBus(), [externalEventBus]);
  const commandManager = useMemo(() => externalCommandManager || getCommandManager(), [externalCommandManager]);

  const { popupStyle, linkPrompt, setLinkPrompt, insertBetweenPicker, setInsertBetweenPicker } = useCanvasPopupsState({
    shapePickerSourceSide: shapePickerState.sourceSide,
    nodes,
    edges,
    eventBus,
    selectedIds,
    nodeRefs,
    stageRef,
  });

  const handleToggleOrgCollapseCommand = useCallback((reportingNodeId: string) => {
    if (diagramType === 'org' && typeof orgHostState?.toggleCollapsedBranch === 'function') {
      orgHostState.toggleCollapsedBranch(reportingNodeId);
      return;
    }

    const plugin = canvasPluginsRef.current.find((p) => typeof p.executeToggleCollapseCommand === 'function');
    plugin?.executeToggleCollapseCommand?.({ reportingNodeId, commandManager });
  }, [commandManager, diagramType, orgHostState?.toggleCollapsedBranch]);

  // Provide CSV cache record lookup for dot grid color resolution (unhydrated lazy nodes)
  const getCsvRecord = useCallback((nodeId: string): Record<string, unknown> | undefined => {
    return getCsvRecordFromHost(nodeId);
  }, [getCsvRecordFromHost]);

  // Provide CSV cache children lookup for dot grid BFS (unhydrated lazy nodes)
  const getCsvChildren = useCallback((nodeId: string): string[] | undefined => {
    return getCsvChildrenFromHost(nodeId);
  }, [getCsvChildrenFromHost]);

  // Provide CSV cache secondary-manager lookup for collapsed dot grid highlights
  // (lets us flag hidden, unhydrated people who dotted-line-report to a
  // selected node without waiting for their edges to be materialized).
  const getCsvSecondaryManager = useCallback((nodeId: string): string | null | undefined => {
    return getCsvSecondaryManagerFromHost(nodeId);
  }, [getCsvSecondaryManagerFromHost]);

  const handleExpandOrgCollapsedBranchOneLevel = useCallback((collapsedReportingNodeId: string) => {
    if (diagramType !== 'org') return;
    if (typeof orgHostState?.expandCollapsedBranchOneLevel !== 'function') return;
    orgHostState.expandCollapsedBranchOneLevel(collapsedReportingNodeId);
  }, [diagramType, orgHostState?.expandCollapsedBranchOneLevel]);

  // Initialize frame manager
  const frameManager = useMemo(() => new FrameManager(eventBus), [eventBus]);

  // Bridge DOM canvas actions to the app event bus (e.g., from context menu)
  useEffect(() => {
    const onCanvasAction = (ev: any) => {
      try {
        const action = ev?.detail?.action;
        if (action === 'add-comment') {
          const state = useCanvasContextMenu.getState();
          const target = state.target;
          const isShapeTarget = target?.kind === 'shape';
          const shapeId = isShapeTarget ? target.shapeId : null;
          const locOnShape =
            isShapeTarget && shapeId
              ? computeCommentRelativeLocOnShape(shapeId, { x: state.diagramX, y: state.diagramY }, toolShapesMapRef.current)
              : null;
          eventBus.emit('comment:placement:open', {
            diagram: { x: state.diagramX, y: state.diagramY },
            local: { x: state.localX, y: state.localY },
            shapeId,
            locOnShape,
          });
        }
      } catch { }
    };
    window.addEventListener('canvas-action', onCanvasAction as any);
    return () => window.removeEventListener('canvas-action', onCanvasAction as any);
  }, [eventBus]);

  // Create tool context
  const effectiveDiagramType = useMemo<DiagramType>(() => {
    return diagramType ?? (frameManager.getActiveFrame()?.type || 'genogram');
  }, [diagramType, frameManager]);

  // Who owns presentation (layout + style). Supplied by the active domain
  // plugin (e.g. orgchart derives it from tree auto-layout state); absent
  // means 'user', which keeps freeform domains' style chrome unchanged.
  const [presentationOwnership, setPresentationOwnership] = useState<PresentationOwnership>('user');
  useEffect(() => {
    const resolveFromPlugins = () => {
      const plugin = canvasPluginsRef.current.find(
        (p) => p.diagramTypes.includes(effectiveDiagramType) && typeof p.getPresentationOwnership === 'function'
      );
      setPresentationOwnership(plugin?.getPresentationOwnership?.() ?? 'user');
    };
    resolveFromPlugins();

    const onOwnershipChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ diagramTypes?: string[] }>).detail;
      if (detail?.diagramTypes && !detail.diagramTypes.includes(effectiveDiagramType)) return;
      resolveFromPlugins();
    };
    window.addEventListener('creately:presentation-ownership-changed', onOwnershipChanged);
    return () => window.removeEventListener('creately:presentation-ownership-changed', onOwnershipChanged);
  }, [canvasPlugins, effectiveDiagramType]);

  const toolContext = useMemo<IToolContext>(() => {
    // Build selectionSummary with semantic context types expected by the tool registry.
    const contextTypes = new Set<ContextType>();
    const selectedNodes = Array.from(selectedIds)
      .map(id => nodes.find(n => n.id === id))
      .filter((node): node is Node => Boolean(node));
    const selectedConnectors = Array.from(selectedIds)
      .map((id) => edges.find((edge) => edge.id === id))
      .filter(Boolean) as Edge[];

    const getNodeContextType = (node: Node): ContextType => {
      const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
      const category = typeof attrs.category === 'string' ? attrs.category : undefined;

      if (node.type === 'text') return 'text';
      if (node.type === 'sticky-note' || category === 'sticky-note') return 'sticky-note';
      if (node.type === 'image') return 'image';
      if (node.type === 'pdf') return 'pdf';
      if (node.type === 'frame') return 'frame';
      if (node.type === 'person') return 'person';
      // Org person card variants (org_person, org_person_photo, …) are people
      // too; person-scoped tools gate further via applicability.diagramTypes.
      if (typeof node.type === 'string' && node.type.startsWith('org_person')) return 'person';

      return 'genericShape';
    };

    selectedNodes.forEach(node => {
      contextTypes.add(getNodeContextType(node));
    });

    if (selectedConnectors.length > 0 && selectedNodes.length === 0) {
      contextTypes.add('connector');
    }

    // Build proper SelectionSummary object
    const typesSet = new Set(contextTypes);
    const countsByType: Record<string, number> = {};

    selectedNodes.forEach(node => {
      const type = getNodeContextType(node);
      countsByType[type] = (countsByType[type] || 0) + 1;
    });

    if (selectedConnectors.length > 0) {
      countsByType.connector = (countsByType.connector || 0) + selectedConnectors.length;
    }

    const contextType = contextTypes.size === 1
      ? Array.from(contextTypes)[0]
      : contextTypes.size > 1
        ? 'mixed'
        : 'none';

    // Debug logging for sticky notes
    if (selectedNodes.some(n => n?.type === 'sticky-note')) {
      canvasLogger.info('Sticky note selection detected', {
        selectedNodes: selectedNodes.map(n => ({ id: n?.id, type: n?.type, attrs: n?.attrs })),
        contextTypes: Array.from(contextTypes),
        contextType,
        countsByType
      });
    }

    const selectionSummary = {
      contextType: contextType as ContextType,
      typesSet,
      countsByType,
      diagramType: effectiveDiagramType,
      flags: {
        hasText: selectedNodes.some(node =>
          node?.text || node?.name || node?.type === 'text' || node?.type === 'sticky-note'
        ),
        hasConnectors: selectedConnectors.length > 0,
      },
      version: 1
    };

    // Debug logging for sticky notes
    if (contextTypes.has('sticky-note')) {
      canvasLogger.info('Sticky note selection detected', {
        contextType,
        typesSet: Array.from(typesSet),
        hasText: selectionSummary.flags.hasText,
        selectedNodes: selectedNodes.map(n => ({ id: n?.id, type: n?.type }))
      });
    }

    // Build selectionStyles - aggregate common style properties from selected nodes
    let selectionStyles = undefined;
    if (selectedNodes.length > 0) {
      const firstNode = selectedNodes[0];
      selectionStyles = {
        fillColor: firstNode?.fillColor || (firstNode?.attrs as any)?.fillColor,
        strokeColor: firstNode?.strokeColor || (firstNode?.attrs as any)?.strokeColor,
        strokeWidth: (firstNode?.attrs as any)?.strokeWidth || 2,
        fontSize: (firstNode?.attrs as any)?.fontSize || firstNode?.fontSize,
        fontFamily: (firstNode?.attrs as any)?.fontFamily || firstNode?.fontFamily,
        textColor: (firstNode?.attrs as any)?.textColor,
        textAlign: (firstNode?.attrs as any)?.textAlign,
        verticalAlign: (firstNode?.attrs as any)?.verticalAlign,
        dash: (firstNode?.attrs as any)?.dash,
        dashEnabled: (firstNode?.attrs as any)?.dashEnabled,
        isMixed: {}
      };

      // Check if styles are mixed (different across selection)
      if (selectedNodes.length > 1) {
        for (const prop of ['fillColor', 'strokeColor', 'strokeWidth', 'fontSize', 'fontFamily'] as const) {
          const values = selectedNodes.map(n => (n as any)?.[prop] || (n?.attrs as any)?.[prop]);
          const uniqueValues = new Set(values);
          if (uniqueValues.size > 1) {
            (selectionStyles.isMixed as any)[prop] = true;
          }
        }
      }
    }

    // Ownership is selection-aware: annotations (text, sticky notes, images,
    // pdfs, frames) are user-authored presentation even on managed charts, so
    // annotation-only selections keep their style tools. Model-entity or
    // mixed selections resolve to the diagram-level ownership.
    const isAnnotationOnlySelection =
      selectedNodes.length > 0 &&
      selectedConnectors.length === 0 &&
      selectedNodes.every(isPresentationAnnotation);
    const resolvedPresentationOwnership: PresentationOwnership =
      isAnnotationOnlySelection ? 'user' : presentationOwnership;

    // Aggregate domain data fields the selection lens exposes to tools
    // (tools read the lens; they never scan stores).
    let selectionData = undefined;
    if (selectedNodes.length === 1 && contextType === 'person') {
      const only = selectedNodes[0];
      const attrs = ((only as any).attrs ?? {}) as Record<string, unknown>;
      selectionData = {
        name: typeof attrs.name === 'string' ? attrs.name : (only as any).name,
        departureDate: typeof attrs.departureDate === 'string' ? attrs.departureDate : undefined,
        isMixed: {},
      };
    }

    return {
      selection: selectedIds,
      shapes: toolShapesMap,
      connectors: new Map(
        edges.map((edge) => [
          edge.id,
          {
            id: edge.id,
            type: edge.type,
            from: edge.from,
            to: edge.to,
            data: edge,
            render: () => null,
            update: () => {},
            destroy: () => {},
          } as any,
        ]),
      ),
      frame: frameManager.getActiveFrame(),
      selectedFrameId,
      diagramType: effectiveDiagramType,
      stage: stageRef.current!,
      layer: layerRef.current!,
      eventBus,
      doc: doc ?? undefined, // Yjs document for persistence
      selectionSummary,
      selectionStyles,
      selectionData,
      presentationOwnership: resolvedPresentationOwnership,
      projectionLocked: projectionLocked && !isAnnotationOnlySelection,
      isSavedAtlasView,
      canWrite,
      entitlements,

      getSelectedShapes: () =>
        Array.from(selectedIds)
          .map((id) => toolShapesMap.get(id))
          .filter(Boolean) as any[],
      getSelectedConnectors: () =>
        selectedConnectors.map((edge) => ({
          id: edge.id,
          type: edge.type,
          from: edge.from,
          to: edge.to,
          data: edge,
          render: () => null,
          update: () => {},
          destroy: () => {},
        })) as any[],
      getShapesInFrame: () => Array.from(toolShapesMap.values()),

      // Provide comment restoration helper if available
      createDeleteNodeCommand: createDeleteNodeCommandWithComments
    };
  }, [selectedIds, selectedFrameId, toolShapesMap, eventBus, frameManager, stageRef, layerRef, doc, nodes, edges, canWrite, entitlements, createDeleteNodeCommandWithComments, effectiveDiagramType, presentationOwnership, projectionLocked, isSavedAtlasView]);

  // Initialize tool registry - use external instance if provided
  const toolRegistry = useMemo(() => {
    if (externalToolRegistry) {
      return externalToolRegistry;
    }
    return new ToolRegistry(
      toolContext,
      eventBus,
      commandManager,
      { autoActivateDefault: true }
    );
  }, [externalToolRegistry, eventBus, commandManager, toolContext]);

  const {
    editingTextState,
    handleTextEdit,
    handleStartTextEdit,
    handleStartTableCellTextEdit,
    handleStartEdgeTextEdit,
    handleCloseTextEdit,
    getInitialEditText,
    pendingEdgeEditId,
  } = useCanvasTextEditing({
    checkWritePermission,
    nodes,
    edges,
    selectedIds,
    nodeRefs,
    edgeRefs,
    eventBus,
    toolRegistry,
    commandManager,
    effectiveDiagramType,
    doc: doc ?? undefined,
    canvasPluginsRef,
    getOrgDeps,
  });

  useFlowchartTextLayoutSync({
    nodes,
    nodeRefs,
    commandManager,
    canWrite,
  });

  const { defaultDimensions, insertImageFileAt, insertImageUrlAt, insertImageFileAtViewportCenter, insertDroppedImageFiles, insertPickerModalImageFiles } =
    useCanvasImageInsertion({
    canWrite,
    diagramId,
    stageRef: stageRef as React.RefObject<Konva.Stage | null>,
    commandManager,
  });
  const { insertDroppedPdfFiles } = useCanvasPdfInsertion({
    canWrite,
    diagramId,
    stageRef: stageRef as React.RefObject<Konva.Stage | null>,
    commandManager,
  });

  const handleModalImageUpload = useCallback(async (files: File[]) => {
    const replaceTarget = imageUploadModalConfig.replaceTarget;
    if (!replaceTarget) {
      return insertPickerModalImageFiles(files);
    }

    const file = files[0];
    if (!file) {
      return { insertedCount: 0, failures: [] as Array<{ file: File; message: string }> };
    }

    const targetNode = nodes.find((node) => node.id === replaceTarget.nodeId);
    if (!targetNode) {
      return {
        insertedCount: 0,
        failures: [{ file, message: 'The selected shape is no longer available.' }],
      };
    }

    try {
      const uploaded = await uploadImage(file);
      if (replaceTarget.targetType === 'image-node') {
        const previousKey = String(
          (targetNode.attrs as Record<string, unknown> | undefined)?.objectKey
            ?? (targetNode as unknown as { objectKey?: unknown }).objectKey
            ?? '',
        ).trim();
        await commandManager.execute(
          new UpdateImageNodeAttrsCommand([
            {
              nodeId: targetNode.id,
              changes: {
                attrs: {
                  objectKey: uploaded.objectKey,
                  uploadState: 'ready',
                  uploadErrorMessage: undefined,
                },
              },
            },
          ]),
        );
        if (
          previousKey &&
          previousKey !== uploaded.objectKey &&
          isLikelyStoredMediaObjectKey(previousKey)
        ) {
          globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
            diagramId,
            addedKeys: [uploaded.objectKey],
            removedKeys: [previousKey],
          });
        } else {
          globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
            diagramId,
            addedKeys: [uploaded.objectKey],
          });
        }
      } else {
        if (!isOrgPersonNodeType(targetNode.type as string)) {
          return {
            insertedCount: 0,
            failures: [{ file, message: 'Selected shape does not support images.' }],
          };
        }
        const previousPhotoRef = String(
          (targetNode.attrs as Record<string, unknown> | undefined)?.photoRef
            ?? (targetNode as unknown as { photoRef?: unknown }).photoRef
            ?? '',
        ).trim();
        await commandManager.execute(
          new ChangeNodeStyleCommand([targetNode.id], 'photoRef', uploaded.objectKey),
        );
        if (
          previousPhotoRef &&
          previousPhotoRef !== uploaded.objectKey &&
          isLikelyStoredMediaObjectKey(previousPhotoRef)
        ) {
          globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
            diagramId,
            addedKeys: [uploaded.objectKey],
            removedKeys: [previousPhotoRef],
          });
        } else {
          globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
            diagramId,
            addedKeys: [uploaded.objectKey],
          });
        }
      }

      setImageUploadModalOpen(false);
      setImageUploadModalConfig({});
      return { insertedCount: 1, failures: [] as Array<{ file: File; message: string }> };
    } catch (error) {
      return {
        insertedCount: 0,
        failures: [{ file, message: error instanceof Error ? error.message : 'Upload failed.' }],
      };
    }
  }, [commandManager, diagramId, imageUploadModalConfig.replaceTarget, insertPickerModalImageFiles, nodes, uploadImage]);

  const handleDroppedImageFiles = useCallback(
    async (
      files: File[],
      point: { x: number; y: number },
      notice: { skippedOverSize: number; skippedOverMaxCount: number },
    ) => {
      await insertDroppedImageFiles(files, point, notice);
    },
    [insertDroppedImageFiles],
  );

  const handleDroppedImageUrl = useCallback(
    async (sourceUrl: string, point: { x: number; y: number }) => {
      await insertImageUrlAt(sourceUrl, point, 'drop');
    },
    [insertImageUrlAt],
  );

  const handleDroppedPdfFiles = useCallback(
    async (
      files: File[],
      point: { x: number; y: number },
      notice: { skippedOverMaxCount: number },
    ) => {
      await insertDroppedPdfFiles(files, point, notice);
    },
    [insertDroppedPdfFiles],
  );

  const handleCanvasPaste = useCallback(
    async (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (!canWrite) return;
      if (isEditableDomTarget(e.target)) return;
      const dt = e.clipboardData as unknown as DataTransfer | null;
      const file = extractFirstSupportedImageFileFromDataTransfer(dt);
      if (!file) {
        const hasClipboardFiles = Boolean(
          dt && (
            (dt.files?.length ?? 0) > 0
            || Array.from(dt.items ?? []).some((item) => item.kind === 'file')
          ),
        );
        if (hasClipboardFiles) {
          toast.error(t('mediaUpload.errors.unsupportedMimeType'));
        }
        try {
          const types = dt && typeof dt.types !== 'undefined' ? [...dt.types] : [];
          canvasLogger.debug('Image paste (ClipboardEvent): no supported file', { types });
        } catch {
          /* ignore */
        }
        return;
      }
      e.preventDefault();
      canvasLogger.debug('Image paste (ClipboardEvent): starting upload', {
        name: file.name,
        type: file.type,
        sizeBytes: file.size,
      });
      await insertImageFileAtViewportCenter(file, 'paste');
    },
    [canWrite, insertImageFileAtViewportCenter, t],
  );

  const tryPasteImageFromSystemClipboard = useCallback(
    async (atDiagramPoint?: { x: number; y: number }): Promise<boolean> => {
      if (!canWrite) return false;
      if (typeof navigator === 'undefined' || !navigator.clipboard?.read) {
        canvasLogger.debug('Image paste (clipboard.read): API not available');
        return false;
      }
      try {
        const items = await navigator.clipboard.read();
        canvasLogger.debug('Image paste (clipboard.read): received items', {
          itemCount: items.length,
          typesPerItem: items.map((it) => [...it.types]),
        });
        const file = await pickBestFileFromNavigatorClipboardItems(items);
        if (!file) {
          canvasLogger.debug('Image paste (clipboard.read): no file after pickBest');
          return false;
        }
        canvasLogger.debug('Image paste (clipboard.read): starting upload', {
          name: file.name,
          type: file.type,
          sizeBytes: file.size,
        });
        const pasted = atDiagramPoint
          ? await insertImageFileAt(
              file,
              {
                x: atDiagramPoint.x - defaultDimensions.width / 2,
                y: atDiagramPoint.y - defaultDimensions.height / 2,
              },
              'paste',
            )
          : await insertImageFileAtViewportCenter(file, 'paste');
        return pasted;
      } catch (err) {
        canvasLogger.debug('Image paste (clipboard.read): error', {
          message: err instanceof Error ? err.message : String(err),
        });
        return false;
      }
    },
    [
      canWrite,
      defaultDimensions.height,
      defaultDimensions.width,
      insertImageFileAt,
      insertImageFileAtViewportCenter,
    ],
  );

  const firefoxPasteShortcutShouldPreventDefault = useCallback((): boolean => {
    if (!isFirefox()) return true;
    return hasClipboardContent(Clipboard.get());
  }, []);

  const runGlobalPasteShortcut = useCallback(async () => {
    const now = Date.now();
    if (now - lastShortcutAtRef.current.paste < 250) return;
    lastShortcutAtRef.current.paste = now;
    let data = Clipboard.get();
    try {
      const sys = await Clipboard.readFromSystem();
      if (sys && hasClipboardContent(sys)) {
        data = sys;
      }
    } catch {
      /* ignore */
    }
    if (!hasClipboardContent(data)) {
      if (isFirefox()) {
        return;
      }
      await tryPasteImageFromSystemClipboard();
      return;
    }
    const cmd = new PasteNodesCommand(data, { x: 50, y: 50 });
    commandManager.execute(cmd);
  }, [commandManager, tryPasteImageFromSystemClipboard]);

  useEffect(() => {
    (window as any).__openImageUploadModal = (options?: ImageUploadModalOpenOptions) => {
      setImageUploadModalConfig(options ?? {});
      setImageUploadModalOpen(true);
    };
    return () => {
      try { delete (window as any).__openImageUploadModal; } catch {}
    };
  }, []);

  useEffect(() => {
    const w = window as unknown as {
      __pasteCanvasImageFromClipboard?: (opts?: { diagramX: number; diagramY: number }) => Promise<boolean>;
    };
    w.__pasteCanvasImageFromClipboard = (opts) => {
      if (opts?.diagramX != null && opts?.diagramY != null) {
        return tryPasteImageFromSystemClipboard({ x: opts.diagramX, y: opts.diagramY });
      }
      return tryPasteImageFromSystemClipboard();
    };
    return () => {
      try {
        delete w.__pasteCanvasImageFromClipboard;
      } catch {
        /* ignore */
      }
    };
  }, [tryPasteImageFromSystemClipboard]);

  const { handleDragOver, handleDrop } = useCanvasDropHandlers({
    canWrite,
    stageRef: stageRef as any,
    doc: doc ?? undefined,
    commandManager,
    toolRegistry,
    frameManager,
    onImageFilesDrop: handleDroppedImageFiles,
    onPdfFilesDrop: handleDroppedPdfFiles,
    onImageUrlDrop: handleDroppedImageUrl,
    onUnsupportedImageDrop: () => {
      toast.error(t('mediaUpload.errors.unsupportedMimeType'));
    },
  });

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const el = stage.getContent();
    if (!el) return;
    const captureOpts: AddEventListenerOptions = { capture: true };
    const onDragEnter = (ev: DragEvent) => {
      handleDragOver(ev as unknown as React.DragEvent<HTMLDivElement>);
    };
    const onDragOver = (ev: DragEvent) => {
      handleDragOver(ev as unknown as React.DragEvent<HTMLDivElement>);
    };
    const onDrop = (ev: DragEvent) => {
      handleDrop(ev as unknown as React.DragEvent<HTMLDivElement>);
      ev.stopPropagation();
    };
    el.addEventListener('dragenter', onDragEnter, captureOpts);
    el.addEventListener('dragover', onDragOver, captureOpts);
    el.addEventListener('drop', onDrop, captureOpts);
    return () => {
      el.removeEventListener('dragenter', onDragEnter, captureOpts);
      el.removeEventListener('dragover', onDragOver, captureOpts);
      el.removeEventListener('drop', onDrop, captureOpts);
    };
  }, [handleDragOver, handleDrop, dimensions.width, dimensions.height, stageRef]);

  useCanvasPluginsLoader({
    // Key registration off the RESOLVED type only. effectiveDiagramType falls
    // back to a placeholder ('genogram') while the type resolves, which would
    // register the wrong domain's tools and race the correct plugin.
    diagramType,
    frames,
    toolRegistry,
    commandManager,
    canvasLogger,
    setCanvasPlugins,
    onLoadError: () => {
      if (diagramType === 'org') setOrgProjectionUnavailable(true);
    },
  });

  useCanvasCommentPlacementBridge({ eventBus, toolRegistry });

  const beginCommentPlacementFromPanel = useCallback(async () => {
    const activated = toolRegistry.activateTool('comments');
    if (!activated) {
      return false;
    }

    const commentsTool = toolRegistry.getTool('comments');
    if (!commentsTool) {
      return false;
    }

    const result = await commentsTool.execute();
    if (!result) {
      return false;
    }

    if (Array.isArray(result)) {
      await commandManager.executeBatch(result, commentsTool.manifest.name);
    } else {
      await commandManager.execute(result);
    }

    return true;
  }, [commandManager, toolRegistry]);

  useEffect(() => {
    const onCanvasAction = async (ev: any) => {
      try {
        const action = ev?.detail?.action;
        if (action !== 'begin-comment-placement') return;
        await beginCommentPlacementFromPanel();
      } catch { }
    };
    window.addEventListener('canvas-action', onCanvasAction as any);
    return () => window.removeEventListener('canvas-action', onCanvasAction as any);
  }, [beginCommentPlacementFromPanel]);

  // Ensure toolRegistry context always has the latest helper function
  useEffect(() => {
    if (toolRegistry) {
      toolRegistry.updateContext({
        createDeleteNodeCommand: createDeleteNodeCommandWithComments
      } as Partial<IToolContext>);
    }
  }, [toolRegistry, createDeleteNodeCommandWithComments]);

  const toolsPlugin = useMemo(() => new DefaultCanvasToolsPlugin(frameManager), [frameManager]);
  const {
    colorTool,
    textTool,
    shapeTool,
    deleteTool,
    connectorTool,
    selectionTool,
    movementTool,
    frameTool,
    shapePickerTool,
    stickyNoteTool,
    stickyNotePropertiesTool,
    universalTextTool,
    commentsTool,
    framesTool,
    penTool,
    universalDeleteTool,
    copyTool,
    connectTool,
    shapeSwitchTool,
    groupTool,
    shapeColorTool,
    shapeOutlineTool,
    fontSizeTool,
    fontPickerTool,
    textStyleTool,
    textAlignmentTool,
    textColorTool,
    bpmnEventStyleTool,
    bpmnEventTriggerTool,
    bpmnGatewayTypeTool,
    bpmnActivityStyleTool,
    bpmnActivityMarkerTool,
    bpmnActivityTaskTool,
    personStyleTool,
    relationshipEditTool,
    schemaDrivenPersonTool,
    resetPersonStyleTool,
    cultureTagTool,
    healthTagTool,
    culturePaletteTool,
    addPersonTool,
    addPartnerTool,
    addChildTool,
    addParentTool,
    addSiblingTool,
    addChildToCoupleTool,
    groupAsSiblingsTool,
    createRelationshipTool,
  } = toolsPlugin.tools;

  useEffect(() => {
    const prev = prevSelectedIdsRef.current;
    const next = selectedIds;
    if (prev.size > 0) {
      const removed: string[] = [];
      prev.forEach((id) => {
        if (!next.has(id)) removed.push(id);
      });
      if (removed.length > 0) {
        const { clearSelection } = useTableCellStore.getState();
        removed.forEach((id) => {
          const n = nodes.find((nn) => nn.id === id);
          if (n?.type === 'table') {
            clearSelection(id);
          }
        });
      }
    }
    prevSelectedIdsRef.current = new Set(next);
  }, [selectedIds, nodes]);

  const selectedNodesTransformBoundsKey = useMemo(() => {
    if (selectedIds.size === 0) return '';
    const byId = new Map(nodesForRender.map((nn) => [nn.id, nn]));
    return Array.from(selectedIds)
      .sort()
      .map((id) => {
        const n = byId.get(id) as Record<string, unknown> | undefined;
        if (!n) return `${id}:?`;
        const w = n.width ?? n.w ?? '';
        const h = n.height ?? n.h ?? '';
        const rot = typeof n.rotation === 'number' ? n.rotation : '';
        const sx = typeof n.scaleX === 'number' ? n.scaleX : '';
        const sy = typeof n.scaleY === 'number' ? n.scaleY : '';
        return `${id}:${w}:${h}:${rot}:${sx}:${sy}`;
      })
      .join('|');
  }, [selectedIds, nodesForRender]);

  // Update node transformer when selection changes or selected shape bounds change
  // (e.g. image crop updates height without changing selection). Skip during drag.
  useEffect(() => {
    if (!transformerRef.current || !layerRef.current) return;
    if (isDraggingShape) return; // Don't update transformer during drag

    if (selectedIds.size > 0) {
      // Get the actual Konva nodes from refs
      const nodesToTransform: Konva.Node[] = [];
      selectedIds.forEach(id => {
        const node = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
        const shapeNode = nodesForRender.find((n) => n.id === id) as any;
        const isLocked = !!(shapeNode?.locked || shapeNode?.attrs?.locked);
        const isVisible = !(shapeNode && (shapeNode.visible === false || shapeNode?.attrs?.visible === false));

        // Check if node is transformable from manifest
        let isTransformable = true;
        if (shapeNode?.type) {
          const nodeDiagramType = diagramType || 'genogram';
          const manifest = getShapeManifest(nodeDiagramType as any, shapeNode.type);
          isTransformable = manifest?.capabilities?.transformable !== false;
        }

        if (node && !isLocked && isVisible && isTransformable) {
          const variant = String(shapeNode?.attrs?.variant ?? shapeNode?.attrs?.shapeType ?? '').toLowerCase();
          const useFullContainer = variant === 'bpmn-annotation';
          const inner = !useFullContainer && (node as any)?.findOne
            ? (node as any).findOne('.rotation-group') as Konva.Node
            : undefined;
          nodesToTransform.push((inner || node) as Konva.Node);
        }
      });

      // Attach transformer to selected nodes
      transformerRef.current.nodes(nodesToTransform);
    } else {
      // Clear transformer
      transformerRef.current.nodes([]);
    }

    // Force redraw
    transformerRef.current.getLayer()?.batchDraw();
    try {
      (transformerRef.current as Konva.Transformer & { forceUpdate?: () => void }).forceUpdate?.();
    } catch {
      /* noop */
    }
  }, [selectedIds, isDraggingShape, selectedNodesTransformBoundsKey, diagramType]); // eslint-disable-line react-hooks/exhaustive-deps -- shape props in selectedNodesTransformBoundsKey (nodesForRender); layerRef stable

  // Attach transformer to selected frame
  const selectedFrame = useMemo(() => frames.find(f => f.id === selectedFrameId) || null, [frames, selectedFrameId]);
  useEffect(() => {
    const tr = frameTransformerRef.current;
    if (!tr) return;
    // If shapes are selected, do not allow frame transform selection
    if (selectedIds.size > 0) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const frNode = selectedFrame ? (frameRectRefs.current.get(selectedFrame.id) || null) : null;
    if (frNode) {
      tr.nodes([frNode]);
    } else {
      tr.nodes([]);
    }
    tr.getLayer()?.batchDraw();
  }, [selectedFrame]);


  // Register all tools ONCE on mount
  const toolsRegisteredRef = useRef<boolean>(false);
  const shortcutsRegisteredRef = useRef<boolean>(false);

  useEffect(() => {
    const registerTools = async () => {
      // Guard against duplicate registration in React Strict Mode
      if (toolsRegisteredRef.current) {
        return;
      }

      try {
        await toolsPlugin.registerTools(toolRegistry);

        toolsRegisteredRef.current = true;

        // Tool registration complete
      } catch (error) {
        canvasLogger.error('Error registering tools', error);
      } finally {
        // Always attempt to register global shortcuts, even if some tools failed
        try {
          const keyboardManager = toolRegistry.getKeyboardManager();
          if (!shortcutsRegisteredRef.current) {

            // Undo/Redo shortcuts
            keyboardManager.registerShortcut(
              'global-undo',
              { key: 'z', modifiers: ['ctrl'], description: 'Undo' },
              ShortcutPriority.GLOBAL,
              () => eventBus.emit('command:request:undo'),
              'Global'
            );

            keyboardManager.registerShortcut(
              'global-undo-cmd',
              { key: 'z', modifiers: ['cmd'], description: 'Undo' },
              ShortcutPriority.GLOBAL,
              () => eventBus.emit('command:request:undo'),
              'Global'
            );

            keyboardManager.registerShortcut(
              'global-redo',
              { key: 'z', modifiers: ['ctrl', 'shift'], description: 'Redo' },
              ShortcutPriority.GLOBAL,
              () => eventBus.emit('command:request:redo'),
              'Global'
            );
            keyboardManager.registerShortcut(
              'global-redo-cmd',
              { key: 'z', modifiers: ['cmd', 'shift'], description: 'Redo' },
              ShortcutPriority.GLOBAL,
              () => eventBus.emit('command:request:redo'),
              'Global'
            );
            keyboardManager.registerShortcut(
              'global-redo-y',
              { key: 'y', modifiers: ['ctrl'], description: 'Redo' },
              ShortcutPriority.GLOBAL,
              () => eventBus.emit('command:request:redo'),
              'Global'
            );

            // Diagram linking shortcuts
            // Cmd/Ctrl+Enter: Preview linked diagram (when shape with link is selected)
            keyboardManager.registerShortcut(
              'link-open-ctrl',
              { key: 'Enter', modifiers: ['ctrl'], description: 'Open linked diagram' },
              ShortcutPriority.GLOBAL,
              async () => {
                const { selectedIds, nodes } = useDiagramStore.getState();
                if (selectedIds.size !== 1) return;

                const selectedId = Array.from(selectedIds)[0];
                const node = nodes.find(n => n.id === selectedId) as any;
                if (!node) return;

                const attrs = node.attrs || {};
                const linkedDiagramId = attrs.linkedDiagramId as string | undefined;
                const linkId = attrs.linkId as string | undefined;

                if (linkedDiagramId && linkId) {
                  useLinkPreviewStore.getState().open(linkedDiagramId);
                }
              },
              'Global'
            );
            keyboardManager.registerShortcut(
              'link-open-cmd',
              { key: 'Enter', modifiers: ['cmd'], description: 'Open linked diagram' },
              ShortcutPriority.GLOBAL,
              async () => {
                const { selectedIds, nodes } = useDiagramStore.getState();
                if (selectedIds.size !== 1) return;

                const selectedId = Array.from(selectedIds)[0];
                const node = nodes.find(n => n.id === selectedId) as any;
                if (!node) return;

                const attrs = node.attrs || {};
                const linkedDiagramId = attrs.linkedDiagramId as string | undefined;
                const linkId = attrs.linkId as string | undefined;

                if (linkedDiagramId && linkId) {
                  useLinkPreviewStore.getState().open(linkedDiagramId);
                }
              },
              'Global'
            );

            // Cmd/Ctrl+[: Navigate back in breadcrumb stack
            keyboardManager.registerShortcut(
              'link-navigate-back-ctrl',
              { key: '[', modifiers: ['ctrl'], description: 'Navigate back' },
              ShortcutPriority.GLOBAL,
              () => {
                const { breadcrumbStack, isNavigating } = useNavigationStore.getState();
                if (isNavigating || breadcrumbStack.length === 0) return;

                const previousItem = breadcrumbStack[breadcrumbStack.length - 1];
                if (previousItem) {
                  const nextStack = breadcrumbStack.slice(0, -1);
                  try {
                    sessionStorage.setItem('diagram-nav-stack', JSON.stringify({ targetDiagramId: previousItem.diagramId, breadcrumbStack: nextStack }));
                    sessionStorage.removeItem('diagram-nav-last'); // legacy
                  } catch { }
                  window.location.href = `/f/${previousItem.folderId}/d/${previousItem.diagramId}`;
                }
              },
              'Global'
            );
            keyboardManager.registerShortcut(
              'link-navigate-back-cmd',
              { key: '[', modifiers: ['cmd'], description: 'Navigate back' },
              ShortcutPriority.GLOBAL,
              () => {
                const { breadcrumbStack, isNavigating } = useNavigationStore.getState();
                if (isNavigating || breadcrumbStack.length === 0) return;

                const previousItem = breadcrumbStack[breadcrumbStack.length - 1];
                if (previousItem) {
                  const nextStack = breadcrumbStack.slice(0, -1);
                  try {
                    sessionStorage.setItem('diagram-nav-stack', JSON.stringify({ targetDiagramId: previousItem.diagramId, breadcrumbStack: nextStack }));
                    sessionStorage.removeItem('diagram-nav-last'); // legacy
                  } catch { }
                  window.location.href = `/f/${previousItem.folderId}/d/${previousItem.diagramId}`;
                }
              },
              'Global'
            );

            // Cut (Ctrl/Cmd+X)
            keyboardManager.registerShortcut(
              'edit-cut',
              { key: 'x', modifiers: ['ctrl'], description: 'Cut' },
              ShortcutPriority.GLOBAL,
              async () => {
                const now = Date.now();
                if (now - lastShortcutAtRef.current.cut < 200) return;
                lastShortcutAtRef.current.cut = now;
                const { nodes, edges, selectedIds } = useDiagramStore.getState();
                if (selectedIds.size === 0) return;
                const { nodes: selected, edges: selectedEdges } = buildClipboardSelection({ selectedIds, nodes, edges });
                Clipboard.set(selected, selectedEdges);
                try {
                  await Clipboard.writeToSystem(selected, selectedEdges);
                } catch { }
                // Cut only unlocked nodes
                const cuttable = selected.filter(n => !(n.locked === true || (n as any)?.attrs?.locked === true));
                if (cuttable.length === 0) return;
                const cmd = new CutNodesCommand(cuttable.map(n => n.id));
                commandManager.execute(cmd);
              },
              'Global'
            );
            keyboardManager.registerShortcut(
              'edit-cut-cmd',
              { key: 'x', modifiers: ['cmd'], description: 'Cut' },
              ShortcutPriority.GLOBAL,
              async () => {
                const now = Date.now();
                if (now - lastShortcutAtRef.current.cut < 200) return;
                lastShortcutAtRef.current.cut = now;
                const { nodes, edges, selectedIds } = useDiagramStore.getState();
                if (selectedIds.size === 0) return;
                const { nodes: selected, edges: selectedEdges } = buildClipboardSelection({ selectedIds, nodes, edges });
                Clipboard.set(selected, selectedEdges);
                try {
                  await Clipboard.writeToSystem(selected, selectedEdges);
                } catch { }
                const cuttable = selected.filter(n => !(n.locked === true || (n as any)?.attrs?.locked === true));
                if (cuttable.length === 0) return;
                const cmd = new CutNodesCommand(cuttable.map(n => n.id));
                commandManager.execute(cmd);
              },
              'Global'
            );

            // Copy (Ctrl/Cmd+C)
            keyboardManager.registerShortcut(
              'edit-copy',
              { key: 'c', modifiers: ['ctrl'], description: 'Copy' },
              ShortcutPriority.GLOBAL,
              async () => {
                const now = Date.now();
                if (now - lastShortcutAtRef.current.copy < 200) return;
                lastShortcutAtRef.current.copy = now;
                const { nodes, edges, selectedIds } = useDiagramStore.getState();
                if (selectedIds.size === 0) return;
                const { nodes: selected, edges: selectedEdges } = buildClipboardSelection({ selectedIds, nodes, edges });
                Clipboard.set(selected, selectedEdges);
                await Clipboard.writeToSystem(selected, selectedEdges);
              },
              'Global'
            );
            keyboardManager.registerShortcut(
              'edit-copy-cmd',
              { key: 'c', modifiers: ['cmd'], description: 'Copy' },
              ShortcutPriority.GLOBAL,
              async () => {
                const now = Date.now();
                if (now - lastShortcutAtRef.current.copy < 200) return;
                lastShortcutAtRef.current.copy = now;
                const { nodes, edges, selectedIds } = useDiagramStore.getState();
                if (selectedIds.size === 0) return;
                const { nodes: selected, edges: selectedEdges } = buildClipboardSelection({ selectedIds, nodes, edges });
                Clipboard.set(selected, selectedEdges);
                await Clipboard.writeToSystem(selected, selectedEdges);
              },
              'Global'
            );

            keyboardManager.registerShortcut(
              'edit-paste',
              { key: 'v', modifiers: ['ctrl'], description: 'Paste' },
              ShortcutPriority.GLOBAL,
              runGlobalPasteShortcut,
              'Global',
              true,
              { shouldPreventDefault: firefoxPasteShortcutShouldPreventDefault }
            );
            keyboardManager.registerShortcut(
              'edit-paste-cmd',
              { key: 'v', modifiers: ['cmd'], description: 'Paste' },
              ShortcutPriority.GLOBAL,
              runGlobalPasteShortcut,
              'Global',
              true,
              { shouldPreventDefault: firefoxPasteShortcutShouldPreventDefault }
            );

            // Escape to cancel current tool (return to selection)
            keyboardManager.registerShortcut(
              'global-escape-cancel',
              { key: 'Escape', modifiers: [], description: 'Cancel tool / return to selection' },
              ShortcutPriority.GLOBAL,
              () => {
                try {
                  toolRegistry.activateTool('genogram-selection');
                } catch { }
              },
              'Global'
            );

            // Note: Delete/Backspace shortcuts are now handled by the GenogramDeleteTool
            // via the ToolRegistry keyboard shortcut system

            // Select All (Ctrl/Cmd+A) — only selects visible (interactive) nodes/edges
            const selectAllHandler = () => {
              const visibleNodes = interactiveNodesRef.current;
              const visibleEdges = interactiveEdgesRef.current;
              const allIds = new Set([
                ...visibleNodes.map(n => n.id),
                ...visibleEdges.map(e => e.id),
              ]);
              setSelected(allIds);
            };

            keyboardManager.registerShortcut(
              'edit-select-all',
              { key: 'a', modifiers: ['ctrl'], description: 'Select all shapes and connectors' },
              ShortcutPriority.GLOBAL,
              selectAllHandler,
              'Global'
            );
            keyboardManager.registerShortcut(
              'edit-select-all-cmd',
              { key: 'a', modifiers: ['cmd'], description: 'Select all shapes and connectors' },
              ShortcutPriority.GLOBAL,
              selectAllHandler,
              'Global'
            );
            shortcutsRegisteredRef.current = true;
          }
        } catch (e) {
          try { canvasLogger.warn('Global shortcuts registration failed', e as any); } catch { }
        }
      }
    };

    registerTools();

    return () => {
      // Unregister all tools on unmount
      TOOL_IDS_TO_UNREGISTER.forEach((id) => {
        if (TOOL_IDS_TO_PRESERVE_ON_UNMOUNT.has(id)) return;
        try {
          toolRegistry.unregister(id);
        } catch (_e) {
          // Ignore unregister errors on unmount
        }
      });
      toolsRegisteredRef.current = false;

      // Unregister shortcuts if we had registered them
      try {
        const keyboardManager = toolRegistry.getKeyboardManager();
        SHORTCUT_IDS_TO_UNREGISTER.forEach((id) => {
          try { keyboardManager.unregisterShortcut(id); } catch { }
        });
        shortcutsRegisteredRef.current = false;
      } catch { }
      // Tools unregistered
    };
  }, [
    colorTool,
    textTool,
    shapeTool,
    deleteTool,
    connectorTool,
    selectionTool,
    movementTool,
    frameTool,
    shapePickerTool,
    stickyNoteTool,
    stickyNotePropertiesTool,
    universalTextTool,
    commentsTool,
    framesTool,
    penTool,
    universalDeleteTool,
    copyTool,
    connectTool,
    groupTool,
    shapeSwitchTool,
    shapeColorTool,
    shapeOutlineTool,
    fontSizeTool,
    fontPickerTool,
    textStyleTool,
    textAlignmentTool,
    textColorTool,
    bpmnEventStyleTool,
    bpmnEventTriggerTool,
    bpmnGatewayTypeTool,
    bpmnActivityStyleTool,
    bpmnActivityMarkerTool,
    bpmnActivityTaskTool,
    personStyleTool,
    relationshipEditTool,
    schemaDrivenPersonTool,
    resetPersonStyleTool,
    cultureTagTool,
    healthTagTool,
    culturePaletteTool,
    addPersonTool,
    addPartnerTool,
    addChildTool,
    runGlobalPasteShortcut,
    firefoxPasteShortcutShouldPreventDefault,
    addParentTool,
    addSiblingTool,
    addChildToCoupleTool,
    groupAsSiblingsTool,
    createRelationshipTool,
    toolRegistry
  ]);

  // Subscribe to pen preview events
  useEffect(() => {
    const onStart = (payload: any) => {
      setPenPreview({
        points: Array.isArray(payload?.points) ? payload.points : [],
        color: payload?.color || '#000',
        width: typeof payload?.width === 'number' ? payload.width : 2,
        opacity: typeof payload?.opacity === 'number' ? payload.opacity : 1,
        visible: true
      });
    };
    const onUpdate = (payload: any) => {
      setPenPreview(prev => ({ ...prev, points: Array.isArray(payload?.points) ? payload.points : prev.points }));
    };
    const onEnd = () => {
      setPenPreview(prev => ({ ...prev, visible: false, points: [] }));
    };
    eventBus.on('pen:preview:start', onStart);
    eventBus.on('pen:preview:update', onUpdate);
    eventBus.on('pen:preview:end', onEnd);
    return () => {
      eventBus.off('pen:preview:start', onStart);
      eventBus.off('pen:preview:update', onUpdate);
      eventBus.off('pen:preview:end', onEnd);
    };
  }, [eventBus]);

  // Subscribe to shape placement preview events
  useEffect(() => {
    const onStart = (p: any) => setPlacePreview({ x: p?.x ?? 0, y: p?.y ?? 0, width: p?.width, height: p?.height, visible: true, shapeId: p?.shapeId, category: p?.category, fillColor: p?.fillColor, strokeColor: p?.strokeColor, fromPoint: p?.fromPoint, toPoint: p?.toPoint });
    const onUpdate = (p: any) => setPlacePreview(prev => ({ ...prev, x: p?.x ?? prev.x, y: p?.y ?? prev.y, width: p?.width ?? prev.width, height: p?.height ?? prev.height, fillColor: p?.fillColor ?? prev.fillColor, strokeColor: p?.strokeColor ?? prev.strokeColor, fromPoint: p?.fromPoint ?? prev.fromPoint, toPoint: p?.toPoint ?? prev.toPoint }));
    const onEnd = () => setPlacePreview(prev => ({ ...prev, visible: false }));
    eventBus.on('place:preview:start', onStart);
    eventBus.on('place:preview:update', onUpdate);
    eventBus.on('place:preview:end', onEnd);
    return () => {
      eventBus.off('place:preview:start', onStart);
      eventBus.off('place:preview:update', onUpdate);
      eventBus.off('place:preview:end', onEnd);
    };
  }, [eventBus]);



  // Persist frames to Yjs (if doc provided) and sync from Yjs
  useEffect(() => {
    if (!doc) return;
    const map = doc.getMap<any>('frames');
    framesMapRef.current = map;

    // Initial local → Yjs sync for frames already present
    const existingFrames = frameManager.getAllFrames();
    if (existingFrames.length > 0) {
      isSyncingFramesToYjs.current = true;
      existingFrames.forEach(f => {
        if (!map.get(f.id)) {
          map.set(f.id, f.toJSON());
        }
      });
      isSyncingFramesToYjs.current = false;
    }

    const stop = observeYjsFrames(map, frameManager, {
      toYjs: isSyncingFramesToYjs,
      fromYjs: isSyncingFramesFromYjs,
    }, setFrames);
    return () => {
      stop();
      framesMapRef.current = null;
    };
  }, [doc, frameManager]);

  // Update tool data when nodes/edges change
  useEffect(() => {
    colorTool.setNodes(interactiveNodes);
    textTool.setNodesAndUpdater(interactiveNodes, updateNode); // TODO: Fix to use proper commands
    movementTool.setNodesAndUpdater(interactiveNodes, updateNode); // TODO: Fix to use proper commands
    shapeTool.setNodeHandlers(addNode, deleteNode); // TODO: Fix to use proper commands
    deleteTool.setData(interactiveNodes, interactiveEdges, addNode, deleteNode, addEdge, deleteEdge); // TODO: Fix to use proper commands
    // provide frame manager so frame deletion cascades via command returned by tool
    deleteTool.setFrameAndCommandManagers(frameManager as any);
    connectorTool.setEdgeHandlers(interactiveEdges, addEdge, deleteEdge);
    connectorTool.setConnectionState = (isConn: boolean, firstNode: string | null) => {
      setIsConnecting(isConn);
      setFirstConnectNode(firstNode);
    };
    selectionTool.setData(interactiveNodes, interactiveEdges, selectedIds, setSelected);
    // Force re-render to update toolbar when selection changes
    forceUpdate({});
  }, [colorTool, textTool, shapeTool, deleteTool, connectorTool, selectionTool,
    interactiveNodes, interactiveEdges, selectedIds, updateNode, addNode, deleteNode, addEdge, deleteEdge, setSelected]);

  // Update tool context
  useEffect(() => {
    const effectiveCanWrite = testCanWriteOverrideRef.current ?? toolContext.canWrite;
    toolRegistry.updateContext({
      ...toolContext,
      canWrite: effectiveCanWrite,
    });
    // Also explicitly set diagram type on connector tool since it may not be the active tool
    if ((connectorTool as any).setDiagramType) {
      (connectorTool as any).setDiagramType(effectiveDiagramType);
    }
  }, [toolRegistry, toolContext, effectiveDiagramType, connectorTool]);

  // The ordinary planning canvas leaves the connector tool armed for matrix
  // reporting. Entering Scenario → Manual/AI is an explicit switch to primary
  // reporting authoring, so reset only that inherited default. A relationship
  // explicitly picked afterwards is still preserved by the canvas plugin.
  useEffect(() => {
    if (!isExplicitScenarioAuthoring || connectorTool.connectionType !== 'matrix_reports') return;
    connectorTool.connectionType = 'reports_to';
  }, [connectorTool, isExplicitScenarioAuthoring]);

  // Awareness → preview targets registry
  useEffect(() => {
    if (!awareness) return;
    const onChange = () => {
      // Recompute latest targets per shape from all remote clients
      const latest = new Map<string, { x: number; y: number; timestamp: number }>();
      const entries = Array.from(awareness.getStates().entries()) as any[];
      for (const [clientId, state] of entries) {
        if (clientId === awareness.clientID) continue;
        const previews = state?.previews as Record<string, any> | undefined;
        if (!previews) continue;
        for (const [shapeId, p] of Object.entries(previews)) {
          const ts = (p as any)?.timestamp ?? 0;
          const props = (p as any)?.props || {};
          const x = typeof props.x === 'number' ? props.x : undefined;
          const y = typeof props.y === 'number' ? props.y : undefined;
          if (typeof x !== 'number' || typeof y !== 'number') continue;
          const prev = latest.get(shapeId);
          if (!prev || ts > prev.timestamp) {
            latest.set(shapeId, { x, y, timestamp: ts });
          }
        }
      }
      previewTargetsRef.current = latest;
      // Start RAF if we have targets
      if (latest.size > 0 && !rafRef.current) {
        const step = () => {
          const layer = layerRef.current;
          if (!layer) {
            rafRef.current = requestAnimationFrame(step);
            return;
          }
          const smoothing = 0.3;
          // Ease nodes towards targets
          previewTargetsRef.current.forEach((target, shapeId) => {
            const node = nodeRefs.current.get(shapeId);
            if (!node) return;
            const cur = smoothedRef.current.get(shapeId) || { x: node.x(), y: node.y() };
            const nx = cur.x + (target.x - cur.x) * smoothing;
            const ny = cur.y + (target.y - cur.y) * smoothing;
            smoothedRef.current.set(shapeId, { x: nx, y: ny });
            node.position({ x: nx, y: ny });
          });
          layer.batchDraw();
          // force overlay re-render to keep overlays in sync with smoothed positions
          setOverlayVersion(v => v + 1);
          // Stop loop if no targets
          if (previewTargetsRef.current.size === 0) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            return;
          }
          rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
      }
      // If no targets, clear smoothed map and stop RAF
      if (latest.size === 0) {
        smoothedRef.current.clear();
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      }
    };
    awareness.on('change', onChange);
    return () => {
      awareness.off('change', onChange);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      previewTargetsRef.current.clear();
      smoothedRef.current.clear();
    };
  }, [awareness, layerRef]);

  // Live overlay shapes (positions/size) based on current Konva node refs
  // Uses interactiveNodes (visible after collapse) for performance with large org charts.
  const overlayShapes = useMemo(() => {
    const map = new Map<string, { x: number; y: number; width: number; height: number }>();
    interactiveNodes.forEach(n => {
      const kn = nodeRefs.current.get(n.id) as any;
      let x = n.x;
      let y = n.y;
      let w = (n.w as number) || (n.width as number) || 60;
      let h = (n.h as number) || (n.height as number) || 60;
      try {
        if (kn) {
          x = typeof kn.x === 'function' ? kn.x() : x;
          y = typeof kn.y === 'function' ? kn.y() : y;
          const attrs = kn.getAttrs?.() || {};
          w = (attrs.width as number) ?? w;
          h = (attrs.height as number) ?? h;
        }
      } catch { }
      map.set(n.id, { x, y, width: w, height: h });
    });
    return map;
  }, [interactiveNodes, overlayVersion]);

  const resetConnectorAnchors = useCallback(() => {
    const anchorTool = connectorTool as {
      setStartAnchor?: (anchor: 'top' | 'right' | 'bottom' | 'left' | null) => void;
      setEndAnchor?: (anchor: 'top' | 'right' | 'bottom' | 'left' | null) => void;
    };
    anchorTool.setStartAnchor?.(null);
    anchorTool.setEndAnchor?.(null);
  }, [connectorTool]);

  const getCurrentCanvasPointer = useCallback(() => {
    const stage = stageRef.current;
    const pos = stage?.getPointerPosition();
    if (!stage || !pos) {
      return null;
    }
    return {
      x: (pos.x - stage.x()) / stage.scaleX(),
      y: (pos.y - stage.y()) / stage.scaleY(),
    };
  }, []);

  const beginConnectingFromNode = useCallback((sourceNodeId: string, pointer?: { x: number; y: number }) => {
    const storeNode = nodes.find(n => n.id === sourceNodeId);
    const geometryNode = resolveConnectionGeometryNode({
      nodeId: sourceNodeId,
      visibleNodes: interactiveNodes,
      storeNodes: nodes,
      measuredBounds: getCanvasBoundsForNodeId(sourceNodeId),
    });
    const sourceNode = storeNode ?? geometryNode;

    if (!sourceNode || !geometryNode) {
      return false;
    }

    const context = {
      toolRegistry,
      diagramType: effectiveDiagramType,
      commandManager,
      doc,
      isPlanningChart,
      isExplicitScenarioAuthoring,
    };
    for (const plugin of canvasPluginsRef.current) {
      const nextType = plugin.defaultConnectorType?.({
        sourceNode,
        currentType: String(connectorTool.connectionType ?? ''),
        context,
      });
      if (nextType) {
        connectorTool.connectionType = nextType;
        break;
      }
    }
    if (sourceNode.type === 'image') {
      connectorTool.connectionType = 'line';
    }

    setIsConnecting(true);
    setFirstConnectNode(sourceNodeId);
    autoStartAnchorRef.current = true;
    previewStartAnchorRef.current = null;
    previewEndAnchorRef.current = null;
    setHoveredMarriageBar(null);
    setHoveredTargetId(null);
    setHoveredTargetAnchor(null);
    setHoveredTargetPoint(null);
    setHoveredTargetAnchors([]);
    resetConnectorAnchors();

    const closestSourceAnchor = pointer ? getClosestNodeAnchor(pointer, geometryNode) : null;
    if (closestSourceAnchor) {
      previewStartAnchorRef.current = closestSourceAnchor.side;
      if ((connectorTool as any).setStartAnchor) {
        (connectorTool as any).setStartAnchor(closestSourceAnchor.side);
      }
    }

    const nodeWidth = geometryNode.w || geometryNode.width || 60;
    const nodeHeight = geometryNode.h || geometryNode.height || 60;
    const startX = closestSourceAnchor?.pt.x ?? geometryNode.x + nodeWidth / 2;
    const startY = closestSourceAnchor?.pt.y ?? geometryNode.y + nodeHeight / 2;

    setConnectionPreview({
      startX,
      startY,
      endX: pointer?.x ?? startX,
      endY: pointer?.y ?? startY,
      visible: true
    });

    document.body.style.cursor = 'crosshair';
    return true;
  }, [
    commandManager,
    connectorTool,
    doc,
    effectiveDiagramType,
    getCanvasBoundsForNodeId,
    interactiveNodes,
    isExplicitScenarioAuthoring,
    isPlanningChart,
    nodes,
    resetConnectorAnchors,
    toolRegistry,
  ]);

  const updateConnectionPreviewFromPointer = useCallback((sourceNodeId: string | null) => {
    const transformedPos = getCurrentCanvasPointer();
    if (!transformedPos) {
      return false;
    }

    if (sourceNodeId && autoStartAnchorRef.current) {
      const sourceNode = resolveConnectionGeometryNode({
        nodeId: sourceNodeId,
        visibleNodes: interactiveNodes,
        storeNodes: nodes,
        measuredBounds: getCanvasBoundsForNodeId(sourceNodeId),
      });
      const closestSourceAnchor = sourceNode
        ? getClosestNodeAnchor(transformedPos, sourceNode)
        : null;
      if (closestSourceAnchor) {
        previewStartAnchorRef.current = closestSourceAnchor.side;
        if ((connectorTool as any).setStartAnchor) {
          (connectorTool as any).setStartAnchor(closestSourceAnchor.side);
        }
        setConnectionPreview((prev) => ({
          ...prev,
          startX: closestSourceAnchor.pt.x,
          startY: closestSourceAnchor.pt.y,
        }));
      }
    }

    const connectionSnapCandidates = getSnapCandidatesForSourceNode(sourceNodeId);
    const { best, candidatesById } = computeConnectionSnap(
      transformedPos,
      connectionSnapCandidates,
      sourceNodeId,
    );

    if (best) {
      setHoveredMarriageBar(null);
      setHoveredTargetId(best.id);
      setHoveredTargetAnchor(best.side);
      setHoveredTargetPoint(best.pt);
      previewEndAnchorRef.current = best.side;
      const targetAnchors = candidatesById.get(best.id) || [];
      setHoveredTargetAnchors(targetAnchors.map((anchor) => ({ id: best.id, side: anchor.side, pt: anchor.pt })));
      if ((connectorTool as any).setEndAnchor) {
        (connectorTool as any).setEndAnchor(best.side);
      }
      if ((connectorTool as any).setEndOffset) {
        const target = resolveConnectionGeometryNode({
          nodeId: best.id,
          visibleNodes: interactiveNodes,
          storeNodes: nodes,
          measuredBounds: getCanvasBoundsForNodeId(best.id),
        }) as any;
        if (target) {
          const symbolRect = getGenogramPersonSymbolRect(target);
          const originX = symbolRect?.x ?? (((target.rectX ?? target.x) as number) || 0);
          const originY = symbolRect?.y ?? (((target.rectY ?? target.y) as number) || 0);
          const width = symbolRect?.w ?? (((target.rectW ?? target.width ?? target.w ?? 60) as number) || 60);
          const height = symbolRect?.h ?? (((target.rectH ?? target.height ?? target.h ?? 60) as number) || 60);
          const offset = best.side === 'top' || best.side === 'bottom'
            ? (best.pt.x - originX) / width
            : (best.pt.y - originY) / height;
          (connectorTool as any).setEndOffset(Math.max(0, Math.min(1, offset)));
        }
      }
      setConnectionPreview((prev) => ({ ...prev, endX: best.pt.x, endY: best.pt.y, visible: true }));
    } else {
      setHoveredMarriageBar(null);
      setHoveredTargetId(null);
      setHoveredTargetAnchor(null);
      setHoveredTargetPoint(null);
      setHoveredTargetAnchors([]);
      previewEndAnchorRef.current = null;
      if ((connectorTool as any).setEndAnchor) {
        (connectorTool as any).setEndAnchor(null);
      }
      setConnectionPreview((prev) => ({
        ...prev,
        endX: transformedPos.x,
        endY: transformedPos.y,
        visible: true,
      }));
    }

    return true;
  }, [
    connectorTool,
    getCanvasBoundsForNodeId,
    getCurrentCanvasPointer,
    getSnapCandidatesForSourceNode,
    interactiveNodes,
    nodes,
  ]);

  // Listen for frame events
  useEffect(() => {
    const handleFrameAdded = (frame: IFrame) => {
      // Auto-parent new frame if placed inside another frame
      if (!frame?.bounds) {
        console.warn('Frame added without bounds:', frame?.id);
        return;
      }
      const center = { x: frame.bounds.x + frame.bounds.width / 2, y: frame.bounds.y + frame.bounds.height / 2 };
      const candidates = frameManager.getFramesAtPoint(center, true).filter(f => f.id !== frame.id);
      const parent = candidates.length > 0 ? candidates[0] : null;
      if (!isSyncingFramesFromYjs.current && parent && !frame.parentFrame) {
        frame.setParentFrame(parent.id);
        parent.addChildFrame(frame.id);
      }
      // Persist immediately (frame and parent if present)
      if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
        const rec = frame.toJSON();
        isSyncingFramesToYjs.current = true;
        framesMapRef.current.set(frame.id, rec);
        if (parent) {
          framesMapRef.current.set(parent.id, parent.toJSON());
        }
        isSyncingFramesToYjs.current = false;
      }
      setHoveredDropFrameId(null);
      setFrames(snapshotFrames(frameManager));
    };

    const handleFrameRemoved = (frame: IFrame) => {
      setFrames(snapshotFrames(frameManager));
      if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
        isSyncingFramesToYjs.current = true;
        framesMapRef.current.delete(frame.id);
        // Persist parent changes so child link is removed
        if (frame.parentFrame) {
          const parent = frameManager.getFrame(frame.parentFrame);
          if (parent) {
            framesMapRef.current.set(parent.id, parent.toJSON());
          }
        }
        isSyncingFramesToYjs.current = false;
      }
    };

    const handleFramePreview = (bounds: any) => {
      setFramePreviewBounds(bounds);
      if (bounds && typeof bounds.x === 'number' && typeof bounds.y === 'number') {
        const center = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
        const parent = frameManager.getFrameAtPoint(center, true);
        setHoveredDropFrameId(parent?.id || null);
      } else {
        setHoveredDropFrameId(null);
      }
    };

    const unsubscribeAdded = eventBus.on('frame:added', handleFrameAdded);
    const unsubscribeRemoved = eventBus.on('frame:removed', handleFrameRemoved);
    const unsubscribePreview = eventBus.on('frame:preview', handleFramePreview);
    const unsubscribeMoved = eventBus.on('frame:moved', ({ frame, deltaX, deltaY }: any) => {
      const updates: Array<{ id: string; updates: { x: number; y: number } }> = [];
      frame.shapes.forEach((id: string) => {
        const n = nodes.find(nd => nd.id === id);
        if (!n) return;
        updates.push({ id, updates: { x: (n.x || 0) + deltaX, y: (n.y || 0) + deltaY } });
      });
      if (updates.length > 0) {
        updateNodes(updates as any);
      }
      // Persist new frame bounds to Yjs
      if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
        const rec = frame.toJSON();
        isSyncingFramesToYjs.current = true;
        framesMapRef.current.set(frame.id, rec);
        isSyncingFramesToYjs.current = false;
      }
      // Force re-render
      setFrames(snapshotFrames(frameManager));
    });
    const unsubscribeResized = eventBus.on('frame:resized', ({ frame }: { frame: IFrame }) => {
      setFrames(snapshotFrames(frameManager));
      if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
        isSyncingFramesToYjs.current = true;
        framesMapRef.current.set(frame.id, frame.toJSON());
        isSyncingFramesToYjs.current = false;
      }
    });
    const unsubscribeActivated = eventBus.on('frame:activated', ({ current }: any) => {
      toolRegistry.updateContext({
        frame: current || null,
        diagramType: current?.type || effectiveDiagramType
      } as any);
    });

    // Handle connect tool events
    const handleArmConnecting = () => {
      setIsConnecting(true);
      setFirstConnectNode(null);
      autoStartAnchorRef.current = true;
      previewStartAnchorRef.current = null;
      previewEndAnchorRef.current = null;
      connectorDragSourceRef.current = null;
      setConnectionPreview(prev => ({ ...prev, visible: false }));
      setHoveredMarriageBar(null);
      setHoveredTargetId(null);
      setHoveredTargetAnchor(null);
      setHoveredTargetPoint(null);
      setHoveredTargetAnchors([]);
      resetConnectorAnchors();
      document.body.style.cursor = 'crosshair';
    };

    const handleStartConnecting = ({ sourceNodeId }: { sourceNodeId: string }) => {
      if (!beginConnectingFromNode(sourceNodeId)) {
        handleArmConnecting();
      }
    };

    const handleCancelConnecting = () => {
      cancelConnectingMode();
    };

    const unsubscribeStartConnecting = eventBus.on('tool:start-connecting', handleStartConnecting);
    const unsubscribeArmConnecting = eventBus.on('tool:arm-connecting', handleArmConnecting);
    const unsubscribeCancelConnecting = eventBus.on('tool:cancel-connecting', handleCancelConnecting);

    return () => {
      unsubscribeAdded();
      unsubscribeRemoved();
      unsubscribePreview();
      unsubscribeStartConnecting();
      unsubscribeArmConnecting();
      unsubscribeCancelConnecting();
      unsubscribeMoved();
      unsubscribeActivated();
      unsubscribeResized();
    };
  }, [beginConnectingFromNode, cancelConnectingMode, effectiveDiagramType, eventBus, nodes, resetConnectorAnchors, toolRegistry, updateNodes]);

  useEffect(() => {
    eventBus.emit('tool:connecting-state', { active: isConnecting });
  }, [eventBus, isConnecting]);

  // Handle selection changes to close popups
  useEffect(() => {
    // If selection changed (different size or different items), close popups
    if (prevSelectedIdsRef.current.size !== selectedIds.size ||
      !Array.from(selectedIds).every(id => prevSelectedIdsRef.current.has(id))) {
      closePopupsOnDeselect();
    }

    prevSelectedIdsRef.current = new Set(selectedIds);
  }, [selectedIds, closePopupsOnDeselect]);

  // Shape picker handlers
  const handleQuickCreate = (side: 'top' | 'right' | 'bottom' | 'left', screenX: number, screenY: number) => {
    const selectedId = Array.from(selectedIds)[0];
    if (!selectedId) return;
    const selectedNode = nodes.find(n => n.id === selectedId);
    if (!selectedNode) return;

    // Support person nodes (genogram) and basic/flowchart/orgchart shapes
    const supportedTypes = ['person', 'basic-shape', 'flowchart-shape', 'bpmn-shape', 'org-shape'];
    const isPluginOwnedType = canvasPluginsRef.current.some((plugin) => plugin.nodeTypes?.has(selectedNode.type));
    if (!supportedTypes.includes(selectedNode.type) && !isPluginOwnedType) return;

    const gap = 16;
    const offsetBySide: Record<'top' | 'right' | 'bottom' | 'left', { x: number; y: number }> = {
      top: { x: 0, y: -gap },
      right: { x: gap, y: 0 },
      bottom: { x: 0, y: gap },
      left: { x: -gap, y: 0 }
    };
    const offset = offsetBySide[side];
    setShapePickerState({
      visible: true,
      position: { x: screenX + offset.x, y: screenY + offset.y },
      sourceNodeId: selectedId,
      sourceSide: side,
      sourceRect: null
    });
  };

  // getOppositeAnchor no longer needed after manifest-driven edge updates

  // removed unused handleShapePickerExecute

  const handleShapePickerClose = () => {
    setShapePickerState({
      visible: false,
      position: { x: 0, y: 0 },
      sourceNodeId: null,
      sourceSide: null,
      sourceRect: null
    });
  };

  // Selection verbs (e.g. Atlas "Add direct report") open the same
  // quick-create panel the node plus-handles use, anchored to the selected
  // node — search/variants/placement stay in one implementation.
  useEffect(() => {
    const onOpenQuickCreate = (payload?: unknown) => {
      const side = (((payload as { side?: string })?.side ?? 'bottom')) as 'top' | 'right' | 'bottom' | 'left';
      const selectedId = Array.from(selectedIds)[0];
      const node = selectedId ? nodes.find((n) => n.id === selectedId) : undefined;
      if (!node) return;
      const scale = viewportStage.scale || 1;
      const nodeAny = node as { width?: number; height?: number; w?: number; h?: number };
      const width = nodeAny.width ?? nodeAny.w ?? 270;
      const height = nodeAny.height ?? nodeAny.h ?? 80;
      const screenX = (node.x + width / 2) * scale + (viewportStage.offsetX || 0);
      const screenY = (node.y + (side === 'top' ? 0 : height)) * scale + (viewportStage.offsetY || 0);
      handleQuickCreate(side, screenX, screenY);
    };
    const off = eventBus.on('canvas:open-quick-create', onOpenQuickCreate);
    return () => { off?.(); };
  });
  useCanvasAutoConnectorPathingEngine({ eventBus, commandManager });

  useCanvasMovementToolToggle({ selectedIds, toolRegistry });

  useCanvasFrameToolActivation({ eventBus, toolRegistry, stageRef, layerRef, canvasLogger });

  useCanvasStageResize({ stageRef, setDimensions });

  useCanvasKeyboardShortcuts({
    stageRef,
    setStage,
    spacePressedRef,
    setPanMode,
    isPanning,
    setIsPanning,
    isConnecting,
    cancelConnectingMode,
    setShapePickerState,
  });

  useCanvasTestHarness({
    stageRef,
    selectedIds,
    testBridgeRef,
    testCanWriteOverrideRef,
    toolRegistry,
    commandManager,
    setSelected,
    setShapePickerState,
    createDeleteNodeCommandWithComments,
    commitViewportStage: commitViewportStageImmediate,
  });

  useCanvasUrlShapeFocus({
    nodes,
    stageRef,
    hasFocusedFromUrlRef,
    setStage,
    setSelected,
    setUserSelection,
  });

  // Update testing bridge when selection changes
  useEffect(() => {
    if (testBridgeRef.current) {
      testBridgeRef.current.updateSelectedIds(selectedIds);
    }
  }, [selectedIds]);

  // Bridge EdgeConnector endpoint dragging to overlay hover (reuse glue points)
  useEffect(() => {
    const onStart = () => setIsEndpointDragging(true);
    const onUpdate = (ev: any) => {
      const detail = ev?.detail || {};
      const pt = { x: Number(detail.x) || 0, y: Number(detail.y) || 0 };
      const endpointSnapCandidates = getSnapCandidatesForSourceNode(detail.sourceId || null);
      const { best, candidatesById } = computeConnectionSnap(pt, endpointSnapCandidates, detail.sourceId || null);
      if (best) {
        setHoveredTargetId(best.id);
        setHoveredTargetAnchor(best.side);
        setHoveredTargetPoint(best.pt);
        const all = candidatesById.get(best.id) || [];
        setHoveredTargetAnchors(all.map((a: any) => ({ id: best.id, side: a.side, pt: a.pt })));
      } else {
        setHoveredTargetId(null);
        setHoveredTargetAnchor(null);
        setHoveredTargetPoint(null);
        setHoveredTargetAnchors([]);
      }
    };
    const onEnd = () => {
      setIsEndpointDragging(false);
      setHoveredTargetId(null);
      setHoveredTargetAnchor(null);
      setHoveredTargetPoint(null);
      setHoveredTargetAnchors([]);
    };
    window.addEventListener('edge:endpoint:drag:start', onStart as any);
    window.addEventListener('edge:endpoint:drag:update', onUpdate as any);
    window.addEventListener('edge:endpoint:drag:end', onEnd as any);
    return () => {
      window.removeEventListener('edge:endpoint:drag:start', onStart as any);
      window.removeEventListener('edge:endpoint:drag:update', onUpdate as any);
      window.removeEventListener('edge:endpoint:drag:end', onEnd as any);
    };
  }, [getSnapCandidatesForSourceNode]);

  // Bridge EdgeConnector body dragging (multi-select) to the node+edge multi-drag flow
  useEffect(() => {
    const onSelDragStart = () => {
      if (!canWrite) return;
      selectionDragActiveRef.current = true;
      selectionDragDeltaRef.current = { x: 0, y: 0 };
      selectionDragStartNodePosRef.current.clear();
      selectionDragNodeIdsRef.current = new Set();
      selectionDragFreeLineIdsRef.current = new Set();
      selectionDragFreeLineStartPointsRef.current.clear();
      // model-driven preview; no visual translation refs needed

      setIsDraggingShape(true);
      // Detach transformer during drag to prevent flickering
      try {
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          transformerRef.current.getLayer()?.batchDraw();
        }
      } catch { }

      const store = useDiagramStore.getState();
      const currentSelected: Set<string> = new Set(store.selectedIds);

      // Record node start positions (only for nodes that exist and are not locked)
      currentSelected.forEach((id) => {
        const nodeData = nodes.find((n) => n.id === id) as any;
        if (!nodeData) return;
        const isLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
        if (isLocked && !isProjectionOwnedNode(nodeData)) return;
        selectionDragNodeIdsRef.current.add(id);
        const kn = nodeRefs.current.get(id) as any;
        let sx: number | undefined;
        let sy: number | undefined;
        try {
          if (kn && typeof kn.x === 'function') sx = kn.x();
          if (kn && typeof kn.y === 'function') sy = kn.y();
        } catch { }
        if (!Number.isFinite(sx as number) || !Number.isFinite(sy as number)) {
          sx = nodeData.x;
          sy = nodeData.y;
        }
        selectionDragStartNodePosRef.current.set(id, { x: sx as number, y: sy as number });
      });

      // Track free lines in selection
      currentSelected.forEach((id) => {
        const edge = edges.find((e) => e.id === id);
        if (!edge || !isFreeEndpointConnector(edge)) return;
        const fromPoint = (edge.attrs as any)?.fromPoint as { x: number; y: number } | undefined;
        const toPoint = (edge.attrs as any)?.toPoint as { x: number; y: number } | undefined;
        if (!fromPoint || !toPoint) return;
        selectionDragFreeLineIdsRef.current.add(edge.id);
        selectionDragFreeLineStartPointsRef.current.set(edge.id, {
          fromPoint: { x: fromPoint.x, y: fromPoint.y },
          toPoint: { x: toPoint.x, y: toPoint.y },
        });
        // model-driven preview; no visual translation refs needed
      });

      // Mark dragging state for selected nodes so Yjs updates don't overwrite local preview
      selectionDragNodeIdsRef.current.forEach((id) => store.setDragging(id, true));
      // Also mark selected free lines as dragging so Electric/Yjs doesn't overwrite their preview
      selectionDragFreeLineIdsRef.current.forEach((edgeId) => store.setDragging(edgeId, true));
    };

    const onSelDragUpdate = (ev: any) => {
      if (!selectionDragActiveRef.current) return;
      const detail = ev?.detail || {};
      const dx = Number(detail.deltaX) || 0;
      const dy = Number(detail.deltaY) || 0;
      selectionDragDeltaRef.current = { x: dx, y: dy };

      // Move selected nodes visually
      selectionDragNodeIdsRef.current.forEach((id) => {
        const startPos = selectionDragStartNodePosRef.current.get(id);
        const kn = nodeRefs.current.get(id);
        if (!startPos || !kn) return;
        try {
          kn.position({ x: startPos.x + dx, y: startPos.y + dy });
        } catch { }
      });

      // Preview free lines by updating their model points (canvas coords)
      selectionDragFreeLineIdsRef.current.forEach((edgeId) => {
        const startPts = selectionDragFreeLineStartPointsRef.current.get(edgeId);
        if (!startPts) return;
        const nextFrom = { x: startPts.fromPoint.x + dx, y: startPts.fromPoint.y + dy };
        const nextTo = { x: startPts.toPoint.x + dx, y: startPts.toPoint.y + dy };
        try {
          useDiagramStore.getState().updateEdge(edgeId, { attrs: { fromPoint: nextFrom, toPoint: nextTo } as any }, true);
        } catch { }
      });

      // Keep connector visuals in sync for moved nodes
      if (selectionDragNodeIdsRef.current.size > 0) {
        try {
          scheduleDirtyForNodes(selectionDragNodeIdsRef.current);
        } catch { }
      }

      layerRef.current?.batchDraw();
    };

    const onSelDragEnd = () => {
      if (!selectionDragActiveRef.current) return;
      selectionDragActiveRef.current = false;
      setIsDraggingShape(false);

      const store = useDiagramStore.getState();
      const { x: dx, y: dy } = selectionDragDeltaRef.current;

      // Commit node moves
      const commands: any[] = [];
      const moves: Array<{ nodeId: string; oldPosition: { x: number; y: number }; newPosition: { x: number; y: number } }> = [];
      if ((dx !== 0 || dy !== 0) && selectionDragNodeIdsRef.current.size > 0) {
        selectionDragNodeIdsRef.current.forEach((id) => {
          const oldPos = selectionDragStartNodePosRef.current.get(id);
          if (!oldPos) return;
          moves.push({
            nodeId: id,
            oldPosition: { x: oldPos.x, y: oldPos.y },
            newPosition: { x: oldPos.x + dx, y: oldPos.y + dy },
          });
        });
        if (moves.length > 0) {
          commands.push(new MoveNodesCommand(moves));
        }
      }

      // Commit free line moves (persist fromPoint/toPoint)
      if (dx !== 0 || dy !== 0) {
        selectionDragFreeLineIdsRef.current.forEach((edgeId) => {
          const startPts = selectionDragFreeLineStartPointsRef.current.get(edgeId);
          if (!startPts) return;
          const nextFrom = { x: startPts.fromPoint.x + dx, y: startPts.fromPoint.y + dy };
          const nextTo = { x: startPts.toPoint.x + dx, y: startPts.toPoint.y + dy };
          commands.push(
            new MoveFreeLineCommand(
              edgeId,
              { fromPoint: startPts.fromPoint, toPoint: startPts.toPoint },
              { fromPoint: nextFrom, toPoint: nextTo }
            )
          );
        });
      } else {
        // No delta: nothing to persist.
      }

      const execPromise =
        commands.length === 1
          ? commandManager.execute(commands[0])
          : commands.length > 1
            ? commandManager.executeBatch(commands, 'Move Selection')
            : Promise.resolve();

      // After committing node moves, recompute flowchart connector anchors for moved nodes
      void execPromise.then(() => {
        try {
          if (moves.length === 0) return;
          const storeAfter = useDiagramStore.getState();
          const movedPos = new Map<string, { x: number; y: number }>(moves.map((m) => [m.nodeId, m.newPosition] as const));
          const movedIds = new Set<string>(moves.map((m) => m.nodeId));

          // (anchor pair logic below)
          const chooseAnchorPair = (
            aNode: { x: number; y: number; w?: number; width?: number; h?: number; height?: number },
            bNode: { x: number; y: number; w?: number; width?: number; h?: number; height?: number },
            aPos?: { x: number; y: number },
            bPos?: { x: number; y: number }
          ): { aSide?: 'top' | 'right' | 'bottom' | 'left'; bSide?: 'top' | 'right' | 'bottom' | 'left' } => {
            const aW = (aNode.w ?? (aNode as any).width ?? 120) as number;
            const aH = (aNode.h ?? (aNode as any).height ?? 80) as number;
            const aX = aPos?.x ?? (aNode as any).x; const aY = aPos?.y ?? (aNode as any).y;
            const aLeft = aX, aRight = aX + aW, aTop = aY, aBottom = aY + aH;
            const bW = (bNode.w ?? (bNode as any).width ?? 120) as number;
            const bH = (bNode.h ?? (bNode as any).height ?? 80) as number;
            const bX = bPos?.x ?? (bNode as any).x; const bY = bPos?.y ?? (bNode as any).y;
            const bRight = bX + bW, bBottom = bY + bH, bTop = bY;
            const bCx = bX + bW / 2; const bCy = bY + bH / 2;
            if (aTop < bTop && bCx > aLeft && bCx < aRight) return { aSide: 'bottom', bSide: 'top' } as const;
            if (aRight < bRight && bCy > aTop && bCy < aBottom) return { aSide: 'right', bSide: 'left' } as const;
            if (aTop > bBottom && bCx > aLeft && bCx < aRight) return { aSide: 'top', bSide: 'bottom' } as const;
            if (aLeft > bRight && bCy > aTop && bCy < aBottom) return { aSide: 'left', bSide: 'right' } as const;
            return {};
          };

          const mm = getNodeMutationManager();
          mm.runInTransaction(() => {
            for (const e of storeAfter.edges) {
              if ((e as any)?.type !== 'sequence_flow') continue;
              const fromId = (e as any).from as string;
              const toId = (e as any).to as string;
              if (!fromId || !toId) continue;
              if (fromId.startsWith('__free') || toId.startsWith('__free')) continue;
              if (!movedIds.has(fromId) && !movedIds.has(toId)) continue;

              const fromNode = storeAfter.nodes.find((n) => n.id === fromId) as any;
              const toNode = storeAfter.nodes.find((n) => n.id === toId) as any;
              if (!fromNode || !toNode) continue;

              const fromPos = movedPos.get(fromId);
              const toPos = movedPos.get(toId);
              const pair = chooseAnchorPair(fromNode, toNode, fromPos, toPos);

              const prevFromAnchor = (e as any).fromAnchor as any;
              const prevToAnchor = (e as any).toAnchor as any;
              const fromLocked = !!(e as any).fromAnchorLocked;
              const toLocked = !!(e as any).toAnchorLocked;

              const updates: Record<string, any> = {};
              if (!fromLocked && pair.aSide) {
                if (prevFromAnchor !== pair.aSide) {
                  updates.fromAnchor = pair.aSide as any;
                  updates.fromOffset = 0.5 as any;
                }
              }
              if (!toLocked && pair.bSide) {
                if (prevToAnchor !== pair.bSide) {
                  updates.toAnchor = pair.bSide as any;
                  updates.toOffset = 0.5 as any;
                }
              }

              if (Object.keys(updates).length === 0) continue;
              try {
                const cmd = new UpdateEdgeCommand(e.id, updates as any);
                cmd.execute();
              } catch { }
            }
          });
        } catch { }
      });

      // IMPORTANT: keep draggingIds set until the commit command finishes.
      // Otherwise the store→Yjs connector sync can persist the preview outside history,
      // causing undo to only affect nodes (not free lines).
      void execPromise.finally(() => {
        // Clear dragging flags for nodes
        selectionDragNodeIdsRef.current.forEach((id) => store.setDragging(id, false));
        // Clear dragging flags for free lines
        selectionDragFreeLineIdsRef.current.forEach((edgeId) => store.setDragging(edgeId, false));
      });

      // Reattach transformer for selected nodes after drag ends
      try {
        const currentSelected: Set<string> = new Set(store.selectedIds);
        if (transformerRef.current && currentSelected.size > 0) {
          const nodesToTransform: Konva.Node[] = [];
          currentSelected.forEach((id) => {
            const container = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
            const shapeNode = nodesForRender.find((n) => n.id === id) as any;
            const isLocked = !!(shapeNode?.locked || shapeNode?.attrs?.locked);
            const isVisible = !(shapeNode && (shapeNode.visible === false || shapeNode?.attrs?.visible === false));

            // Check if node is transformable from manifest
            let isTransformable = true;
            if (shapeNode?.type) {
              const nodeDiagramType = diagramType || 'genogram';
              const manifest = getShapeManifest(nodeDiagramType as any, shapeNode.type);
              isTransformable = manifest?.capabilities?.transformable !== false;
            }

            if (container && !isLocked && isVisible && isTransformable) {
              const variant = String(shapeNode?.attrs?.variant ?? shapeNode?.attrs?.shapeType ?? '').toLowerCase();
              const useFullContainer = variant === 'bpmn-annotation';
              const inner = !useFullContainer && (container as any)?.findOne
                ? ((container as any).findOne('.rotation-group') as Konva.Node)
                : undefined;
              nodesToTransform.push((inner || (container as unknown as Konva.Node)) as Konva.Node);
            }
          });
          transformerRef.current.nodes(nodesToTransform);
          transformerRef.current.getLayer()?.batchDraw();
        }
      } catch { }

      selectionDragDeltaRef.current = { x: 0, y: 0 };
      selectionDragStartNodePosRef.current.clear();
      selectionDragNodeIdsRef.current = new Set();
      selectionDragFreeLineIdsRef.current = new Set();
      selectionDragFreeLineStartPointsRef.current.clear();
      // model-driven preview; no visual translation refs needed
    };

    window.addEventListener('selection:drag:start', onSelDragStart as any);
    window.addEventListener('selection:drag:update', onSelDragUpdate as any);
    window.addEventListener('selection:drag:end', onSelDragEnd as any);
    return () => {
      window.removeEventListener('selection:drag:start', onSelDragStart as any);
      window.removeEventListener('selection:drag:update', onSelDragUpdate as any);
      window.removeEventListener('selection:drag:end', onSelDragEnd as any);
    };
  }, [canWrite, commandManager, edges, nodes, nodesForRender]);

  const rotateHandleGroupRef = useRef<Konva.Container | null>(null);

  const { handleStageMouseDown, handleStageMouseMove, handleStageMouseUp, connectorNodeCommitHandledRef } = useCanvasStagePointerHandlers({
    stageRef,
    layerRef,
    nodeRefs,
    frameGroupRefs,
    toolRegistry,
    canWrite,
    clearActiveComment,
    isPanMode,
    panOnPrimaryCanvasDrag: isPositionChart,
    isPanning,
    setIsPanning,
    panStart,
    setPanStart,
    isSelecting,
    setIsSelecting,
    selectionRect,
    setSelectionRect,
    setSelected,
    setUserSelection,
    placePreview,
    frameTool,
    frameManager,
    setSelectedFrameId,
    isConnecting,
    setIsConnecting,
    firstConnectNode,
    setFirstConnectNode,
    hoveredMarriageBar,
    setHoveredMarriageBar,
    hoveredTargetId,
    setHoveredTargetId,
    setHoveredTargetAnchor,
    setHoveredTargetPoint,
    setHoveredTargetAnchors,
    connectionPreview,
    setConnectionPreview,
    connectorTool,
    previewStartAnchorRef,
    previewEndAnchorRef,
    autoStartAnchorRef,
    freeConnectionStartPointRef,
    beginConnectingFromNode,
    getSnapCandidatesForSourceNode,
    nodes,
    edges,
    interactiveNodes,
    renderedEdges,
    diagramType,
    isEndpointDragging,
    effectiveDiagramType,
    commandManager,
    doc,
    canvasPluginsRef,
    connectionCommitExtra: effectiveDiagramType === 'org' ? { orgReportingTargets } : undefined,
    ensureOrgDeps,
    isRotating,
    setIsRotating,
    rotateTargetIdRef,
    rotateStartAngleRef,
    rotateInitialRotationRef,
    rotatePreviewRef,
    rotateInnerRef,
    rotateCenterRef,
    rotateRafRef,
    rotateHandleGroupRef,
    updateRotateHandlePosition,
    scheduleDirtyForNode,
    alignmentGuidesEnabled: effectiveAlignmentGuidesEnabled,
    distributionGuidesEnabled: effectiveDistributionGuidesEnabled,
    rotationStaticCandidatesRef,
    getCanvasBoundsForNodeId,
    setAlignmentGuidesThrottled,
    setDistributionSegmentsThrottled,
    previewViewportStage: previewPanViewportStage,
    getLatestViewportStage,
    commitViewportStageImmediate
  });

  const handleStageMouseDownWithPasteFocus = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      canvasPasteHostRef.current?.focus({ preventScroll: true });
      handleStageMouseDown(e);
    },
    [handleStageMouseDown],
  );

  const handleNodeDoubleClick = useCallback(async (id: string, _e: Konva.KonvaEventObject<MouseEvent>) => {
    // Bundle drill-through: when a node carries a `bundleLink` attribute
    // (stamped by the RDM hydrator from `link id -> "view.rdm"` declarations),
    // double-click navigates to the linked view. This is the network-domain
    // drill-through flow. The viewer listens for `canvas:bundle-link:click`
    // and performs the actual navigation via its own routing adapter.
    const node = useDiagramStore.getState().nodes.find((n) => n.id === id) as any;
    const bundleLink = node?.attrs?.bundleLink as string | undefined;
    if (bundleLink) {
      window.dispatchEvent(
        new CustomEvent('canvas:bundle-link:click', { detail: { nodeId: id, bundleLink } }),
      );
      return;
    }
    // Compass Explorer: double-clicking a bubble or frame toggles its
    // in-place expansion. Depth navigation lives on the bubble's
    // explicit in-card affordance, so plain click and double-click do
    // not unexpectedly change Explorer focus.
    if (node?.type === 'compass_bubble' || node?.attrs?.containerId) {
      window.dispatchEvent(
        new CustomEvent('compass:toggle-expand', { detail: { nodeId: id, mode: 'expand' } }),
      );
      return;
    }
    // Otherwise: no global double-click behavior. Double-click is reserved
    // for text editing inside node components.
  }, []);

  const handleNodeClick = (id: string, e: Konva.KonvaEventObject<MouseEvent>) => {
    // Do not change selection on right/middle click (context menu/pan)
    if (e?.evt && (e.evt.button === 2 || e.evt.button === 1)) return;
    if (isPanMode) return; // ignore node clicks in pan mode
    if (effectiveDiagramType === 'org') {
      window.dispatchEvent(new CustomEvent('orgchart:node-click', { detail: { nodeId: id } }));
    }
    // Ctrl/Cmd+Click: follow shape link when available
    if (e.evt && (e.evt.ctrlKey || e.evt.metaKey)) {
      const node = nodes.find(n => n.id === id) as any;
      const link = node?.attrs?.link;
      if (typeof link === 'string' && link.trim().length > 0) {
        try {
          const url = new URL(link, window.location.href);
          // Support `?shape=<id>` (preferred) and legacy `/s/<id>`
          const m = url.pathname.match(/\/s\/([\w-]+)/);
          const targetShape = url.searchParams.get('shape') || (m ? m[1] : (url.hash?.startsWith('#') ? url.hash.slice(1) : null));
          // Same diagram when origin+pathname match
          const sameDiagram = (url.origin + url.pathname) === (window.location.origin + window.location.pathname);
          if (sameDiagram && targetShape) {
            const target = nodes.find(n => n.id === targetShape);
            if (target && stageRef.current) {
              const stage = stageRef.current;
              const scale = stage.scaleX();
              const view = stage.container().getBoundingClientRect();
              const cx = target.x + (target.w || (target as any).width || 60) / 2;
              const cy = target.y + (target.h || (target as any).height || 60) / 2;
              const nextX = view.width / 2 - cx * scale;
              const nextY = view.height / 2 - cy * scale;
              stage.position({ x: nextX, y: nextY });
              setStage({ offsetX: nextX, offsetY: nextY });
              // briefly highlight selection
              setSelected(new Set([targetShape]));
              setUserSelection?.([targetShape]);
            }
            return; // handled
          }
          // Otherwise, navigate to the link (another diagram/app)
          window.location.href = url.toString();
          return;
        } catch {
          // If link is just a raw id (local shape id)
          const target = nodes.find(n => n.id === link);
          if (target && stageRef.current) {
            const stage = stageRef.current;
            const scale = stage.scaleX();
            const view = stage.container().getBoundingClientRect();
            const cx = target.x + (target.w || (target as any).width || 60) / 2;
            const cy = target.y + (target.h || (target as any).height || 60) / 2;
            const nextX = view.width / 2 - cx * scale;
            const nextY = view.height / 2 - cy * scale;
            stage.position({ x: nextX, y: nextY });
            setStage({ offsetX: nextX, offsetY: nextY });
            setSelected(new Set([target.id]));
            setUserSelection?.([target.id]);
            return;
          }
        }
      }
    }
    // Public viewers can inspect cards, but cannot activate editing tools or
    // mutate the diagram. Keep this branch before all editing behavior.
    if (!canWrite) {
      setSelected(new Set([id]));
      setUserSelection?.([id]);
      const ui = useUIStore.getState();
      ui.setPropertyPanelUserClosed(false);
      ui.setActivePanel('properties');
      return;
    }
    // Ignore selection while text tool is active
    try { if (toolRegistry.getActiveTool()?.manifest.id === 'text') return; } catch { }
    // Deactivate pen tool when clicking on a shape
    try {
      const activeTool = toolRegistry.getActiveTool();
      if (activeTool?.manifest.id === 'pen') {
        toolRegistry.activateTool('genogram-selection');
      }
    } catch { }
    // If in connecting mode, handle connection
    if (isConnecting) {
      if (connectorNodeCommitHandledRef.current) {
        return;
      }
      if (!firstConnectNode) {
        beginConnectingFromNode(id, getCurrentCanvasPointer() ?? undefined);
        return;
      }
      if (firstConnectNode === id) {
        return;
      }
      const sourceNode = nodes.find((node) => node.id === firstConnectNode);
      const targetNode = nodes.find((node) => node.id === id);
      const plan = planCanvasConnectionCommit({
        plugins: canvasPluginsRef.current,
        diagramType: effectiveDiagramType,
        sourceNodeId: firstConnectNode,
        targetNodeId: id,
        sourceNode,
        targetNode,
        startAnchor: previewStartAnchorRef.current,
        endAnchor: previewEndAnchorRef.current,
        currentConnectionType: connectorTool.connectionType || 'line',
        nodes,
        edges,
        context: {
          toolRegistry,
          diagramType: effectiveDiagramType,
          commandManager,
          doc,
          isPlanningChart,
          isExplicitScenarioAuthoring,
        },
        extra: {
          ...(effectiveDiagramType === 'org' ? { orgReportingTargets } : {}),
          autoStartAnchor: autoStartAnchorRef.current,
        },
      });
      executeCanvasConnectionPlan(plan, {
        connectorTool,
        commandManager,
        ensureOrgDeps,
      });
      setFirstConnectNode(null);
      setIsConnecting(false);
      autoStartAnchorRef.current = false;
      previewStartAnchorRef.current = null;
      setConnectionPreview(prev => ({ ...prev, visible: false }));
      document.body.style.cursor = 'default';
      try { toolRegistry.activateTool('genogram-selection'); } catch { }
      return;
    }

    // Normal selection behavior (read freshest selection from store to avoid stale closures)
    const currentSelected: Set<string> = (() => {
      try { return new Set((useDiagramStore as any).getState().selectedIds || []); } catch { return new Set(selectedIds); }
    })();

    const clickedNode = nodes.find((n) => n.id === id);
    const groupKey = getNodeGroupKey(clickedNode);
    const groupMemberIds = groupKey ? collectGroupMembersByKey([groupKey], nodes) : new Set<string>([id]);

    if (e.evt.shiftKey) {
      // Append only
      groupMemberIds.forEach((gid) => currentSelected.add(gid));
      const next = new Set(currentSelected);
      setSelected(next);
      setUserSelection?.(Array.from(next));
    } else if (e.evt.ctrlKey || e.evt.metaKey) {
      // Toggle membership
      const next = new Set(currentSelected);
      const shouldRemoveAll = Array.from(groupMemberIds).every((gid) => next.has(gid));
      groupMemberIds.forEach((gid) => {
        if (shouldRemoveAll) next.delete(gid);
        else next.add(gid);
      });
      setSelected(next);
      setUserSelection?.(Array.from(next));
    } else {
      // Single select
      setSelected(new Set(groupMemberIds));
      setUserSelection?.(Array.from(groupMemberIds));
    }
  };

  const handleEdgeClick = useCallback((id: string, e: Konva.KonvaEventObject<MouseEvent>) => {
    // Do not change selection on right/middle click (context menu/pan)
    if (e?.evt && (e.evt.button === 2 || e.evt.button === 1)) return;

    // Public viewers may inspect an edge in the read-only properties panel.
    if (!canWrite) {
      setSelected(new Set([id]));
      setUserSelection?.([id]);
      const ui = useUIStore.getState();
      ui.setPropertyPanelUserClosed(false);
      ui.setActivePanel('properties');
      return;
    }
    const openPropertiesPanel = () => {
      const ui = useUIStore.getState();
      ui.setPropertyPanelUserClosed(false);
      ui.setActivePanel('properties');
    };
    const currentSelected: Set<string> = (() => {
      try { return new Set((useDiagramStore as any).getState().selectedIds || []); } catch { return new Set(selectedIds); }
    })();
    if (e.evt.shiftKey) {
      currentSelected.add(id);
      const next = new Set(currentSelected);
      setSelected(next);
      setUserSelection?.(Array.from(next));
      openPropertiesPanel();
    } else if (e.evt.ctrlKey || e.evt.metaKey) {
      const next = new Set(currentSelected);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelected(next);
      setUserSelection?.(Array.from(next));
      if (next.has(id)) openPropertiesPanel();
    } else {
      setSelected(new Set([id]));
      setUserSelection?.([id]);
      openPropertiesPanel();
    }
  }, [canWrite, selectedIds, setSelected, setUserSelection]);

  useEffect(() => {
    if (diagramType !== 'org') return;
    const visibleIds = new Set<string>();
    renderedNodes.forEach((node) => visibleIds.add(node.id));
    renderedEdges.forEach((edge) => visibleIds.add(edge.id));
    // Explicit branch selections include collapsed descendants. Retain only
    // still-permissioned source cards, so they can receive the same drag delta.
    if (projectedLayoutMode === 'custom') {
      nodes.filter(isProjectionOwnedNode).forEach((node) => visibleIds.add(node.id));
    }

    let changed = false;
    const next = new Set<string>();
    selectedIds.forEach((id) => {
      if (visibleIds.has(id)) {
        next.add(id);
      } else {
        changed = true;
      }
    });

    if (changed) {
      setSelected(next);
      setUserSelection?.(Array.from(next));
    }
  }, [diagramType, renderedNodes, renderedEdges, selectedIds, setSelected, setUserSelection, projectedLayoutMode, nodes]);

  const handleFrameTransformStart = (frameId: string) => {
    if (!checkWritePermission()) {
      try {
        frameTransformerRef.current?.nodes([]);
        frameTransformerRef.current?.getLayer()?.batchDraw();
      } catch { }
      return;
    }
    const frame = frameManager.getFrame(frameId);
    if (frame) {
      frameTransformStartState.current.set(frameId, frame.getBounds());
    }
  };

  const handleFrameTransformEnd = (frameId: string, e: Konva.KonvaEventObject<Event>) => {
    if (!checkWritePermission()) {
      return;
    }
    const frame = frameManager.getFrame(frameId);
    const rectNode = e.target;
    const stage = rectNode?.getStage?.();
    if (!frame || !rectNode || !stage) {
      return;
    }
    const stageScale = stage.scaleX() || 1;
    const stagePos = stage.position() || { x: 0, y: 0 };
    const clientRect = rectNode.getClientRect({ skipShadow: true });

    const newBounds: IFrameBounds = {
      x: (clientRect.x - stagePos.x) / stageScale,
      y: (clientRect.y - stagePos.y) / stageScale,
      width: clientRect.width / stageScale,
      height: clientRect.height / stageScale,
    };

    // Normalize Konva state to match the new bounds so visuals and data stay in sync.
    try {
      rectNode.scaleX(1);
      rectNode.scaleY(1);
      rectNode.position({ x: 0, y: 0 });
      rectNode.width(newBounds.width);
      rectNode.height(newBounds.height);
      const group = frameGroupRefs.current.get(frameId);
      group?.position({ x: newBounds.x, y: newBounds.y });
    } catch { }

    frameTransformStartState.current.delete(frameId);
    frameManager.resizeFrame(frameId, newBounds);
  };

  const { handleTransformStart, handleTransform, handleTransformEnd } = useCanvasTransformHandlers({
    setIsTransforming,
    checkWritePermission,
    nodes,
    nodeRefs,
    transformerRef,
    transformStartState,
    selectedIds,
    commandManager,
    scheduleDirtyForNode,
    scheduleDirtyForNodes,
  });

  const handleTransformStartWithGuides = useCallback(
    (nodeId: string, e: any) => {
      try {
        const store = useDiagramStore.getState();
        const currentSelected: Set<string> = new Set(store.selectedIds);
        const movingIds = currentSelected.has(nodeId) ? currentSelected : new Set([nodeId]);
        transformMovingIdsRef.current = movingIds;
        transformStaticCandidatesRef.current = nodes.reduce<AlignmentGuideCandidate[]>((acc, n) => {
          if (movingIds.has(n.id)) return acc;
          const isVisible = !((n as any)?.visible === false || (n as any)?.attrs?.visible === false);
          if (!isVisible) return acc;
          const measured = getCanvasBoundsForNodeId(n.id);
          if (measured) {
            acc.push({ shapeId: n.id, bounds: measured });
            return acc;
          }
          const w = (n.w as number) || (n.width as number) || 60;
          const h = (n.h as number) || (n.height as number) || 60;
          acc.push({ shapeId: n.id, bounds: { x: n.x, y: n.y, width: w, height: h } });
          return acc;
        }, []);
        setAlignmentGuidesThrottled([]);
        setDistributionSegmentsThrottled([]);
      } catch { }

      handleTransformStart(nodeId, e);
    },
    [
      getCanvasBoundsForNodeId,
      handleTransformStart,
      nodes,
      setAlignmentGuidesThrottled,
      setDistributionSegmentsThrottled,
      transformMovingIdsRef,
      transformStaticCandidatesRef,
    ]
  );

  // Listen for link prompt from toolbar
  useEffect(() => {
    const off = eventBus.on('canvas:link:prompt', async ({ nodeId }: { nodeId?: string }) => {
      try {
        const targetId = nodeId || Array.from(selectedIds)[0];
        if (!targetId) return;
        const node = nodes.find(n => n.id === targetId) as any;
        if (!node) return;
        const kn = nodeRefs.current.get(targetId) as any;
        const stage = stageRef.current;
        if (!kn || !stage) return;
        const stageBox = stage.container().getBoundingClientRect();
        const scale = stage.scaleX();
        // place below shape center
        const w = node.w || (node as any).width || 60;
        const h = node.h || (node as any).height || 60;
        const cx = (node.x || 0) + w / 2;
        const cy = (node.y || 0) + h + 12; // 12px gap below
        const px = stageBox.left + stage.x() + cx * scale;
        const py = stageBox.top + stage.y() + cy * scale;
        let initial = '';
        try { initial = (node?.attrs?.link || '') as string; } catch { }
        setLinkPrompt({ visible: true, position: { x: px, y: py }, nodeId: targetId, initial });
      } catch (err) {
        canvasLogger.error('Failed to open link prompt', err);
      }
    });
    return () => off();
  }, [eventBus, nodes, selectedIds]);

  const handleTransformEndWithGuides = useCallback(
    (nodeId: string, e: any) => {
      handleTransformEnd(nodeId, e);
      transformStaticCandidatesRef.current = [];
      transformMovingIdsRef.current = new Set();
      setAlignmentGuidesThrottled([]);
      setDistributionSegmentsThrottled([]);
    },
    [handleTransformEnd, setAlignmentGuidesThrottled, setDistributionSegmentsThrottled]
  );

  useCanvasLinkIndicatorListener();

  return (
    <div
      ref={canvasPasteHostRef}
      tabIndex={0}
      className="w-full h-full overflow-hidden relative outline-none focus:outline-none"
      data-testid="tid.canvas.stage"
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onPaste={handleCanvasPaste}
    >
      <ImageUploadModal
        open={imageUploadModalOpen}
        onOpenChange={(next) => {
          setImageUploadModalOpen(next);
          if (!next) {
            setImageUploadModalConfig({});
          }
        }}
        onUploadFiles={handleModalImageUpload}
        maxFiles={imageUploadModalConfig.maxFiles}
        title={imageUploadModalConfig.title}
        description={imageUploadModalConfig.description}
      />
	    <CanvasPluginHosts
	      canvasPlugins={canvasPlugins}
	      toolRegistry={toolRegistry}
	      effectiveDiagramType={effectiveDiagramType}
	      commandManager={commandManager}
	      doc={doc ?? undefined}
	      frames={frames}
	      stageRef={stageRef}
	      layerRef={layerRef}
	      pluginHostStateById={pluginHostStateById}
	      setPluginHostState={setPluginHostState}
	    />
	    <Stage
	      ref={stageRef}
	      width={dimensions.width}
	      height={dimensions.height}
        onWheel={handleWheel}
        onMouseDown={handleStageMouseDownWithPasteFocus}
        onMouseMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onTouchStart={handleStageMouseDownWithPasteFocus as any}
        onTouchMove={handleStageMouseMove as any}
        onTouchEnd={handleStageMouseUp as any}
        scaleX={viewportStage.scale}
        scaleY={viewportStage.scale}
        x={viewportStage.offsetX}
        y={viewportStage.offsetY}
      >
        <Layer
          ref={layerRef}
          onContextMenu={(e: any) => {
            e.evt.preventDefault();
            const stage = stageRef.current;
            if (!stage) return;
            const pointer = stage.getPointerPosition();
            if (!pointer) return;
            const scale = stage.scaleX();
            const position = stage.position();
            const diagramX = (pointer.x - position.x) / scale;
            const diagramY = (pointer.y - position.y) / scale;
            const containerRect = stage.container().getBoundingClientRect();

            // Determine if a shape was right-clicked: climb to ancestor with name === 'shape'
            const target: any = e.target;
            try {
              const isShapeSelf = typeof target?.name === 'function' && target.name() === 'shape';
              const shapeNode = isShapeSelf ? target : (typeof target?.findAncestor === 'function' ? target.findAncestor((n: any) => typeof n?.name === 'function' && n.name() === 'shape', true) : null);
              if (shapeNode && typeof shapeNode.id === 'function') {
                const shapeId = shapeNode.id();
                useCanvasContextMenu.getState().openAt({
                  screenX: containerRect.left + pointer.x,
                  screenY: containerRect.top + pointer.y,
                  localX: pointer.x,
                  localY: pointer.y,
                  diagramX,
                  diagramY,
                  target: { kind: 'shape', shapeId }
                });
                return;
              }
            } catch { }

            // Fallback: open canvas menu
            useCanvasContextMenu.getState().openAt({
              screenX: containerRect.left + pointer.x,
              screenY: containerRect.top + pointer.y,
              localX: pointer.x,
              localY: pointer.y,
              diagramX,
              diagramY,
              target: { kind: 'canvas' }
            });
          }}
        >
          <Rect
            x={-250000}
            y={-250000}
            width={500000}
            height={500000}
            fill={mode === 'dark' ? '#0E162A' : '#F7F8FA'}
            name="background"
          />

          {/* Render frames first (behind everything else) */}
          {frames.map((frame) => {
            const Comp: any = VersionedComponentRegistry.getComponent('frame', (frame as any).version, 'frame') || FrameRenderer;
            return (
              <Comp
                key={frame.id}
                frame={frame}
                selected={selectedFrameId === frame.id}
                stageScale={viewportStage.scale}
                isDropTarget={hoveredDropFrameId === frame.id}
                onSelect={(frameId: string, _e: any) => {
                  setSelected(new Set());
                  setSelectedFrameId(frameId);
                  frameManager.setActiveFrame(frameId);
                }}
                onDoubleClick={(frameId: string) => {
                  // Infrastructure drill-through: when the frame represents
                  // a container resource (VPC/Subnet) from a Compass infra
                  // import, double-click navigates to the per-VPC view
                  // diagram materialized in the folder.
                  const f = frameManager.getFrame(frameId);
                  const meta = f?.metadata as Record<string, unknown> | undefined;
                  const source = meta?.source;
                  const containerType = meta?.containerType;
                  const resourceId = meta?.resourceId;
                  const needsDrilldown =
                    (source === 'network-import' || source === 'compass-view') &&
                    containerType === 'aws_vpc' &&
                    typeof resourceId === 'string';
                  if (!needsDrilldown) return;
                  // Fetch views and navigate to the matching VPC diagram.
                  // Uses sessionStorage + navigation so it works across stage.
                  // Extract folderId from the current route /f/:folderId/d/:diagramId
                  const match_path = window.location.pathname.match(/^\/f\/([^/]+)\//);
                  const folderId = match_path?.[1];
                  if (!folderId) return;
                  fetch(`/api/compass/folders/${folderId}/views`, { credentials: 'include' })
                    .then((r) => (r.ok ? r.json() : null))
                    .then((data) => {
                      const views = (data?.views ?? []) as Array<{
                        queryId: string;
                        diagramId?: string;
                        params?: Record<string, string>;
                      }>;
                      const match = views.find(
                        (v) => v.queryId === `network-vpc:${resourceId}` && v.diagramId,
                      );
                      if (match?.diagramId) {
                        window.open(`/f/${folderId}/d/${match.diagramId}`, '_blank');
                      }
                    })
                    .catch(() => {
                      // Silent — drill-through is best-effort
                    });
                }}
                onDragStart={(frameId: string) => {
                  if (frame?.bounds) {
                    lastFrameDragPosRef.current.set(frameId, { x: frame.bounds.x, y: frame.bounds.y });
                  }
                  // Hide children during drag for perf
                  const f = frameManager.getFrame(frameId);
                  f?.shapes.forEach(id => {
                    const n = nodeRefs.current.get(id);
                    if (n) n.visible(false);
                  });
                  layerRef.current?.batchDraw();
                }}
                onDragMove={(frameId: string, pos: { x: number; y: number }) => {
                  const last = lastFrameDragPosRef.current.get(frameId);
                  if (!last) return;
                  const deltaX = pos.x - last.x;
                  const deltaY = pos.y - last.y;
                  if (deltaX !== 0 || deltaY !== 0) {
                    frameManager.moveFrame(frameId, deltaX, deltaY);
                    lastFrameDragPosRef.current.set(frameId, { x: pos.x, y: pos.y });
                  }
                }}
                onDragEnd={(frameId: string, pos: { x: number; y: number }) => {
                  const last = lastFrameDragPosRef.current.get(frameId);
                  if (last) {
                    const deltaX = pos.x - last.x;
                    const deltaY = pos.y - last.y;
                    if (deltaX !== 0 || deltaY !== 0) {
                      frameManager.moveFrame(frameId, deltaX, deltaY);
                    }
                  }
                  lastFrameDragPosRef.current.delete(frameId);
                  // Show children again
                  const f = frameManager.getFrame(frameId);
                  f?.shapes.forEach(id => {
                    const n = nodeRefs.current.get(id);
                    if (n) n.visible(true);
                  });
                  layerRef.current?.batchDraw();
                  // Persist bounds
                  if (f && framesMapRef.current && !isSyncingFramesFromYjs.current) {
                    isSyncingFramesToYjs.current = true;
                    framesMapRef.current.set(f.id, f.toJSON());
                    isSyncingFramesToYjs.current = false;
                  }
                }}
                onTransformStart={(frameId: string) => {
                  setSelectedFrameId(frameId);
                  const rectRef = frameRectRefs.current.get(frameId);
                  if (rectRef) {
                    frameTransformerRef.current?.nodes([rectRef]);
                  }
                  handleFrameTransformStart(frameId);
                }}
                onTransformEnd={(frameId: string, e: any) => {
                  handleFrameTransformEnd(frameId, e);
                  frameTransformerRef.current?.getLayer()?.batchDraw();
                }}
                registerRef={(frameId: string, node: any | null) => {
                  if (node) frameGroupRefs.current.set(frameId, node);
                  else frameGroupRefs.current.delete(frameId);
                }}
                registerRectRef={(frameId: string, node: any | null) => {
                  if (node) frameRectRefs.current.set(frameId, node);
                  else frameRectRefs.current.delete(frameId);
                }}
              />
            );
          })}

          {/* Frame preview during creation */}
          <FramePreview bounds={framePreviewBounds} />

	          <NodesRenderer
              diagramId={diagramId}
	            nodes={canvasNodesForRender}
	            edges={canvasEdgesForRender}
	            selectedIds={selectedIds}
	            diagramType={diagramType}
	            orgChartTenure={orgChartTenure}
	            orgReportCounts={orgReportCounts}
	            orgReportingTargets={orgReportingTargets}
	            orgExitNodeIds={orgExitNodeIds}
	            scenarioStatusIndex={scenarioProjection.statusIndex as any}
            collapsedNodeIds={collapsedNodeIds}
            preCollapseEdges={focusedEdges}
            onToggleOrgCollapse={handleToggleOrgCollapseCommand}
            onExpandOrgCollapsedBranch={handleExpandOrgCollapsedBranchOneLevel}
            getCsvRecord={getCsvRecord}
            getCsvChildren={getCsvChildren}
            getCsvSecondaryManager={getCsvSecondaryManager}
            enableOrgCollapseAnimation={enableOrgCollapseAnimation}
            registerNodeRef={(nodeId, node) => {
              if (node) {
                nodeRefs.current.set(nodeId, node);
                if (diagramType === 'org') {
                  const mountedNode = canvasNodesForRender.find((n) => n.id === nodeId);
                  const mountedNodeType = String(mountedNode?.type ?? '');
                  if (mountedNodeType.startsWith('org_')) {
                    requestAnimationFrame(() => scheduleDirtyForNode(nodeId));
                  }
                }
                // Minimal fix: if this node is currently selected, immediately
                // reattach the transformer so selection borders appear without
                // requiring a deselect/reselect cycle.
                try {
                  if (selectedIds.has(nodeId) && transformerRef.current) {
                    const nodesToTransform: Konva.Node[] = [];
                    selectedIds.forEach((id) => {
                      const container = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
                      const shapeNode = nodesForRender.find((n) => n.id === id) as any;
                      const isLocked = !!(shapeNode?.locked || shapeNode?.attrs?.locked);
                      const isVisible = !(shapeNode && (shapeNode.visible === false || shapeNode?.attrs?.visible === false));
                      if (container && !isLocked && isVisible) {
                        const variant = String(shapeNode?.attrs?.variant ?? shapeNode?.attrs?.shapeType ?? '').toLowerCase();
                        const useFullContainer = variant === 'bpmn-annotation';
                        const inner = !useFullContainer && (container as any)?.findOne
                          ? (container as any).findOne('.rotation-group') as Konva.Node
                          : undefined;
                        nodesToTransform.push((inner || (container as unknown as Konva.Node)) as Konva.Node);
                      }
                    });
                    transformerRef.current.nodes(nodesToTransform);
                    transformerRef.current.getLayer()?.batchDraw();
                  }
                } catch { }
              } else {
                nodeRefs.current.delete(nodeId);
              }
            }}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            onDragStart={(nodeId, pos) => {
              // Check write permission first
              if (!checkWritePermission()) {
                return;
              }

              // Check if node is locked BEFORE marking as dragging
              const nodeData = nodes.find(n => n.id === nodeId) as any;
              const isLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
              if ((isLocked && !isProjectionOwnedNode(nodeData)) || isProjectedNodePlacementLocked(nodeData, lockProjectedNodePlacement)) {
                // Immediately revert any visual shift and ignore drag
                const kn = nodeRefs.current.get(nodeId);
                if (kn) {
                  try { kn.position({ x: nodeData.x, y: nodeData.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                return;
              }

              if (isConnecting) {
                connectorDragSourceRef.current = nodeId;
                const sourceNodeId = firstConnectNode || nodeId;
                const didStart = beginConnectingFromNode(sourceNodeId, getCurrentCanvasPointer() ?? undefined);
                updateConnectionPreviewFromPointer(sourceNodeId);
                const kn = nodeRefs.current.get(nodeId);
                const visibleNode = interactiveNodes.find((n) => n.id === nodeId) ?? nodeData;
                if (kn && visibleNode) {
                  try { kn.position({ x: visibleNode.x, y: visibleNode.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                if (!didStart) {
                  connectorDragSourceRef.current = null;
                }
                return;
              }

              // Mark node as dragging to prevent Yjs updates from overwriting local drag state
              // IMPORTANT: read current selection live from the store to avoid stale closures
              const store = useDiagramStore.getState();
              const currentSelected: Set<string> = new Set(store.selectedIds);
              activeDragSessionIdRef.current = dragSessionIdRef.current + 1;
              dragSessionIdRef.current = activeDragSessionIdRef.current;
              const dragBaseSelection = currentSelected.has(nodeId) ? currentSelected : new Set([nodeId]);
              const dragTargets = expandNodeIdsWithGroupMembers(dragBaseSelection, nodes);

              // Cache static candidates for alignment/distribution guides for the duration of this drag.
              // (Snap threshold is computed per-move based on zoom.)
              alignmentMovingIdsRef.current = dragTargets;
              alignmentLastSnappedPosRef.current.clear();
              if (effectiveAlignmentGuidesEnabled || effectiveDistributionGuidesEnabled) {
                alignmentStaticCandidatesRef.current = nodes.reduce<AlignmentGuideCandidate[]>((acc, n) => {
                  if (dragTargets.has(n.id)) return acc;
                  const isVisible = !((n as any)?.visible === false || (n as any)?.attrs?.visible === false);
                  if (!isVisible) return acc;
                  const measured = getCanvasBoundsForNodeId(n.id);
                  if (measured) {
                    acc.push({ shapeId: n.id, bounds: measured });
                    return acc;
                  }
                  const w = (n.w as number) || (n.width as number) || 60;
                  const h = (n.h as number) || (n.height as number) || 60;
                  acc.push({ shapeId: n.id, bounds: { x: n.x, y: n.y, width: w, height: h } });
                  return acc;
                }, []);
                setAlignmentGuidesThrottled([]);
                setDistributionSegmentsThrottled([]);
              } else {
                alignmentStaticCandidatesRef.current = [];
                setAlignmentGuidesThrottled([]);
                setDistributionSegmentsThrottled([]);
              }

              // Capture selected free lines so they translate with multi-drag
              selectedFreeLineIdsRef.current = new Set<string>();
              freeLineDragStartPointsRef.current.clear();
              // model-driven preview; no visual translation refs needed
              currentSelected.forEach((id) => {
                const edge = edges.find((e) => e.id === id);
                if (!edge || !isFreeEndpointConnector(edge)) return;
                const fromPoint = (edge.attrs as any)?.fromPoint as { x: number; y: number } | undefined;
                const toPoint = (edge.attrs as any)?.toPoint as { x: number; y: number } | undefined;
                if (!fromPoint || !toPoint) return;
                selectedFreeLineIdsRef.current.add(edge.id);
                freeLineDragStartPointsRef.current.set(edge.id, {
                  fromPoint: { x: fromPoint.x, y: fromPoint.y },
                  toPoint: { x: toPoint.x, y: toPoint.y },
                });
                // model-driven preview; no visual translation refs needed
              });
              // Also mark selected free lines as dragging so their preview isn't overwritten.
              selectedFreeLineIdsRef.current.forEach((edgeId) => setDraggingGuarded(edgeId, true));

              setIsDraggingShape(true);
              // Detach transformer during drag to prevent flickering
              if (transformerRef.current) {
                transformerRef.current.nodes([]);
                transformerRef.current.getLayer()?.batchDraw();
              }
              // Clear any stale drag positions first to prevent conflicts
              dragStartPositions.current.clear();
              dockedDragStartPositionsRef.current.clear();
              dockedDragDeltaRef.current = { dx: 0, dy: 0 };
              draggedTableIdsRef.current.clear();
              if (dockedPreviewRafRef.current != null) {
                cancelAnimationFrame(dockedPreviewRafRef.current);
                dockedPreviewRafRef.current = null;
              }

              // Store initial positions for all drag targets (selected nodes + group peers).
              // Record start positions from live Konva refs (local coords), fallback to store.
              dragTargets.forEach((id) => {
                const nodeData = nodes.find((n) => n.id === id) as any;
                if (!nodeData) return;
                const isTargetLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
                if ((isTargetLocked && !isProjectionOwnedNode(nodeData))
                  || isProjectedNodePlacementLocked(nodeData, lockProjectedNodePlacement)) return;
                setDraggingGuarded(id, true);
                const kn = nodeRefs.current.get(id) as any;
                let sx: number | undefined;
                let sy: number | undefined;
                try {
                  if (kn && typeof kn.x === 'function') sx = kn.x();
                  if (kn && typeof kn.y === 'function') sy = kn.y();
                } catch { }
                if (!Number.isFinite(sx as number) || !Number.isFinite(sy as number)) {
                  sx = nodeData.x;
                  sy = nodeData.y;
                }
                if (Number.isFinite(sx as number) && Number.isFinite(sy as number)) {
                  dragStartPositions.current.set(id, { x: sx as number, y: sy as number });
                }
              });
              // Preserve multi-selection in awareness; otherwise select only the dragged node.
              if (currentSelected.has(nodeId)) {
                setUserSelection?.(Array.from(currentSelected));
              } else {
                setUserSelection?.([nodeId]);
              }

              // Cache start positions for nodes docked to any dragged table so they move visually during drag.
              try {
                const liveNodes = useDiagramStore.getState().nodes as any[];
                const draggedNodeData = liveNodes.find((n) => n.id === nodeId) as any;
                if (draggedNodeData?.type === 'table') {
                  draggedTableIdsRef.current.add(nodeId);
                } else if (dragTargets.size > 1) {
                  const selectedTables = Array.from(dragTargets)
                    .map((id) => liveNodes.find((n) => n.id === id))
                    .filter((n) => n?.type === 'table') as any[];
                  selectedTables.forEach((t) => draggedTableIdsRef.current.add(t.id));
                }

                if (draggedTableIdsRef.current.size > 0) {
                  const dockedNodes = liveNodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId);
                  for (const dn of dockedNodes) {
                    const ownerTableId = (dn as any)?.attrs?.tableDock?.tableId as string | undefined;
                    if (!ownerTableId || !draggedTableIdsRef.current.has(ownerTableId)) continue;
                    // Mark as dragging to prevent projection/Yjs updates from snapping it back during preview.
                    setDraggingGuarded(dn.id, true);
                    const kn = nodeRefs.current.get(dn.id) as any;
                    let sx: number | undefined;
                    let sy: number | undefined;
                    try {
                      if (kn && typeof kn.x === 'function') sx = kn.x();
                      if (kn && typeof kn.y === 'function') sy = kn.y();
                    } catch { }
                    if (!Number.isFinite(sx as number) || !Number.isFinite(sy as number)) {
                      sx = (dn as any).x;
                      sy = (dn as any).y;
                    }
                    if (Number.isFinite(sx as number) && Number.isFinite(sy as number)) {
                      dockedDragStartPositionsRef.current.set(dn.id, { x: sx as number, y: sy as number });
                    }
                  }
                  if (dockedDragStartPositionsRef.current.size > 0) {
                    dockedDragDeltaRef.current = { dx: 0, dy: 0 };
                    setDockedPreviewTick((t) => t + 1);
                  }
                }
              } catch { }

              // seed preview position for local user
              if (updatePreview) {
                const qx = Math.round(pos.x);
                const qy = Math.round(pos.y);
                lastSentPreviewRef.current.set(nodeId, { x: qx, y: qy });
                updatePreview(nodeId, { x: qx, y: qy });
              }
            }}
            onDragMove={(nodeId, pos) => {
              const nodeData = nodes.find(n => n.id === nodeId) as any;
              const isLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
              if ((isLocked && !isProjectionOwnedNode(nodeData)) || isProjectedNodePlacementLocked(nodeData, lockProjectedNodePlacement)) {
                const kn = nodeRefs.current.get(nodeId);
                if (kn) {
                  try { kn.position({ x: nodeData.x, y: nodeData.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                return;
              }

              if (connectorDragSourceRef.current === nodeId) {
                updateConnectionPreviewFromPointer(firstConnectNode || nodeId);
                const kn = nodeRefs.current.get(nodeId);
                if (kn && nodeData) {
                  try { kn.position({ x: nodeData.x, y: nodeData.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                return;
              }

              const stageScale = stageRef.current?.scaleX() || stageScaleRef.current || viewportStage.scale || 1;
              const threshold = 6 / stageScale;
              const w = (nodeData?.w as number) || (nodeData?.width as number) || 60;
              const h = (nodeData?.h as number) || (nodeData?.height as number) || 60;
              const measuredMoving = getCanvasBoundsForNodeId(nodeId);
              const movingBounds: Bounds = measuredMoving || { x: pos.x, y: pos.y, width: w, height: h };
              const staticCandidates = alignmentStaticCandidatesRef.current;

              let effectivePos = pos;
              if (effectiveAlignmentGuidesEnabled || effectiveDistributionGuidesEnabled) {
                const alignmentSnapDelta = effectiveAlignmentGuidesEnabled
                  ? AlignmentGuidesService.compute({
                      movingId: nodeId,
                      movingBounds,
                      staticCandidates,
                      threshold,
                    }).snapDelta
                  : { dx: 0, dy: 0 };
                const effectiveAlign = effectiveAlignmentGuidesSnapEnabled ? alignmentSnapDelta : { dx: 0, dy: 0 };
                const basePos = { x: pos.x + effectiveAlign.dx, y: pos.y + effectiveAlign.dy };

                const maxShapes = 10;
                const labelActivationDistance = 50 / stageScale;
                const collinearTolerance = 10 / stageScale;
                const distCandidates = staticCandidates.map((c) => ({ shapeId: c.shapeId, bounds: c.bounds }));
                const baseBounds: Bounds = translateBounds(movingBounds, effectiveAlign.dx, effectiveAlign.dy);
                const distH = effectiveDistributionGuidesEnabled
                  ? DistributionGuidesService.compute({
                      movingId: nodeId,
                      movingBounds: baseBounds,
                      staticCandidates: distCandidates,
                      orientation: 'horizontal',
                      threshold,
                      maxShapes,
                      labelActivationDistance,
                      collinearTolerance,
                    })
                  : { snapDelta: { dx: 0, dy: 0 }, segments: [] };
                const distV = effectiveDistributionGuidesEnabled
                  ? DistributionGuidesService.compute({
                      movingId: nodeId,
                      movingBounds: baseBounds,
                      staticCandidates: distCandidates,
                      orientation: 'vertical',
                      threshold,
                      maxShapes,
                      labelActivationDistance,
                      collinearTolerance,
                    })
                  : { snapDelta: { dx: 0, dy: 0 }, segments: [] };

                const mergedSegments = effectiveDistributionGuidesEnabled ? [...distH.segments, ...distV.segments] : [];
                setDistributionSegmentsThrottled(mergedSegments);

                const distDx = effectiveDistributionGuidesSnapEnabled ? distH.snapDelta.dx : 0;
                const distDy = effectiveDistributionGuidesSnapEnabled ? distV.snapDelta.dy : 0;
                effectivePos = { x: basePos.x + distDx, y: basePos.y + distDy };

                const finalBounds: Bounds = translateBounds(movingBounds, effectiveAlign.dx + distDx, effectiveAlign.dy + distDy);
                const guides = effectiveAlignmentGuidesEnabled
                  ? AlignmentGuidesService.compute({
                      movingId: nodeId,
                      movingBounds: finalBounds,
                      staticCandidates,
                      threshold,
                    }).guides
                  : [];
                setAlignmentGuidesThrottled(guides);

                // Only persist a snapped position when snapping is actually applied.
                // Otherwise drag-end commits should use the raw position (and grid snap will apply there).
                const didSnap = effectiveAlign.dx !== 0 || effectiveAlign.dy !== 0 || distDx !== 0 || distDy !== 0;
                if (didSnap) alignmentLastSnappedPosRef.current.set(nodeId, effectivePos);
                else alignmentLastSnappedPosRef.current.delete(nodeId);
              } else {
                setAlignmentGuidesThrottled([]);
                setDistributionSegmentsThrottled([]);
                alignmentLastSnappedPosRef.current.delete(nodeId);
              }

              if (updatePreview) {
                const qx = Math.round(effectivePos.x);
                const qy = Math.round(effectivePos.y);
                const last = lastSentPreviewRef.current.get(nodeId) || { x: NaN, y: NaN };
                if (qx !== last.x || qy !== last.y) {
                  lastSentPreviewRef.current.set(nodeId, { x: qx, y: qy });
                  updatePreview(nodeId, { x: qx, y: qy });
                }
              }

              // Preview-only drag: do NOT write to the store per-move.
              // Move Konva nodes directly for smooth visuals.
              // Use live selection to avoid stale selection captured in memoized nodes
              //   const currentSelected: Set<string> = new Set((useDiagramStore as any).getState().selectedIds);
              const dragTargets = new Set(dragStartPositions.current.keys());

              // Drive render-time docked preview override (no persistence here).
              const activeStartPos = dragStartPositions.current.get(nodeId);
              if (activeStartPos && dockedDragStartPositionsRef.current.size > 0 && draggedTableIdsRef.current.size > 0) {
                const deltaX = effectivePos.x - activeStartPos.x;
                const deltaY = effectivePos.y - activeStartPos.y;
                if (deltaX !== 0 || deltaY !== 0) {
                  dockedDragDeltaRef.current = { dx: deltaX, dy: deltaY };
                  if (dockedPreviewRafRef.current == null) {
                    dockedPreviewRafRef.current = requestAnimationFrame(() => {
                      dockedPreviewRafRef.current = null;
                      setDockedPreviewTick((t) => t + 1);
                      // Schedule connector updates for docked nodes after preview position update.
                      const dockedNodeIds = Array.from(dockedDragStartPositionsRef.current.keys());
                      if (dockedNodeIds.length > 0) {
                        scheduleDirtyForNodes(dockedNodeIds);
                      }
                    });
                  }
                }
              }

              if (dragTargets.size > 1) {
                const startPos = dragStartPositions.current.get(nodeId);
                if (startPos) {
                  const deltaX = effectivePos.x - startPos.x;
                  const deltaY = effectivePos.y - startPos.y;
                  dragTargets.forEach((id) => {
                    if (id === nodeId) return;
                    const otherStartPos = dragStartPositions.current.get(id);
                    if (!otherStartPos) return;
                    const konvaNode = nodeRefs.current.get(id);
                    if (konvaNode) {
                      konvaNode.position({ x: otherStartPos.x + deltaX, y: otherStartPos.y + deltaY });
                    }
                  });

                  // Preview free lines during multi-drag by updating their model points (canvas coords).
                  // This is more stable than translating the Konva group, because React-Konva renders from attrs.
                  selectedFreeLineIdsRef.current.forEach((edgeId) => {
                    const startPts = freeLineDragStartPointsRef.current.get(edgeId);
                    if (!startPts) return;
                    const nextFrom = { x: startPts.fromPoint.x + deltaX, y: startPts.fromPoint.y + deltaY };
                    const nextTo = { x: startPts.toPoint.x + deltaX, y: startPts.toPoint.y + deltaY };
                    try {
                      useDiagramStore.getState().updateEdge(edgeId, { attrs: { fromPoint: nextFrom, toPoint: nextTo } as any }, true);
                    } catch { }
                  });
                  // Batch redraw once after moving peers
                  layerRef.current?.batchDraw();
                }
              }
              // Move the active node's Konva position as well (it is controlled by Konva during drag)
              const selfNode = nodeRefs.current.get(nodeId);
              if (selfNode) {
                selfNode.position(effectivePos);
              }
              // Schedule relevant connectors to recompute (RAF). When multi-selecting,
              // invalidate all affected edges for all moved nodes so connectors update live.
              if (dragTargets.size > 1) {
                scheduleDirtyForNodes(dragTargets);
              } else {
                scheduleDirtyForNode(nodeId);
              }

              // Also schedule connector updates for docked nodes when any table is dragged.
              if (dockedDragStartPositionsRef.current.size > 0 && draggedTableIdsRef.current.size > 0) {
                const dockedNodeIds = Array.from(dockedDragStartPositionsRef.current.keys());
                if (dockedNodeIds.length > 0) {
                  scheduleDirtyForNodes(dockedNodeIds);
                }
              }

              // Live preview: adjust flowchart connector anchors while nodes are being moved.
              // This updates only the local store (fromYjs=true) and respects endpoint lock flags.
              try {
                const movedIds = new Set<string>();
                const movedPos = new Map<string, { x: number; y: number }>();
                const startPos = dragStartPositions.current.get(nodeId);
                if (dragTargets.size > 1 && startPos) {
                  const dx = effectivePos.x - startPos.x;
                  const dy = effectivePos.y - startPos.y;
                  dragTargets.forEach((id) => {
                    const sp = dragStartPositions.current.get(id);
                    if (!sp) return;
                    movedIds.add(id);
                    movedPos.set(id, { x: sp.x + dx, y: sp.y + dy });
                  });
                } else {
                  movedIds.add(nodeId);
                  movedPos.set(nodeId, { x: effectivePos.x, y: effectivePos.y });
                }

                const storeNow = useDiagramStore.getState();
                // choose anchors using relative bounds; keep previous when no rule matches
                const chooseAnchorPair = (
                  aNode: { x: number; y: number; w?: number; width?: number; h?: number; height?: number },
                  bNode: { x: number; y: number; w?: number; width?: number; h?: number; height?: number },
                  aPos?: { x: number; y: number },
                  bPos?: { x: number; y: number }
                ): { aSide?: 'top' | 'right' | 'bottom' | 'left'; bSide?: 'top' | 'right' | 'bottom' | 'left' } => {
                  const aW = (aNode.w ?? (aNode as any).width ?? 120) as number;
                  const aH = (aNode.h ?? (aNode as any).height ?? 80) as number;
                  const aX = aPos?.x ?? (aNode as any).x; const aY = aPos?.y ?? (aNode as any).y;
                  const aLeft = aX, aRight = aX + aW, aTop = aY, aBottom = aY + aH;
                  const bW = (bNode.w ?? (bNode as any).width ?? 120) as number;
                  const bH = (bNode.h ?? (bNode as any).height ?? 80) as number;
                  const bX = bPos?.x ?? (bNode as any).x; const bY = bPos?.y ?? (bNode as any).y;
                  const bRight = bX + bW, bBottom = bY + bH, bTop = bY;
                  const bCx = bX + bW / 2; const bCy = bY + bH / 2;

                  // 1) A above B and B centerX within A width => bottom/top
                  if (aTop < bTop && bCx > aLeft && bCx < aRight) return { aSide: 'bottom', bSide: 'top' } as const;
                  // 2) A left of B and B centerY within A height => right/left
                  if (aRight < bRight && bCy > aTop && bCy < aBottom) return { aSide: 'right', bSide: 'left' } as const;
                  // 3) A below B and B centerX within A width => top/bottom
                  if (aTop > bBottom && bCx > aLeft && bCx < aRight) return { aSide: 'top', bSide: 'bottom' } as const;
                  // 4) A right of B and B centerY within A height => left/right
                  if (aLeft > bRight && bCy > aTop && bCy < aBottom) return { aSide: 'left', bSide: 'right' } as const;
                  return {};
                };

                const previewUpdateEdge = (edgeId: string, updates: any) => {
                  try { storeNow.updateEdge(edgeId, updates, true); } catch { }
                };

                for (const e of storeNow.edges) {
                  if ((e as any)?.type !== 'sequence_flow') continue;
                  const fromId = (e as any).from as string;
                  const toId = (e as any).to as string;
                  if (!fromId || !toId) continue;
                  if (fromId.startsWith('__free') || toId.startsWith('__free')) continue;
                  if (!movedIds.has(fromId) && !movedIds.has(toId)) continue;

                  const fromNode = storeNow.nodes.find((n) => n.id === fromId) as any;
                  const toNode = storeNow.nodes.find((n) => n.id === toId) as any;
                  if (!fromNode || !toNode) continue;

                  const fromPos = movedPos.get(fromId);
                  const toPos = movedPos.get(toId);
                  const pair = chooseAnchorPair(fromNode, toNode, fromPos, toPos);

                  const prevFromAnchor = (e as any).fromAnchor as any;
                  const prevToAnchor = (e as any).toAnchor as any;
                  const fromLocked = !!(e as any).fromAnchorLocked;
                  const toLocked = !!(e as any).toAnchorLocked;

                  const updates: Record<string, any> = {};
                  if (!fromLocked && pair.aSide) {
                    if (prevFromAnchor !== pair.aSide) {
                      updates.fromAnchor = pair.aSide as any;
                      updates.fromOffset = 0.5 as any;
                    }
                  }
                  if (!toLocked && pair.bSide) {
                    if (prevToAnchor !== pair.bSide) {
                      updates.toAnchor = pair.bSide as any;
                      updates.toOffset = 0.5 as any;
                    }
                  }

                  if (Object.keys(updates).length > 0) {
                    previewUpdateEdge(e.id, updates as any);
                  }
                }
              } catch { }

              // Highlight frame under cursor as drop target
              const nodeSize = { w: nodes.find(n => n.id === nodeId)?.w || 60, h: nodes.find(n => n.id === nodeId)?.h || 60 };
              const center = { x: effectivePos.x + nodeSize.w / 2, y: effectivePos.y + nodeSize.h / 2 };
              const frameAt = frameManager.getFrameAtPoint(center, true);
              setHoveredDropFrameId(frameAt?.id || null);
            }}
            onDragEnd={(nodeId, pos) => {
              if (connectorDragSourceRef.current === nodeId) {
                connectorDragSourceRef.current = null;
                const nodeData = nodes.find(n => n.id === nodeId);
                const kn = nodeRefs.current.get(nodeId);
                if (kn && nodeData) {
                  try { kn.position({ x: nodeData.x, y: nodeData.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                return;
              }

              setIsDraggingShape(false);
              setAlignmentGuidesThrottled([]);
              setDistributionSegmentsThrottled([]);
              alignmentStaticCandidatesRef.current = [];
              alignmentMovingIdsRef.current = new Set();
              const hadGuideSnap = alignmentLastSnappedPosRef.current.has(nodeId);
              const dropPos = alignmentLastSnappedPosRef.current.get(nodeId) || pos;
              alignmentLastSnappedPosRef.current.clear();
              const endPos = hadGuideSnap ? dropPos : snapPointToGrid(dropPos);

              // Clear dragging state for this node (and all selected nodes if multi-selection)
              const store = useDiagramStore.getState();
              const currentSelected: Set<string> = new Set(store.selectedIds);
              const dragTargets = new Set(dragStartPositions.current.keys());
              const dockedPreviewIds = Array.from(dockedDragStartPositionsRef.current.keys());
              const cleanupDockedPreview = () => {
                dockedPreviewIds.forEach((id) => setDraggingGuarded(id, false));
                dockedDragStartPositionsRef.current.clear();
                dockedDragDeltaRef.current = { dx: 0, dy: 0 };
                draggedTableIdsRef.current.clear();
                if (dockedPreviewRafRef.current != null) {
                  cancelAnimationFrame(dockedPreviewRafRef.current);
                  dockedPreviewRafRef.current = null;
                }
                setDockedPreviewTick((t) => t + 1);
              };

              // Reattach transformer after drag ends
              if (transformerRef.current && currentSelected.size > 0) {
                const nodesToTransform: Konva.Node[] = [];
                currentSelected.forEach(id => {
                  const node = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
                  const shapeNode = nodesForRender.find((n) => n.id === id) as any;
                  const isLocked = !!(shapeNode?.locked || shapeNode?.attrs?.locked);
                  const isVisible = !(shapeNode && (shapeNode.visible === false || shapeNode?.attrs?.visible === false));
                  if (node && !isLocked && isVisible) {
                    const variant = String(shapeNode?.attrs?.variant ?? shapeNode?.attrs?.shapeType ?? '').toLowerCase();
                    const useFullContainer = variant === 'bpmn-annotation';
                    const inner = !useFullContainer && (node as any)?.findOne
                      ? (node as any).findOne('.rotation-group') as Konva.Node
                      : undefined;
                    nodesToTransform.push((inner || node) as Konva.Node);
                  }
                });
                transformerRef.current.nodes(nodesToTransform);
                transformerRef.current.getLayer()?.batchDraw();
              }
              // NOTE: For multi-select drags, we clear draggingIds in a finally() of the commit batch
              // so preview edge moves aren't persisted outside command history.
              if (dragTargets.size <= 1) {
                setDraggingGuarded(nodeId, false);
                selectedFreeLineIdsRef.current.forEach((edgeId) => setDraggingGuarded(edgeId, false));
              }

              const nodeData = nodes.find(n => n.id === nodeId) as any;
              const isLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
              if ((isLocked && !isProjectionOwnedNode(nodeData)) || isProjectedNodePlacementLocked(nodeData, lockProjectedNodePlacement)) {
                const kn = nodeRefs.current.get(nodeId);
                if (kn) {
                  try { kn.position({ x: nodeData.x, y: nodeData.y }); } catch { }
                }
                layerRef.current?.batchDraw();
                cleanupDockedPreview();
                return;
              }

              const shouldRestoreTreeModeDrop = (id: string): boolean => {
                const targetNode = nodes.find((n) => n.id === id);
                if (!targetNode || isProjectionOwnedNode(targetNode)) return false;
                return shouldRestoreOrgTreeModeDrop({
                  diagramType,
                  autoLayoutEnabled: orgHostState?.autoLayoutEnabled,
                  node: targetNode,
                  nodes,
                  edges,
                  sourceBacked: Boolean(
                    getCsvRecordFromHost(targetNode.id) ||
                    (getCsvChildrenFromHost(targetNode.id)?.length ?? 0) > 0,
                  ),
                });
              };
              const treeModeRestoreIds = Array.from(dragTargets).filter(shouldRestoreTreeModeDrop);
              if (treeModeRestoreIds.length > 0) {
                dragTargets.forEach((id) => {
                  const originalNode = nodes.find((n) => n.id === id);
                  const startPosition = dragStartPositions.current.get(id);
                  const konvaNode = nodeRefs.current.get(id);
                  const restorePosition = startPosition
                    ?? (originalNode && Number.isFinite(originalNode.x) && Number.isFinite(originalNode.y)
                      ? { x: originalNode.x, y: originalNode.y }
                      : null);
                  if (konvaNode && restorePosition) {
                    try { konvaNode.position(restorePosition); } catch { }
                  }
                  setDraggingGuarded(id, false);
                });
                selectedFreeLineIdsRef.current.forEach((edgeId) => {
                  const startPts = freeLineDragStartPointsRef.current.get(edgeId);
                  if (startPts) {
                    try {
                      useDiagramStore.getState().updateEdge(edgeId, {
                        attrs: {
                          fromPoint: startPts.fromPoint,
                          toPoint: startPts.toPoint,
                        } as any,
                      }, true);
                    } catch { }
                  }
                  setDraggingGuarded(edgeId, false);
                });
                cleanupDockedPreview();
                layerRef.current?.batchDraw();
                scheduleDirtyForNodes(new Set(dragTargets));
                setTimeout(() => scheduleDirtyForNodes(new Set(dragTargets)), 0);
                setTimeout(() => scheduleDirtyForNodes(new Set(dragTargets)), 50);
                if (clearPreviewForShape) {
                  dragTargets.forEach((id) => {
                    requestAnimationFrame(() => clearPreviewForShape(id));
                    lastSentPreviewRef.current.delete(id);
                  });
                }
                selectedFreeLineIdsRef.current = new Set();
                freeLineDragStartPointsRef.current.clear();
                dragStartPositions.current.clear();
                dragLastPos.current.clear();
                setHoveredDropFrameId(null);
                return;
              }
              // Recompute relevant connectors for moved nodes. For multiselect, make sure
              // all affected edges across the selection are invalidated.
              if (dragTargets.size > 1) {
                scheduleDirtyForNodes(dragTargets);
                setTimeout(() => scheduleDirtyForNodes(dragTargets), 0);
                setTimeout(() => scheduleDirtyForNodes(dragTargets), 50);
              } else {
                scheduleDirtyForNode(nodeId);
                setTimeout(() => scheduleDirtyForNode(nodeId), 0);
                setTimeout(() => scheduleDirtyForNode(nodeId), 50);
              }
              // Clear preview on end
              if (clearPreviewForShape) {
                requestAnimationFrame(() => clearPreviewForShape(nodeId));
                lastSentPreviewRef.current.delete(nodeId);
              }
              if (dragTargets.size > 1) {
                // Calculate the delta using local canvas coordinates (consistent with store)
                const startPos = dragStartPositions.current.get(nodeId);
                if (startPos) {
                  const deltaX = endPos.x - startPos.x;
                  const deltaY = endPos.y - startPos.y;

                  // Create moves array for all selected nodes using calculated final positions
                  const moves: Array<{
                    nodeId: string;
                    oldPosition: { x: number; y: number };
                    newPosition: { x: number; y: number };
                  }> = [];

                  dragTargets.forEach((id) => {
                    const oldPos = dragStartPositions.current.get(id);
                    if (oldPos) {
                      const finalPos = {
                        x: oldPos.x + deltaX,
                        y: oldPos.y + deltaY
                      };
                      moves.push({
                        nodeId: id,
                        oldPosition: oldPos,
                        newPosition: finalPos
                      });
                    }
                  });

                  const commands: any[] = [];
                  if (moves.length > 0) {
                    const dockedStartById =
                      draggedTableIdsRef.current.size > 0 && dockedDragStartPositionsRef.current.size > 0
                        ? Object.fromEntries(Array.from(dockedDragStartPositionsRef.current.entries()))
                        : undefined;
                    commands.push(new MoveNodesCommand(moves, dockedStartById));
                  }

                  // Persist free-line moves for any selected free lines
                  if (startPos) {
                    const deltaX = endPos.x - startPos.x;
                    const deltaY = endPos.y - startPos.y;
                    if (deltaX !== 0 || deltaY !== 0) {
                      selectedFreeLineIdsRef.current.forEach((edgeId) => {
                        const startPts = freeLineDragStartPointsRef.current.get(edgeId);
                        if (!startPts) return;
                        const nextFrom = { x: startPts.fromPoint.x + deltaX, y: startPts.fromPoint.y + deltaY };
                        const nextTo = { x: startPts.toPoint.x + deltaX, y: startPts.toPoint.y + deltaY };
                        commands.push(
                          new MoveFreeLineCommand(
                            edgeId,
                            { fromPoint: startPts.fromPoint, toPoint: startPts.toPoint },
                            { fromPoint: nextFrom, toPoint: nextTo }
                          )
                        );
                      });
                    }
                  }

                  const execPromise =
                    commands.length === 1
                      ? commandManager.execute(commands[0])
                      : commands.length > 1
                        ? commandManager.executeBatch(commands, 'Move Selection')
                        : Promise.resolve();

                  // Keep draggingIds set until commit finishes to prevent store→Yjs persisting
                  // preview edge moves outside command history (which breaks undo for free lines).
                  const dragTargetsAtEnd = new Set(dragTargets);
                  const freeLineIdsAtEnd = new Set(selectedFreeLineIdsRef.current);
                  void execPromise.finally(() => {
                    dragTargetsAtEnd.forEach((id) => setDraggingGuarded(id, false));
                    freeLineIdsAtEnd.forEach((edgeId) => setDraggingGuarded(edgeId, false));
                    cleanupDockedPreview();

                    // Trigger auto-layout for org charts after multi-node drag
                    if (diagramType === 'org') {
                      if (orgHostState?.autoLayoutEnabled) {
                        const storeNow = useDiagramStore.getState();
                        dragTargetsAtEnd.forEach((id) => {
                          const node = storeNow.nodes.find(n => n.id === id);
                          if (node && !isProjectionOwnedNode(node)) {
                            markOrgNodeDirty(id);
                          }
                        });
                      }
                    }
                  });
                } else {
                  cleanupDockedPreview();
                }

              } else {
                // Single node drag - create and execute command using local coordinates
                const startPos = dragStartPositions.current.get(nodeId);
                if (startPos && (startPos.x !== endPos.x || startPos.y !== endPos.y)) {
                  // Attempt table docking/undocking before normal move commit.
                  try {
                    const storeNow = useDiagramStore.getState();
                    const movedNode = storeNow.nodes.find((n) => n.id === nodeId) as any;
                    const nodeType = movedNode?.type as string | undefined;
                    const prevDock = (movedNode?.attrs as any)?.tableDock as
                      | { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } }
                      | undefined;

                    // Allow any shape type to be docked (except tables themselves).
                    const canDock = !!nodeType && nodeType !== 'table' && !isProjectionOwnedNode(movedNode);
                    if (canDock) {
                      const nodeW = (movedNode?.w ?? movedNode?.width ?? 60) as number;
                      const nodeH = (movedNode?.h ?? movedNode?.height ?? 60) as number;
                      const center = { x: dropPos.x + nodeW / 2, y: dropPos.y + nodeH / 2 };

                      const tableNodes = storeNow.nodes.filter((n) => n.type === 'table');
                      const tableHit =
                        tableNodes
                          .map((t) => getTableDropHitAtPoint(t as any, center))
                          .filter(Boolean)[0] ?? null;

                      if (tableHit) {
                        const tableNode = storeNow.nodes.find((n) => n.id === tableHit.tableId) as any;
                        const tableAttrs = (tableNode?.attrs ?? {}) as Record<string, unknown>;
                        const layout = resolveCellLayoutConfig(tableAttrs, tableHit.cellKey as any);
                        const autoResizeCells = (tableAttrs as any).autoResizeCells === true;

                        const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
                        const fixedOffset = {
                          x: clamp(dropPos.x - tableHit.cellRect.x, 0, tableHit.cellRect.w - nodeW),
                          y: clamp(dropPos.y - tableHit.cellRect.y, 0, tableHit.cellRect.h - nodeH),
                        };
                        const fixedPos = { x: tableHit.cellRect.x + fixedOffset.x, y: tableHit.cellRect.y + fixedOffset.y };
                        const snapped = {
                          x: tableHit.cellRect.x + tableHit.cellRect.w / 2 - nodeW / 2,
                          y: tableHit.cellRect.y + tableHit.cellRect.h / 2 - nodeH / 2,
                        };
                        const targetPos = layout.algorithmId === 'grid' ? snapped : fixedPos;

                        // Optimistic local update so immediate subsequent actions see the latest dock state.
                        try {
                          const current = storeNow.nodes.find((n) => n.id === nodeId) as any;
                          const nextAttrs = {
                            ...(current?.attrs || {}),
                            tableDock:
                              layout.algorithmId === 'grid'
                                ? { tableId: tableHit.tableId, cellKey: tableHit.cellKey }
                                : { tableId: tableHit.tableId, cellKey: tableHit.cellKey, offset: fixedOffset },
                          };
                          storeNow.updateNode(nodeId, { x: targetPos.x, y: targetPos.y, attrs: nextAttrs } as any, true);
                        } catch { }

                        const dockCmd = new DockNodeToTableCellCommand(nodeId, targetPos, startPos, {
                          mode: 'dock',
                          dock:
                            layout.algorithmId === 'grid'
                              ? { tableId: tableHit.tableId, cellKey: tableHit.cellKey }
                              : { tableId: tableHit.tableId, cellKey: tableHit.cellKey, offset: fixedOffset },
                        });

                        const cmds: any[] = [dockCmd];
                        if (layout.algorithmId === 'grid') {
                          cmds.push(new ReflowDockedNodesInTableCellCommand(tableHit.tableId, tableHit.cellKey));
                        }
                        if (autoResizeCells) {
                          cmds.push(new AutoResizeTableCellToFitDockedContentCommand(tableHit.tableId, tableHit.cellKey));
                        }
                        if (cmds.length === 1) {
                          void commandManager.execute(cmds[0]);
                        } else {
                          void commandManager.executeBatch(cmds, 'Dock to cell');
                        }

                        cleanupDockedPreview();
                        return;
                      }

                      // Only undock when previously docked.
                      if (prevDock) {
                        try {
                          const current = storeNow.nodes.find((n) => n.id === nodeId) as any;
                          const nextAttrs = { ...(current?.attrs || {}) };
                          delete (nextAttrs as any).tableDock;
                          storeNow.updateNode(nodeId, { x: endPos.x, y: endPos.y, attrs: nextAttrs } as any, true);
                        } catch { }
                        void commandManager.execute(new DockNodeToTableCellCommand(nodeId, endPos, startPos, { mode: 'undock' }));
                        cleanupDockedPreview();
                        return;
                      }
                    }
                  } catch { }

                  const dockedStartById =
                    draggedTableIdsRef.current.size > 0 && dockedDragStartPositionsRef.current.size > 0
                      ? Object.fromEntries(Array.from(dockedDragStartPositionsRef.current.entries()))
                      : undefined;
                  // Capture current connector anchors as computed during drag preview so they
                  // don't "flip" after the move is committed (Yjs sync can overwrite preview-only fields).
                  const anchorUpdates = (() => {
                    try {
                      const storeNow = useDiagramStore.getState();
                      const updates: Array<{ edgeId: string; updates: any }> = [];
                      for (const e of storeNow.edges as any[]) {
                        const t = (e as any)?.type as string | undefined;
                        if (t !== 'sequence_flow' && t !== 'line') continue;
                        const fromId = (e as any).from as string;
                        const toId = (e as any).to as string;
                        if (fromId !== nodeId && toId !== nodeId) continue;
                        if (fromId?.startsWith('__free') || toId?.startsWith('__free')) continue;

                        const fromLocked = !!(e as any).fromAnchorLocked;
                        const toLocked = !!(e as any).toAnchorLocked;
                        const next: Record<string, any> = {};

                        if (!fromLocked && (e as any).fromAnchor) {
                          next.fromAnchor = (e as any).fromAnchor;
                          next.fromOffset = typeof (e as any).fromOffset === 'number' ? (e as any).fromOffset : 0.5;
                        }
                        if (!toLocked && (e as any).toAnchor) {
                          next.toAnchor = (e as any).toAnchor;
                          next.toOffset = typeof (e as any).toOffset === 'number' ? (e as any).toOffset : 0.5;
                        }
                        if (Object.keys(next).length > 0) {
                          updates.push({ edgeId: e.id, updates: next });
                        }
                      }
                      return updates;
                    } catch {
                      return [];
                    }
                  })();

                  const command = new MoveNodeCommand(nodeId, endPos, startPos, dockedStartById);
                  // Execute and wait for completion before triggering post-move logic
                  void commandManager.execute(command)
                    .then(async () => {
                      if (anchorUpdates.length > 0) {
                        await commandManager.execute(new AutoPathEdgesCommand(anchorUpdates));
                      }
                      // Trigger auto-layout after position is persisted (allows sibling reordering)
                      if (diagramType === 'org') {
                        const movedNode = useDiagramStore.getState().nodes.find((node) => node.id === nodeId);
                        if (orgHostState?.autoLayoutEnabled && movedNode && !isProjectionOwnedNode(movedNode)) {
                          markOrgNodeDirty(nodeId);
                        }
                      }
                    })
                    .finally(() => cleanupDockedPreview());
                } else {
                  cleanupDockedPreview();
                }

                if (diagramType === 'org') {
                  const storeNow = useDiagramStore.getState();
                  const personNode = storeNow.nodes.find(n => n.id === nodeId);

                  // Check for all person types: org_person, org_person_photo, org_person_photo_left, org_person_photo_right, org_person_photo_inner_left, org_person_text
                  const isPersonType =
                    personNode?.type === 'org_person' ||
                    personNode?.type === 'org_person_photo' ||
                    personNode?.type === 'org_person_photo_left' ||
                    personNode?.type === 'org_person_photo_right' ||
                    personNode?.type === 'org_person_photo_inner_left' ||
                    personNode?.type === 'org_person_text';
                  if (isPersonType && !isProjectionOwnedNode(personNode)) {
                    const personW = (personNode.w ?? personNode.width ?? 60) as number;
                    const personH = (personNode.h ?? personNode.height ?? 60) as number;
                    const personCenter = { x: endPos.x + personW / 2, y: endPos.y + personH / 2 };

                    let targetId: string | null = null;
                    let bestDist = Infinity;
                    storeNow.nodes.forEach((candidate) => {
                      if (candidate.type !== 'org_position') return;
                      const w = (candidate.w ?? candidate.width ?? 60) as number;
                      const h = (candidate.h ?? candidate.height ?? 60) as number;
                      const withinX = personCenter.x >= candidate.x && personCenter.x <= candidate.x + w;
                      const withinY = personCenter.y >= candidate.y && personCenter.y <= candidate.y + h;
                      if (!withinX || !withinY) return;
                      const dx = personCenter.x - (candidate.x + w / 2);
                      const dy = personCenter.y - (candidate.y + h / 2);
                      const dist = Math.hypot(dx, dy);
                      if (dist < bestDist) {
                        bestDist = dist;
                        targetId = candidate.id;
                      }
                    });

                    if (targetId) {
                      const alreadyLinked = storeNow.edges.some((edge) => {
                        const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation;
                        const edgeType = edge.type as unknown as string;
                        const isOccupies = edgeType === 'occupies' || relation === 'occupies';
                        return isOccupies && edge.from === personNode.id && edge.to === targetId;
                      });
                      if (!alreadyLinked) {
                        const deps = getOrgDeps();
                        if (deps) {
                          commandManager.execute(new deps.CreateOccupiesCommand({
                            fromId: personNode.id,
                            toId: targetId,
                          }));
                        } else {
                          void ensureOrgDeps().then((loaded) => {
                            commandManager.execute(new loaded.CreateOccupiesCommand({
                              fromId: personNode.id,
                              toId: targetId,
                            }));
                          });
                        }
                      }
                    }
                  }
                }
              }

              // Clear free-line drag refs (preview is model-driven via updateEdge during drag)
              selectedFreeLineIdsRef.current = new Set();
              freeLineDragStartPointsRef.current.clear();
              // model-driven preview; no visual translation refs needed

              // Clear all tracked positions
              dragStartPositions.current.clear();
              dragLastPos.current.clear();

              // Source cards can overlap presentation frames without becoming
              // document-owned shapes or acquiring semantic membership.
              if (nodeData && isProjectionOwnedNode(nodeData)) return;

              // After drop, place node into frame under its center
              const nodeSize = { w: nodes.find(n => n.id === nodeId)?.w || 60, h: nodes.find(n => n.id === nodeId)?.h || 60 };
              const center = { x: endPos.x + nodeSize.w / 2, y: endPos.y + nodeSize.h / 2 };
              let frameAt = frameManager.getFrameAtPoint(center, true);
              if (!frameAt && hoveredDropFrameId) {
                // Fallback to highlighted frame if hit-test missed
                frameAt = frameManager.getFrame(hoveredDropFrameId) || null;
              }
              if (frameAt) {
                frameManager.addShapeToFrame(nodeId, frameAt.id);
                // Persist membership in Yjs frame record
                if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
                  const rec = frameAt.toJSON();
                  isSyncingFramesToYjs.current = true;
                  framesMapRef.current.set(frameAt.id, rec);
                  isSyncingFramesToYjs.current = false;
                }
              } else {
                frameManager.removeShapeFromAllFrames(nodeId);
                // Persist removal across all frames
                if (framesMapRef.current && !isSyncingFramesFromYjs.current) {
                  framesMapRef.current.forEach((rec: any, id: string) => {
                    if (Array.isArray(rec.shapes)) {
                      const next = { ...rec, shapes: rec.shapes.filter((sid: string) => sid !== nodeId) };
                      framesMapRef.current!.set(id, next);
                    }
                  });
                }
              }
              setHoveredDropFrameId(null);
            }}
            onTransformStart={handleTransformStartWithGuides}
            onTransform={handleTransform}
            onTransformEnd={handleTransformEndWithGuides}
            onStartTextEdit={handleStartTextEdit}
            onStartTableCellTextEdit={handleStartTableCellTextEdit}
            editingTextState={
              editingTextState?.nodeId && editingTextState.field
                ? { nodeId: editingTextState.nodeId, field: editingTextState.field, tableCellKey: editingTextState.tableCellKey }
                : null
            }
            canWrite={canWrite}
            projectionLocked={projectionLocked}
            lockProjectedNodePlacement={lockProjectedNodePlacement}
          />


          <EdgesRenderer
            edges={canvasEdgesForRender}
            nodes={canvasNodesForRender}
            diagramType={resolveEdgeRenderDiagramType(
              diagramType,
              typeof window !== 'undefined' ? window.sessionStorage.getItem('diagramType') : null,
            )}
            selectedIds={selectedIds}
            canWrite={(canWrite !== undefined ? canWrite : true) && !projectionLocked}
            getShapeState={React.useCallback((id: string, base: any) => {
              const merged = resolveConnectorShapeState(historyActive, id, base, getShapeState);
              const kn = nodeRefs.current.get(id);
              if (kn) {
                // For genogram shapes, prefer rotation-group or shape-content to exclude text labels from bounds
                let measureNode: Konva.Node | undefined = kn as unknown as Konva.Node;
                try {
                  const rotationGroup = (kn as any).findOne?.('.rotation-group') as Konva.Node | null;
                  const shapeContent = (kn as any).findOne?.('.shape-content') as Konva.Node | null;
                  if (shapeContent) measureNode = shapeContent;
                  else if (rotationGroup) measureNode = rotationGroup;
                } catch { }
                const rect = measureNode ? (measureNode as any).getClientRect?.({ skipShadow: true }) ?? null : null;
                const kW = (kn as any).getAttrs?.().width ?? (kn as any).width?.() ?? merged.width ?? merged.w;
                const kH = (kn as any).getAttrs?.().height ?? (kn as any).height?.() ?? merged.height ?? merged.h;
                let rectX: number | undefined = undefined;
                let rectY: number | undefined = undefined;
                const scale = stageScaleRef.current || 1;
                let rectW = rect ? rect.width / scale : undefined as number | undefined;
                let rectH = rect ? rect.height / scale : undefined as number | undefined;
                try {
                  const t = (merged as any)?.type;
                  const pts = Array.isArray((merged as any)?.attrs?.points) ? ((merged as any).attrs.points as number[]) : null;
                  if (t === 'freehand' && pts && pts.length >= 4) {
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                    for (let i = 0; i < pts.length; i += 2) {
                      const px = pts[i] as number; const py = pts[i + 1] as number;
                      if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
                      if (px < minX) minX = px; if (py < minY) minY = py;
                      if (px > maxX) maxX = px; if (py > maxY) maxY = py;
                    }
                    if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
                      rectX = (merged as any).x + minX;
                      rectY = (merged as any).y + minY;
                      rectW = Math.max(1, maxX - minX);
                      rectH = Math.max(1, maxY - minY);
                    }
                  }
                } catch { }
                const draggingIds = (useDiagramStore as any).getState?.().draggingIds as Set<string> | undefined;
                const isDraggingThis = !!draggingIds?.has(id);
                let kx: number | undefined;
                let ky: number | undefined;
                try {
                  if (typeof (kn as any).x === 'function') kx = (kn as any).x();
                  if (typeof (kn as any).y === 'function') ky = (kn as any).y();
                } catch { }
                const mergedX = (merged as any)?.x;
                const mergedY = (merged as any)?.y;
                const mergedW = (merged as any)?.w ?? (merged as any)?.width;
                const mergedH = (merged as any)?.h ?? (merged as any)?.height;
                const resolvedX =
                  isDraggingThis && Number.isFinite(kx as number)
                    ? (kx as number)
                    : (mergedX ?? (Number.isFinite(kx as number) ? (kx as number) : 0));
                const resolvedY =
                  isDraggingThis && Number.isFinite(ky as number)
                    ? (ky as number)
                    : (mergedY ?? (Number.isFinite(ky as number) ? (ky as number) : 0));
                const isOrgNode = typeof (merged as any)?.type === 'string' && (merged as any).type.startsWith('org_');
                const isTransformingThis = transformStartState.current.has(id);
                const resolvedW = isOrgNode && !isTransformingThis && Number.isFinite(mergedW)
                  ? mergedW
                  : ((kW as number | undefined) ?? mergedW);
                const resolvedH = isOrgNode && !isTransformingThis && Number.isFinite(mergedH)
                  ? mergedH
                  : ((kH as number | undefined) ?? mergedH);
                const personSymbolRect = getGenogramPersonSymbolRect(
                  { ...merged, x: resolvedX, y: resolvedY },
                  {
                    x: resolvedX,
                    y: resolvedY,
                    w: resolvedW,
                    h: resolvedH,
                  },
                );
                if (personSymbolRect) {
                  rectX = personSymbolRect.x;
                  rectY = personSymbolRect.y;
                  rectW = personSymbolRect.w;
                  rectH = personSymbolRect.h;
                }
                const mergedNode = {
                  ...merged,
                  // Default: prefer React data positions (projected/repositioned) — Konva refs
                  // may lag by one frame during scenario projection.
                  // During local drags, prefer live Konva positions so connectors reroute continuously.
                  x: resolvedX,
                  y: resolvedY,
                  // Org cards redraw their Konva bounds during resize. Use those bounds only
                  // while the transform is active; after commit, model geometry is authoritative.
                  w: resolvedW,
                  h: resolvedH,
                  width: resolvedW,
                  height: resolvedH,
                  rectX,
                  rectY,
                  rectW,
                  rectH,
                } as any;

                if (typeof mergedNode.type === 'string' && mergedNode.type.startsWith('org_')) {
                  const w = (mergedNode.width as number) ?? (mergedNode.w as number);
                  const h = (mergedNode.height as number) ?? (mergedNode.h as number);
                  const hasModel = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0;
                  const hasRect = Number.isFinite(rectW) && Number.isFinite(rectH) && (rectW as number) > 0 && (rectH as number) > 0;

                  if (hasModel) {
                    mergedNode.rectX = mergedNode.x;
                    mergedNode.rectY = mergedNode.y;
                    mergedNode.rectW = w as number;
                    mergedNode.rectH = h as number;
                  } else if (hasRect) {
                    const rw = rectW as number;
                    const rh = rectH as number;
                    mergedNode.width = rw;
                    mergedNode.height = rh;
                    mergedNode.w = rw;
                    mergedNode.h = rh;
                    mergedNode.rectX = rectX ?? mergedNode.x;
                    mergedNode.rectY = rectY ?? mergedNode.y;
                    mergedNode.rectW = rw;
                    mergedNode.rectH = rh;
                  }
                }

                return mergedNode;
              }
              return merged;
            }, [getShapeState, historyActive, stageScaleRef])}
            awareness={awareness}
            registerEdgeRef={(id, n) => {
              if (n) edgeRefs.current.set(id, n as unknown as Konva.Node); else edgeRefs.current.delete(id);
            }}
            versions={edgeVersions}
            onEdgeClick={handleEdgeClick}
            onStartEdgeTextEdit={handleStartEdgeTextEdit}
            editingEdgeText={editingTextState?.edgeId}
            editRequestEdgeId={pendingEdgeEditId}
          />

          {diagramType === 'org' ? (
            <OrgDisplayRuleLabelsOverlay nodes={canvasNodesForRender} />
          ) : null}


          <CommentPinsLayer
            comments={positionedComments}
            stageRef={stageRef}
            activeCommentId={activeCommentId}
          />

          {/* Generic text editing overlay (disabled for locked nodes) */}
          {editingTextState && (() => {
            const isNodeLocked = editingTextState.nodeId ? (() => {
              const n = nodes.find(nn => nn.id === editingTextState.nodeId) as any;
              return !!(n?.locked || n?.attrs?.locked);
            })() : false;
            if (isNodeLocked) return null;
            const allowEmptyCommit = (() => {
              if (editingTextState.tableCellKey || editingTextState.edgeId) return true;
              if (editingTextState.nodeId && editingTextState.field === 'text') {
                const n = nodes.find((nn) => nn.id === editingTextState.nodeId);
                return (
                  n?.type === 'basic-shape' ||
                  n?.type === 'flowchart-shape' ||
                  n?.type === 'bpmn-shape'
                );
              }
              return false;
            })();
            return (
              <EditableTextOverlay
                textNode={editingTextState.textNode}
                initialText={getInitialEditText()}
                allowEmptyCommit={allowEmptyCommit}
                selectAllOnMount={editingTextState.selectAllOnMount ?? true}
                onChange={(text, metrics) => handleTextEdit(
                  (editingTextState.nodeId || editingTextState.edgeId)!,
                  editingTextState.field,
                  text,
                  metrics
                )}
                onClose={handleCloseTextEdit}
                maxWidth={(() => {
                  if (editingTextState?.edgeId) return 200;
                  if (editingTextState?.nodeId && editingTextState.field === 'text') {
                    const n = nodes.find(nn => nn.id === editingTextState.nodeId);
                    return (n?.width as number) || (n?.w as number) || 100;
                  }
                  if (editingTextState?.nodeId && editingTextState.field === 'name') {
                    const n = nodes.find(nn => nn.id === editingTextState.nodeId);
                    if (n && isManifestResourceCardNameEditNode(n, editingTextState.field)) {
                      return getManifestResourceCardNameEditorWidth(n);
                    }
                  }
                  if (editingTextState?.nodeId && (editingTextState.field === 'name' || editingTextState.field === 'location')) {
                    return 500;
                  }
                  return undefined;
                })()}
                centerCanvas={(() => {
                  if (editingTextState?.nodeId && editingTextState.field === 'text') {
                    const n = nodes.find(nn => nn.id === editingTextState.nodeId);
                    if (n) {
                      return {
                        x: (n.x || 0) + ((n.width as number) || (n.w as number) || 100) / 2,
                        y: (n.y || 0) + ((n.height as number) || (n.h as number) || 30) / 2
                      };
                    }
                  }
                  return undefined;
                })()}
                noWrap={(() => {
                  // Enable noWrap (single line, expanding width) for:
                  // - Edge labels
                  // - Person node name/location fields
                  if (editingTextState?.edgeId) return true;
                  if (editingTextState?.nodeId && (editingTextState.field === 'name' || editingTextState.field === 'location')) {
                    const n = nodes.find(nn => nn.id === editingTextState.nodeId);
                    const t = (n as any)?.type;
                    if (isManifestResourceCardNameEditNode(n, editingTextState.field)) return false;
                    if (t === 'person') return true;
                    if (editingTextState.field === 'name' && typeof (n as any)?.attrs?.name === 'string') return true;
                    if (
                      t === 'org_person' ||
                      t === 'org_person_photo' ||
                      t === 'org_person_photo_left' ||
                      t === 'org_person_photo_right' ||
                      t === 'org_person_photo_inner_left' ||
                      t === 'org_person_text' ||
                      t === 'org_department'
                    ) {
                      return true;
                    }
                  }
                  if (editingTextState?.nodeId && editingTextState.field === 'text') {
                    const n = nodes.find(nn => nn.id === editingTextState.nodeId);
                    if ((n as any)?.type === 'org_position') return true;
                  }
                  return false;
                })()}
              />
            );
          })()}

          <OverlaysLayer
            connectionPreview={connectionPreview}
            previewStartAnchor={previewStartAnchorRef.current}
            previewEndAnchor={previewEndAnchorRef.current}
            firstConnectNode={firstConnectNode}
            hoveredTargetId={hoveredTargetId}
            nodes={nodes}
            selectedIds={selectedIds}
            alignmentGuides={alignmentGuides}
            alignmentViewportRect={alignmentViewportRect}
            distributionSegments={distributionSegments}
            marriageHover={hoveredMarriageBar ? { x1: hoveredMarriageBar.x1, x2: hoveredMarriageBar.x2, y: hoveredMarriageBar.y } : null}
            transformerRect={selectedIds.size === 1 ? (() => {
              const id = Array.from(selectedIds)[0];
              const n = nodes.find(nn => nn.id === id);
              if (!n) return null;

              // Prefer exact client rect from the actual Konva node (works for locked/unlocked)
              try {
                const container = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
                if (container && typeof (container as any).getClientRect === 'function') {
                  const variant = String((n as any).attrs?.variant ?? (n as any).attrs?.shapeType ?? '').toLowerCase();
                  const useFullBounds = variant === 'bpmn-annotation';
                  const inner = !useFullBounds && (container as any).findOne ? (container as any).findOne('.rotation-group') as Konva.Node | null : null;
                  const measureNode: Konva.Node = (inner || (container as unknown as Konva.Node));
                  const raw = measureNode.getClientRect({ skipShadow: true });
                  const st = measureNode.getStage();
                  const scale = st?.scaleX() || 1;
                  const pos = st?.position() || { x: 0, y: 0 };
                  const r = {
                    x: (raw.x - pos.x) / scale,
                    y: (raw.y - pos.y) / scale,
                    width: raw.width / scale,
                    height: raw.height / scale,
                  };
                  if (isFinite(r.x) && isFinite(r.y) && r.width > 0 && r.height > 0) return r;
                }
              } catch { }

              // Fallback 1: if a transformer is attached, use it
              const tr = transformerRef.current;
              if (tr && typeof tr.getClientRect === 'function' && tr.nodes && tr.nodes().length > 0) {
                const raw = tr.getClientRect();
                const st = tr.getStage();
                const scale = st?.scaleX() || 1;
                const pos = st?.position() || { x: 0, y: 0 };
                const r = {
                  x: (raw.x - pos.x) / scale,
                  y: (raw.y - pos.y) / scale,
                  width: raw.width / scale,
                  height: raw.height / scale,
                };
                if (isFinite(r.x) && isFinite(r.y) && r.width > 0 && r.height > 0) return r;
              }

              // Fallback 2: manual math using model (rotation-aware)
              const width = (n.w as number) || (n.width as number) || 60;
              const height = (n.h as number) || (n.height as number) || 60;
              const rotation = ((n.attrs as any)?.rotation as number) || 0;
              if (rotation === 0 || !isFinite(rotation)) {
                return { x: n.x, y: n.y, width, height };
              }
              const cx = n.x + width / 2;
              const cy = n.y + height / 2;
              const corners = [
                { x: n.x, y: n.y },
                { x: n.x + width, y: n.y },
                { x: n.x + width, y: n.y + height },
                { x: n.x, y: n.y + height }
              ];
              const rad = rotation * Math.PI / 180;
              const cos = Math.cos(rad);
              const sin = Math.sin(rad);
              const rotated = corners.map(corner => {
                const dx = corner.x - cx;
                const dy = corner.y - cy;
                return { x: cx + (dx * cos - dy * sin), y: cy + (dx * sin + dy * cos) };
              });
              const xs = rotated.map(c => c.x);
              const ys = rotated.map(c => c.y);
              const minX = Math.min(...xs);
              const maxX = Math.max(...xs);
              const minY = Math.min(...ys);
              const maxY = Math.max(...ys);
              return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
            })() : null}
            onConnectionStart={(side: 'top' | 'right' | 'bottom' | 'left', startX: number, startY: number) => {
              const id = Array.from(selectedIds)[0];
              const sourceNode = nodes.find(n => n.id === id);
              if (sourceNode) {
                const context = {
                  toolRegistry,
                  diagramType: effectiveDiagramType,
                  commandManager,
                  doc,
                  isPlanningChart,
                  isExplicitScenarioAuthoring,
                };
                for (const plugin of canvasPluginsRef.current) {
                  const nextType = plugin.defaultConnectorType?.({
                    sourceNode,
                    currentType: String(connectorTool.connectionType ?? ''),
                    context,
                  });
                  if (nextType) {
                    connectorTool.connectionType = nextType;
                    break;
                  }
                }
                if (sourceNode.type === 'image') {
                  connectorTool.connectionType = 'line';
                }
              }
              if (connectorTool && (connectorTool as any).setStartAnchor) {
                (connectorTool as any).setStartAnchor(side);
              }
              setIsConnecting(true);
              setFirstConnectNode(id);
              autoStartAnchorRef.current = false;
              setConnectionPreview({ startX, startY, endX: startX, endY: startY, visible: true });
              previewStartAnchorRef.current = side;
              document.body.style.cursor = 'crosshair';
            }}
            onQuickCreate={handleQuickCreate}
            onRotationStart={(startAngle: number) => {
              // Prevent rotation for viewers
              if (!canWrite) return;
              const id = Array.from(selectedIds)[0];
              const nodeData = nodes.find(n => n.id === id);
              if (!nodeData) return;
              // Prevent rotation for locked nodes
              if (nodeData.locked || (nodeData as any)?.attrs?.locked) return;

              rotationStaticCandidatesRef.current = [];
              if (effectiveAlignmentGuidesEnabled || effectiveDistributionGuidesEnabled) {
                rotationStaticCandidatesRef.current = nodes.reduce<AlignmentGuideCandidate[]>((acc, n) => {
                  if (n.id === id) return acc;
                  const isVisible = !((n as any)?.visible === false || (n as any)?.attrs?.visible === false);
                  if (!isVisible) return acc;
                  const measured = getCanvasBoundsForNodeId(n.id);
                  if (measured) {
                    acc.push({ shapeId: n.id, bounds: measured });
                    return acc;
                  }
                  const w = (n.w as number) || (n.width as number) || 60;
                  const h = (n.h as number) || (n.height as number) || 60;
                  acc.push({ shapeId: n.id, bounds: { x: n.x, y: n.y, width: w, height: h } });
                  return acc;
                }, []);
              }
              setAlignmentGuidesThrottled([]);
              setDistributionSegmentsThrottled([]);

              const w = nodeData.w || nodeData.width || 60;
              const h = nodeData.h || nodeData.height || 60;
              const cx = nodeData.x + (w / 2);
              const cy = nodeData.y + (h / 2);
              const rotation = ((nodeData.attrs as any)?.rotation) || 0;

              rotateTargetIdRef.current = id;
              rotateStartAngleRef.current = startAngle;
              rotateInitialRotationRef.current = rotation;
              rotatePreviewRef.current = rotation;
              const node = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
              rotateInnerRef.current = ((node as any && typeof (node as any).findOne === 'function')
                ? (node as any).findOne('.rotation-group') as Konva.Node
                : null);
              rotateCenterRef.current = { cx, cy };
              rotateSizeRef.current = { w, h };
              setIsRotating(true);
            }}
            onUnlockNode={(nodeId: string) => {
              try {
                const cmd = new ChangeNodeStyleCommand([nodeId], 'locked', false);
                commandManager.execute(cmd);
              } catch { }
            }}
            suppressConnectionTriggers={isDraggingShape || isTransforming || isRotating}
            hoveredTargetPoint={hoveredTargetPoint}
            hoveredTargetAnchor={hoveredTargetAnchor}
            hoveredTargetAnchors={hoveredTargetAnchors}
            isConnecting={isConnecting || isEndpointDragging}
            penPreview={penPreview}
            placePreview={placePreview}
            selectionRect={selectionRect}
            awareness={awareness}
            currentUserId={currentUserId}
            overlayShapes={overlayShapes}
            canWrite={canWrite}
          />

          {/*
            Zoom-stable label pills for container shapes. Rendered
            after the regular node renderer so the pills sit above
            their containers; `listening={false}` means clicks fall
            through to the shape underneath.
          */}
          <ContainerShapeLabelOverlay stageScale={viewportStage.scale} />

          <TransformersLayer
            ref={(refs) => {
              if (refs) {
                // Update refs for compatibility with existing code
                Object.assign(transformerRef, refs.nodesTransformerRef);
                Object.assign(frameTransformerRef, refs.frameTransformerRef);
              }
            }}
            nodesBoundBoxFunc={handleNodesTransformerBoundBox}
          />
        </Layer>
        {/* Plugin-contributed Konva layers — see CanvasPlugin.renderStageLayer.
            These are siblings of the main Layer so they pan+zoom with the
            Stage. Each plugin is expected to return its own <Layer>. */}
        {canvasPlugins.map((plugin) => {
          if (!plugin.renderStageLayer) return null;
          const node = plugin.renderStageLayer({
            context: { toolRegistry, diagramType: effectiveDiagramType, commandManager, doc: doc ?? undefined },
            stageRef,
            layerRef,
          });
          return node ? <React.Fragment key={`stagelayer-${plugin.id}`}>{node}</React.Fragment> : null;
        })}
      </Stage>
      {/* Canvas context menu */}
      <CanvasContextMenu />
      {/* Pan Overlay: intercept events to pan when pan mode is enabled */}
      <div
        data-testid="tid.canvas.panOverlay"
        className="absolute inset-0 z-10"
        style={{ pointerEvents: isPanMode ? 'auto' as const : 'none' as const, cursor: isPanMode ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
        onMouseDown={handlePanOverlayMouseDown}
        onMouseMove={handlePanOverlayMouseMove}
        onMouseUp={handlePanOverlayMouseUp}
        onMouseLeave={handlePanOverlayMouseUp}
        onWheel={handlePanOverlayWheel}
        onContextMenu={(e) => { if (isPanMode) { e.preventDefault(); e.stopPropagation(); } }}
        // Touch support
        onTouchStart={(e) => {
          if (!isPanMode) return;
          const t = e.touches[0];
          if (!t) return;
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          setIsPanning(true);
          panOverlayActiveRef.current = true;
          setPanStart({ x: t.clientX - rect.left - (stageRef.current?.x() || 0), y: t.clientY - rect.top - (stageRef.current?.y() || 0) });
          document.body.style.cursor = 'grabbing';
        }}
        onTouchMove={(e) => {
          if (!isPanMode || !isPanning) return;
          const t = e.touches[0];
          if (!t) return;
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          const nextX = t.clientX - rect.left - panStart.x;
          const nextY = t.clientY - rect.top - panStart.y;
          const stage = stageRef.current;
          if (!stage) return;
          previewPanViewportStage({ scale: stage.scaleX(), offsetX: nextX, offsetY: nextY });
        }}
        onTouchEnd={handlePanOverlayMouseUp as any}
      />
        <CanvasPopups
          stageRef={stageRef}
          positionedComments={positionedComments}
          diagramId={diagramId}
          createRootComment={createRootComment}
          createReply={createReply}
          isCreating={isCreating}
          isReplying={isReplying}
          resolveComment={resolveComment}
          reopenComment={reopenComment}
          isResolving={isResolving}
          isReopening={isReopening}
          updateComment={updateComment}
          isUpdating={isUpdating}
          softDeleteComment={softDeleteComment}
          isDeleting={isDeleting}
          eventBus={eventBus}
          commandManager={commandManager}
          currentUserId={currentUserId}
          shapePickerState={shapePickerState}
          handleShapePickerClose={handleShapePickerClose}
          popupStyle={popupStyle}
          nodes={nodes}
          edges={edges}
          toolRegistry={toolRegistry}
          connectorTool={connectorTool}
          setIsConnecting={setIsConnecting}
          setFirstConnectNode={setFirstConnectNode}
          effectiveDiagramType={effectiveDiagramType}
          doc={doc}
          ensureOrgDeps={ensureOrgDeps}
          orgReportingTargets={orgReportingTargets}
          collapsedNodeIds={collapsedNodeIds}
          handleToggleOrgCollapseCommand={handleToggleOrgCollapseCommand}
          requestConnectorRefresh={requestConnectorRefresh}
          canvasPluginsRef={canvasPluginsRef as any}
          insertBetweenPicker={insertBetweenPicker}
          setInsertBetweenPicker={setInsertBetweenPicker}
          linkPrompt={linkPrompt}
          setLinkPrompt={setLinkPrompt}
        />
	    </div>
	  );
	};
